/**
 * GERADO por apps/api/scripts/gen-schemas.ts — nao edite.
 *
 * Tabelas campo a campo da documentacao, derivadas dos schemas zod que a API
 * usa pra validar (apps/api/src/modules/external/banco.schemas.ts). Formato e
 * obrigatoriedade saem dos validadores; a descricao sai dos `.describe()`.
 *
 * Regerar: pnpm --filter @atlas/api gen:schemas
 */
export interface CampoDoc {
  nome: string;
  formato: string;
  obrigatorio: boolean;
  /** Chave sempre presente, mas o valor pode vir `null`. Diferente de opcional. */
  nulavel?: boolean;
  descricao: string;
  padrao?: string;
  /** Sub-tabela: campos do objeto aninhado (ou do item, quando e' lista). */
  campos?: CampoDoc[];
}

/** Chave: "METODO /path" na mesma grafia do catalogo (`{param}`). */
export const CAMPOS_POR_ENDPOINT: Record<string, CampoDoc[]> = {
  "POST /v1/external/banco/contratos/averbar": [
    {
      "nome": "cpf",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "CPF do servidor, só dígitos. Opcional: a matrícula já identifica a operação."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor na prefeitura. É a identidade da operação — um CPF pode ter várias matrículas, cada uma com margem própria."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio onde a operação será averbada. Precisa ser um convênio do seu banco; de outro responde 404."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ENUM (EMPRESTIMO | REFIN | ECONSIGNADO)",
      "obrigatorio": false,
      "descricao": "Natureza da operação. `ECONSIGNADO` é o cartão, averbado com parcela fixa. A reserva e o lançamento de cartão, em `/v1/external/banco/cartoes/*`, estão temporariamente indisponíveis.",
      "padrao": "\"EMPRESTIMO\""
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor total financiado, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Quantidade de parcelas. Limitada pelo teto do convênio (definido pela prefeitura) e pelo teto da plataforma, de 120."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Empréstimo e refinanciamento: o **CET** a.m. em FRAÇÃO (0.0179 é 1,79% a.m.), sujeito ao teto de CET do convênio e ao mínimo de 0,5% a.m. Cartão (`ECONSIGNADO`): juros do rotativo, sem teto. Enviar 1.79 em vez de 0.0179 é recusado."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Parcela (desconto mensal) em reais, calculada por você. A averbadora não aplica Price/PMT: confere só se cabe na margem. **Envie sempre** — ausente, a parcela é estimada pelo CET, comportamento mantido só por compatibilidade."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "IOF do contrato, em reais, como calculado por você. Ausente = 0 (empréstimo) ou estimado (cartão)."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Cartão (`ECONSIGNADO`): o CET a.m. em FRAÇÃO, quando `taxa_am` são os juros. Em empréstimo e refinanciamento é ignorado — ali `taxa_am` já é o CET."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor líquido liberado ao servidor, em reais. Não pode passar de `valor_financiado`."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": false,
      "descricao": "Número do contrato no seu sistema. Não pode repetir outro contrato seu (`numero_contrato_em_uso`)."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro (mín. 0)",
      "obrigatorio": false,
      "descricao": "Dias entre a liberação do crédito e a primeira parcela.",
      "padrao": "30"
    },
    {
      "nome": "reserva",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "true cria pré-reserva de margem em vez de averbar. A reserva expira sozinha e libera a margem.",
      "padrao": "false"
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Texto livre registrado no histórico do contrato."
    },
    {
      "nome": "contrato_origem",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": false,
      "descricao": "ADF da operacao substituida. Quando valida, somente o aumento da parcela reserva margem nova."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/acao": [
    {
      "nome": "acao",
      "formato": "ENUM (aprovar | quitar | suspender | cancelar | alongar | alterar | confirmar)",
      "obrigatorio": true,
      "descricao": "Transição pedida. A plataforma valida se ela é permitida na situação atual do contrato; se não for, responde 409."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Justificativa registrada no histórico. Em `quitar` (desaverbar por liquidação) é obrigatória — mínimo 3 caracteres."
    },
    {
      "nome": "parcelas_extras",
      "formato": "NUMERICO inteiro (mín. 1)",
      "obrigatorio": false,
      "descricao": "Só em `alongar`: quantas parcelas acrescentar ao prazo."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Texto livre registrado no histórico."
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Só em `alterar`: substitui o código de verba do contrato."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/editar": [
    {
      "nome": "valor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Novo valor financiado. Note o nome: aqui é `valor`, enquanto na averbação é `valor_financiado`."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Novo prazo. Vale o mesmo teto do convênio da averbação."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": true,
      "descricao": "Empréstimo e refinanciamento: o **CET** a.m. do contrato, em FRAÇÃO (0.0179 = 1,79%). Não pode passar do teto de CET do convênio nem ficar abaixo de 0,5% a.m. Cartão: juros do rotativo, sem teto."
    },
    {
      "nome": "juros_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Juros a.m. do contrato, em FRAÇÃO. Opcional e sem teto; se enviado, o CET (`taxa_am`) não pode ser menor que ele. Não é gravado — vai para o histórico."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Nova parcela em reais, calculada por você. A averbadora não aplica Price/PMT: confere só a margem. **Envie sempre** — ausente, a parcela é estimada pelo CET, comportamento mantido só por compatibilidade."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "IOF do contrato, em reais. Ausente = 0 (empréstimo) ou estimado (cartão)."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Cartão: o CET a.m. em FRAÇÃO, quando `taxa_am` são os juros. Ignorado em empréstimo e refinanciamento."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": false,
      "descricao": "Número do contrato no seu sistema. Ausente = mantém o que o contrato tem. Não pode repetir outro contrato seu."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 300 caracteres)",
      "obrigatorio": false,
      "descricao": "Justificativa da alteração, registrada no histórico."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/tratar-falha": [
    {
      "nome": "acao",
      "formato": "ENUM (reenviar | cancelar | cobranca_direta)",
      "obrigatorio": true,
      "descricao": "Como tratar o contrato que falhou em folha. `reenviar` exige snapshot da situação anterior; sem ele responde 409."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (3..500 caracteres)",
      "obrigatorio": true,
      "descricao": "Justificativa, obrigatória — a falha em folha é evento auditado."
    }
  ],
  "POST /v1/external/banco/tabelas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Informe para atualizar uma tabela existente; omita para criar."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio ao qual a tabela pertence."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": true,
      "descricao": "**CET** a.m. desta faixa, em FRAÇÃO (0..1): juros, IOF, tarifas e seguro, tudo dentro. É o CET máximo que você pratica no prazo — o contrato pode sair abaixo, nunca acima. Não pode passar do teto de CET do convênio (`taxa_max_am`) nem ficar abaixo de 0,5% a.m.; os dois casos respondem 422."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO (até 60 caracteres)",
      "obrigatorio": false,
      "descricao": "Nome comercial do produto (ex.: \"Curto\", \"Campanha servidor ativo\"). Aparece pro servidor na comparação de ofertas."
    },
    {
      "nome": "prazo_min_meses",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": false,
      "descricao": "Primeira parcela coberta por esta tabela. Omitido = 1. Junto com `prazo_max_meses` forma a faixa do produto — é o que permite cobrar CETs diferentes por prazo no mesmo convênio. Faixa que cruza a de outra tabela ativa do convênio é recusada com 422."
    },
    {
      "nome": "prazo_max_meses",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Última parcela coberta por esta tabela. Não pode passar do teto de parcelas do convênio."
    },
    {
      "nome": "vigencia_inicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Início da vigência, AAAA-MM-DD. Não pode ser anterior ao início do convênio."
    },
    {
      "nome": "vigencia_fim",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Fim da vigência, AAAA-MM-DD. Se o convênio tem fim definido, esta data é obrigatória e não pode ultrapassá-lo."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Tabela disponível para novas simulações.",
      "padrao": "true"
    }
  ],
  "POST /v1/external/banco/ofertas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Informe para atualizar; omita para criar."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO (3..120 caracteres)",
      "obrigatorio": true,
      "descricao": "Título exibido na vitrine do servidor."
    },
    {
      "nome": "mensagem",
      "formato": "ALFANUMERICO (3..500 caracteres)",
      "obrigatorio": true,
      "descricao": "Chamada da oferta."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (> 0 até 1)",
      "obrigatorio": true,
      "descricao": "Taxa mensal em FRAÇÃO (0.0179 = 1,79% a.m.) — mesma convenção de contratos e tabelas."
    },
    {
      "nome": "parcelas_max",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Prazo máximo anunciado."
    },
    {
      "nome": "valor_max",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor máximo anunciado."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Data de expiração, AAAA-MM-DD. Vazio never expira."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Oferta visível na vitrine.",
      "padrao": "true"
    },
    {
      "nome": "icone",
      "formato": "ALFANUMERICO (até 8 caracteres)",
      "obrigatorio": false,
      "descricao": "Emoji exibido no card."
    },
    {
      "nome": "tipo",
      "formato": "ENUM (credito_novo | portabilidade | refinanciamento | cartao_consignado | cartao_beneficio)",
      "obrigatorio": false,
      "descricao": "Produto anunciado."
    },
    {
      "nome": "filtro",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Público-alvo. Atenção: os campos deste objeto são camelCase, diferente do resto do corpo.",
      "padrao": "{}",
      "campos": [
        {
          "nome": "convenioIds",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Restringe a oferta a estes convênios."
        },
        {
          "nome": "vinculos",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Vínculos alcançados (CLT, ESTATUTARIO...)."
        },
        {
          "nome": "situacaoFuncional",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Situações funcionais alcançadas."
        },
        {
          "nome": "prefeituraIds",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": false,
          "descricao": "Restringe a estas prefeituras. Nome historico de `unidadeIds`."
        },
        {
          "nome": "unidadeIds",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": false,
          "descricao": "Restringe a estas unidades pagadoras."
        },
        {
          "nome": "salarioMin",
          "formato": "NUMERICO",
          "obrigatorio": false,
          "descricao": "Piso de salário líquido."
        },
        {
          "nome": "salarioMax",
          "formato": "NUMERICO",
          "obrigatorio": false,
          "descricao": "Teto de salário líquido."
        },
        {
          "nome": "idadeMin",
          "formato": "NUMERICO inteiro",
          "obrigatorio": false,
          "descricao": "Idade mínima do servidor."
        },
        {
          "nome": "idadeMax",
          "formato": "NUMERICO inteiro",
          "obrigatorio": false,
          "descricao": "Idade máxima do servidor."
        }
      ]
    }
  ],
  "POST /v1/external/banco/portabilidade": [
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Proponente: valor do contrato destino, em reais. Precisa cobrir o saldo informado pela credora."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Liberado ao servidor alem da quitacao, em reais. Ausente = valor_financiado − saldo informado."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Parcela do contrato destino, em reais. Voce calcula; a averbadora confere a margem DEPOIS da quitacao da origem."
    },
    {
      "nome": "prazo",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Prazo do contrato destino, em meses. Respeita a faixa do seu convenio."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": true,
      "descricao": "CET mensal em FRACAO (0.0179 = 1,79% a.m.). Sujeito ao teto de CET do convenio e ao minimo de 0,5% a.m."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": false,
      "descricao": "Juros mensais em FRACAO, opcionais: so' conferidos contra o CET (CET >= juros)."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": true,
      "descricao": "Numero do contrato destino no seu sistema. Nao pode repetir dentro do banco."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO (1..64 caracteres)",
      "obrigatorio": true,
      "descricao": "Contrato de origem para o qual o banco apresenta a proposta com condições."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/proposta": [
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Proponente: valor do contrato destino, em reais. Precisa cobrir o saldo informado pela credora."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Liberado ao servidor alem da quitacao, em reais. Ausente = valor_financiado − saldo informado."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Parcela do contrato destino, em reais. Voce calcula; a averbadora confere a margem DEPOIS da quitacao da origem."
    },
    {
      "nome": "prazo",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Prazo do contrato destino, em meses. Respeita a faixa do seu convenio."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": true,
      "descricao": "CET mensal em FRACAO (0.0179 = 1,79% a.m.). Sujeito ao teto de CET do convenio e ao minimo de 0,5% a.m."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": false,
      "descricao": "Juros mensais em FRACAO, opcionais: so' conferidos contra o CET (CET >= juros)."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": true,
      "descricao": "Numero do contrato destino no seu sistema. Nao pode repetir dentro do banco."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/saldo": [
    {
      "nome": "saldo_devedor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Saldo de quitação em reais. Esta ação aceita a portabilidade e informa o saldo na mesma transação."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/decisao": [
    {
      "nome": "decisao",
      "formato": "ENUM (aceitar | recusar)",
      "obrigatorio": true,
      "descricao": "Aceitar informando o saldo de quitação na mesma ação ou recusar a portabilidade."
    },
    {
      "nome": "saldo_devedor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Obrigatório ao aceitar: saldo de quitação em reais."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Obrigatório para recusar a portabilidade."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/pagamento": [
    {
      "nome": "valor_pago",
      "formato": "NUMERICO (> 0 até 1000000000)",
      "obrigatorio": true,
      "descricao": "Valor pago à credora, em reais. Para anexar comprovante, envie multipart/form-data com o campo comprovante."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/cancelar": [
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Motivo do cancelamento, mostrado ao servidor e a credora."
    }
  ],
  "POST /v1/external/banco/refinanciamentos": [
    {
      "nome": "origens",
      "formato": "LISTA de ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": true,
      "descricao": "Contratos SEUS, da mesma matrícula e do mesmo convênio, que o refinanciamento quita. Mais de um = unificação."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor do novo contrato, em reais. Precisa cobrir os saldos de quitação."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Parcela (margem) do novo contrato, em reais. Você calcula; a averbadora não aplica Price/PMT."
    },
    {
      "nome": "prazo",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Prazo do novo contrato, em meses. Respeita o teto do convênio."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": true,
      "descricao": "Taxa mensal em FRAÇÃO (0.0179 = 1,79% a.m.). Respeita o teto do convênio."
    },
    {
      "nome": "troco",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Valor liberado ao servidor, em reais. Ausente = sugestão valor_financiado − saldo, se o convênio permite troco."
    },
    {
      "nome": "saldos_quitacao",
      "formato": "MAPA (chave livre -> NUMERICO (> 0))",
      "obrigatorio": false,
      "descricao": "Saldo de quitação por contrato de origem (chave = número do contrato). Ausente = saldo devedor registrado na averbadora."
    }
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/responder": [
    {
      "nome": "acao",
      "formato": "ENUM (aprovar | recusar)",
      "obrigatorio": true,
      "descricao": "`aprovar` responde com as suas condições; `recusar` encerra o pedido (exige `motivo`)."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Motivo da recusa, mostrado ao servidor."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Ao aprovar: valor do novo contrato, em reais."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Ao aprovar: parcela (margem) em reais. Ausente = a parcela pedida pelo servidor."
    },
    {
      "nome": "prazo",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": false,
      "descricao": "Ao aprovar: prazo em meses. Ausente = o prazo pedido."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": false,
      "descricao": "Ao aprovar (obrigatória): taxa mensal em FRAÇÃO."
    },
    {
      "nome": "troco",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Ao aprovar: troco em reais. Ausente = sugestão valor_financiado − saldo, se o convênio permite."
    },
    {
      "nome": "saldos_quitacao",
      "formato": "MAPA (chave livre -> NUMERICO (> 0))",
      "obrigatorio": false,
      "descricao": "Saldo de quitação por contrato de origem (chave = número do contrato). Ausente = saldo devedor registrado na averbadora."
    }
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/cancelar": [
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Motivo do cancelamento, mostrado ao servidor."
    }
  ],
  "POST /v1/external/banco/bate-carteira": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(?:0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competência da conferência, no formato AAAAMM (202608 = agosto de 2026)."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Restringe a conferência a uma prefeitura."
    }
  ],
  "POST /v1/external/banco/webhooks": [
    {
      "nome": "url",
      "formato": "EFFECTS",
      "obrigatorio": true,
      "descricao": "Endpoint HTTPS PÚBLICO que receberá as entregas. localhost, IP privado e link-local são recusados (proteção contra SSRF). Responda 2xx rápido: o timeout é de 8 segundos."
    },
    {
      "nome": "events",
      "formato": "LISTA de ENUM (proposta.criada | proposta.aprovada | proposta.rejeitada | proposta.expirada | contrato.averbado | contrato.suspenso | contrato.cancelado | contrato.quitado | contrato.alongado | contrato.alterado | folha.sincronizada | folha.fechada | servidor.bloqueado | servidor.desbloqueado | comunicado.publicado | portabilidade.solicitada | portabilidade.proposta_informada | portabilidade.confirmada | portabilidade.saldo_informado | portabilidade.pagamento_informado | portabilidade.concluida | portabilidade.cancelada | portabilidade.expirada | refinanciamento.solicitado | refinanciamento.ofertado | refinanciamento.aceito | refinanciamento.recusado | refinanciamento.cancelado | refinanciamento.expirado | refinanciamento.efetivado | webhook.test)",
      "obrigatorio": true,
      "descricao": "Eventos assinados. Cada entrega é uma requisição ao seu servidor — assine só o que você trata."
    }
  ],
  "POST /v1/external/banco/cartoes/reservas": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor no órgão ou entidade do convênio."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio sob o qual a reserva é feita. Precisa ser um convênio do seu banco; de outro responde 404."
    },
    {
      "nome": "produto",
      "formato": "ENUM (CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Cartão de crédito consignado ou cartão benefício — o balde de margem que a reserva ocupa."
    },
    {
      "nome": "valor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor da parcela contratada, em reais: o teto de cada lançamento. A reserva ocupa sempre o disponível inteiro do balde, independentemente deste valor, para que a margem não fique livre para outra consignatária. Ausente = a parcela é o disponível inteiro. Acima do disponível é recusado."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": false,
      "descricao": "Prazo da reserva, em parcelas: cada lançamento é uma delas, e a última quita a reserva. Ausente = reserva sem prazo."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": false,
      "descricao": "Número do contrato no seu sistema."
    },
    {
      "nome": "vencimento",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Vencimento, AAAA-MM-DD."
    },
    {
      "nome": "data_liberacao",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Data de liberação do crédito, AAAA-MM-DD."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor total financiado, em reais."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor líquido liberado, em reais."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "IOF, em reais."
    },
    {
      "nome": "juros_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Juros do rotativo a.m., em FRAÇÃO (0.0399 é 3,99% a.m.)."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "CET a.m., em FRAÇÃO, conforme a CCB."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro (mín. 0)",
      "obrigatorio": false,
      "descricao": "Dias de carência."
    },
    {
      "nome": "valor_carencia",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Valor da carência, em reais."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO (até 500 caracteres)",
      "obrigatorio": false,
      "descricao": "Texto livre registrado no histórico da reserva."
    }
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competência do desconto, AAAAMM. Só competência aberta: antes do corte e com a folha não fechada, e nunca anterior à reserva."
    },
    {
      "nome": "valor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor a descontar nesta competência, em reais — a fatura do mês. Não passa do valor reservado. Reenviar a mesma competência substitui."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO (1..60 caracteres)",
      "obrigatorio": false,
      "descricao": "Número do contrato no seu sistema."
    },
    {
      "nome": "vencimento",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Vencimento, AAAA-MM-DD."
    },
    {
      "nome": "data_liberacao",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Data de liberação do crédito, AAAA-MM-DD."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor total financiado, em reais."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Valor líquido liberado, em reais."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "IOF, em reais."
    },
    {
      "nome": "juros_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "Juros do rotativo a.m., em FRAÇÃO (0.0399 é 3,99% a.m.)."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "CET a.m., em FRAÇÃO, conforme a CCB."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro (mín. 0)",
      "obrigatorio": false,
      "descricao": "Dias de carência."
    },
    {
      "nome": "valor_carencia",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Valor da carência, em reais."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO (até 500 caracteres)",
      "obrigatorio": false,
      "descricao": "Texto livre registrado no lançamento."
    }
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos/cancelar": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competência do lançamento a cancelar, AAAAMM. Só antes do corte."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 300 caracteres)",
      "obrigatorio": false,
      "descricao": "Por que o lançamento saiu. Fica no histórico."
    }
  ],
  "POST /v1/external/banco/cartoes/lancamentos": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competência de todos os itens do lote, AAAAMM."
    },
    {
      "nome": "itens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Até 500 lançamentos. Cada item é decidido sozinho: a recusa de um não desfaz os outros.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "ADF da reserva."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO (> 0)",
          "obrigatorio": true,
          "descricao": "Valor a descontar nesta competência, em reais."
        }
      ]
    }
  ],
  "POST /v1/external/prefeitura/adf/confirmar": [
    {
      "nome": "ids",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "IDs de ADF a marcar como aplicadas em folha, no formato `ADF-AAAAMM-...` — e' o campo `id` de `GET /adf`, NAO o `adf` do contrato. A competencia sai do proprio id, entao ids de meses diferentes podem vir juntos. Maximo de 500 por chamada."
    }
  ],
  "POST /v1/external/prefeitura/adf/falha": [
    {
      "nome": "ids",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "IDs de ADF que NAO entraram na folha, no formato `ADF-AAAAMM-...`. Maximo de 500 por chamada."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (3..300 caracteres)",
      "obrigatorio": true,
      "descricao": "Por que o desconto nao entrou. Chega ao banco como `motivo_falha` e e' o que ele usa pra decidir entre reenviar, cancelar ou cobrar direto — motivo generico devolve a decisao pra voce depois."
    }
  ],
  "POST /v1/external/prefeitura/folhas": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(?:0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competencia da folha em AAAAMM (ex.: 202608). So existe uma folha por competencia nesta prefeitura — repetir responde 400."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO (mín. 8 caracteres)",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha (AAAA-MM-DD)."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO (mín. 8 caracteres)",
      "obrigatorio": false,
      "descricao": "Data prevista do repasse (AAAA-MM-DD). A partir dela a folha pode ser fechada mesmo sem movimentacao."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro (mín. 1)",
      "obrigatorio": false,
      "descricao": "Id da unidade pagadora em que a folha sera aberta. Ausente = a organizacao do token."
    }
  ],
  "PATCH /v1/external/prefeitura/folhas/{id}": [
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada)",
      "obrigatorio": false,
      "descricao": "Novo estado. Fechar exige ao menos uma movimentacao registrada — se nao houve mudanca de pessoal no mes, importe uma linha com `tipo=desconto`. Fechar tambem ABRE sozinha a competencia seguinte."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nova data de corte (AAAA-MM-DD)."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Nova data de repasse (AAAA-MM-DD). Envie `null` para limpar; omitir mantem o valor atual."
    }
  ],
  "POST /v1/external/prefeitura/anuencia": [
    {
      "nome": "aceito",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Confirmacao explicita. So o valor `true` e aceito — nao existe registrar anuencia como recusada."
    },
    {
      "nome": "aceito_por",
      "formato": "ALFANUMERICO (mín. 2 caracteres)",
      "obrigatorio": true,
      "descricao": "Nome de quem aceita, pessoa fisica responsavel. Fica no registro junto do IP de origem e da versao do termo; e' o dado que sustenta a anuencia numa auditoria."
    }
  ],
  "POST /v1/external/servidor/propostas/simular": [
    {
      "nome": "valor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor a simular, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro (mín. 1)",
      "obrigatorio": true,
      "descricao": "Prazo em meses."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": false,
      "descricao": "CET mensal em FRACAO (0.0179 = 1,79% a.m.). Omitir usa 1,79% como ESTIMATIVA — o CET real vem da tabela do banco no convenio, e a simulacao nao reserva nada nem vincula ninguem.",
      "padrao": "0.0179"
    }
  ],
  "POST /v1/external/servidor/propostas": [
    {
      "nome": "valor",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "Valor solicitado, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro (mín. 1)",
      "obrigatorio": true,
      "descricao": "Prazo em meses."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO (> 0)",
      "obrigatorio": true,
      "descricao": "CET mensal em FRACAO, exatamente o de uma opcao de `GET /ofertas` para o prazo escolhido. Sujeito ao teto de CET do convenio."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Matricula a usar quando o CPF tem mais de um vinculo (acumulacao de cargos). Omitir usa a matricula do token — e cada matricula tem margem PROPRIA, entao escolher a errada muda o que cabe."
    },
    {
      "nome": "tipo",
      "formato": "ENUM (novo | portabilidade | refinanciamento)",
      "obrigatorio": false,
      "descricao": "Natureza da operacao. `portabilidade` responde 422 `use_portabilidade`: portabilidade tem rito proprio em `POST /portabilidade`.",
      "padrao": "\"novo\""
    },
    {
      "nome": "senha",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Senha de transacao do servidor. Exigida quando a prefeitura do convenio pede autorizacao explicita — o token identifica a aplicacao, a senha identifica a pessoa."
    },
    {
      "nome": "oferta_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Oferta que originou a proposta. Quando presente, valor e prazo sao conferidos contra os limites dela."
    }
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/confirmar": [
    {
      "nome": "senha",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": true,
      "descricao": "Senha de transacao do servidor. Obrigatoria: esta rota e' o aceite de uma operacao que o BANCO iniciou, e o aceite e' da pessoa, nao da aplicacao."
    }
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/rejeitar": [
    {
      "nome": "senha",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": true,
      "descricao": "Senha de transacao do servidor."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 300 caracteres)",
      "obrigatorio": false,
      "descricao": "Motivo da recusa, registrado no historico do contrato."
    }
  ],
  "POST /v1/external/servidor/portabilidade": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Matricula a usar quando o CPF tem mais de um vinculo."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO (1..64 caracteres)",
      "obrigatorio": true,
      "descricao": "Contrato a portar, de `GET /portabilidade/elegiveis`. Precisa ser de uma matricula deste CPF — contrato de terceiro responde 404."
    },
    {
      "nome": "banco_destino_id",
      "formato": "NUMERICO inteiro (mín. 1)",
      "obrigatorio": true,
      "descricao": "Banco que vai receber o contrato (`bancos_destino` de `GET /portabilidade/elegiveis`). Nao pode ser a credora atual."
    },
    {
      "nome": "senha",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Senha de transacao, exigida quando a prefeitura pede autorizacao explicita."
    }
  ],
  "POST /v1/external/servidor/portabilidade/{id}/confirmar": [
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro (mín. 0)",
      "obrigatorio": false,
      "descricao": "Versão da proposta exibida ao servidor, retornada no detalhe. Obrigatória no fluxo novo; operações anteriores podem concluir sem esse campo."
    },
    {
      "nome": "senha",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Senha de transacao. Confirmar e' o consentimento da PESSOA com a portabilidade que o banco de destino abriu."
    }
  ],
  "POST /v1/external/servidor/portabilidade/{id}/cancelar": [
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Motivo, mostrado aos dois bancos."
    }
  ],
  "POST /v1/external/servidor/beneficios/{id}/contratar": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Matricula a usar quando o CPF tem mais de um vinculo."
    }
  ],
  "POST /v1/external/averbadora/bancos": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Banco a ATUALIZAR. Omitir cria um novo; enviar o id de um existente sobrescreve os campos enviados."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO (mín. 2 caracteres)",
      "obrigatorio": true,
      "descricao": "Razao social ou nome de exibicao do banco."
    },
    {
      "nome": "status",
      "formato": "ENUM (ativo | pausado | inativo)",
      "obrigatorio": false,
      "descricao": "Situacao. `pausado` derruba TODOS os tokens do banco na hora — as chamadas dele passam a responder 401 ate a reativacao.",
      "padrao": "\"ativo\""
    },
    {
      "nome": "adapter",
      "formato": "ENUM (sandbox | ifractal)",
      "obrigatorio": false,
      "descricao": "Adaptador bancario. `sandbox` nao toca sistema externo; `ifractal` fala com a API real do banco.",
      "padrao": "\"sandbox\""
    },
    {
      "nome": "contatoEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail de contato tecnico. Note o camelCase.",
      "padrao": "\"\""
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Escopos padrao do banco. NAO sao os escopos do token — estes sao do cadastro; o token tem os proprios.",
      "padrao": "[\"propostas:rw\"]"
    },
    {
      "nome": "mtlsHabilitado",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se a integracao exige mTLS. Note o camelCase.",
      "padrao": "false"
    }
  ],
  "POST /v1/external/averbadora/prefeituras": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Prefeitura a atualizar. Omitir cria uma nova."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO (mín. 2 caracteres)",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF com exatamente 2 letras."
    },
    {
      "nome": "municipioIbge",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Codigo IBGE do municipio. Note o camelCase. OBRIGATORIO so na esfera municipal (default): fora dela e a sede, e uma assembleia estadual nao pertence a municipio nenhum. Sem esfera declarada, omiti-lo continua sendo 400, como antes da feature 009."
    },
    {
      "nome": "modoIntegracao",
      "formato": "ENUM (REST | SOAP | CSV | MANUAL)",
      "obrigatorio": false,
      "descricao": "Como a prefeitura troca dados com a plataforma. Note o camelCase.",
      "padrao": "\"REST\""
    },
    {
      "nome": "status",
      "formato": "ENUM (ativo | pausado)",
      "obrigatorio": false,
      "descricao": "Situacao da prefeitura.",
      "padrao": "\"ativo\""
    },
    {
      "nome": "tipo",
      "formato": "ENUM (prefeitura | camara_municipal | governo_estadual | assembleia_legislativa | tribunal | ministerio_publico | tribunal_de_contas | defensoria_publica | secretaria | autarquia | fundacao | fundo | instituto_previdencia | empresa_publica | consorcio_publico | outra)",
      "obrigatorio": false,
      "descricao": "`prefeitura` | `camara` | `governo_estadual` | `assembleia_legislativa` | `tribunal` | `autarquia` | `fundacao` | `fundo` | `instituto_previdencia` | `empresa_publica` | `consorcio` | `secretaria`. Ausente = `prefeitura`, que e' o que todo cadastro anterior de fato era."
    },
    {
      "nome": "esfera",
      "formato": "ENUM (municipal | estadual | federal)",
      "obrigatorio": false,
      "descricao": "`municipal` | `estadual` | `federal` | `distrital`. Ausente = `municipal`. **O municipio so' e' obrigatorio na esfera municipal** — uma assembleia estadual nao pertence a municipio nenhum."
    },
    {
      "nome": "poder",
      "formato": "ENUM (executivo | legislativo | judiciario | autonomo | outro)",
      "obrigatorio": false,
      "descricao": "`executivo` | `legislativo` | `judiciario` | `autonomo`. Ausente = sugerido pelo tipo."
    },
    {
      "nome": "classificacao_adm",
      "formato": "ENUM (direta | indireta | nao_se_aplica)",
      "obrigatorio": false,
      "descricao": "`direta` | `indireta` | `nao_se_aplica`. Ausente = sugerida pelo tipo."
    },
    {
      "nome": "sigla",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Como a casa se chama. Fora do tipo prefeitura, e' o que o servidor ve."
    }
  ],
  "POST /v1/external/averbadora/convenios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Convenio a atualizar. Omitir cria um novo."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco do convenio. Precisa existir — senao 404."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura do convenio. Precisa existir — senao 404."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO (mín. 2 caracteres)",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO (até 4 caracteres)",
      "obrigatorio": false,
      "descricao": "Rubrica unica, do modelo antigo: atende todos os produtos. Ate' 4 caracteres. Continua aceita, mas prefira `verbas` — uma rubrica so' para emprestimo e cartao impede conferir os tetos de margem, que sao independentes. Omitir os dois cria um convenio sem verba declarada, que nao origina contrato ate' ser completado."
    },
    {
      "nome": "verbas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": false,
      "descricao": "Uma verba por produto ofertado. E' esta declaracao que faz a folha reconhecer o desconto como sendo deste banco — nao ha' passo seguinte.",
      "campos": [
        {
          "nome": "produto",
          "formato": "ENUM (EMPRESTIMO | CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
          "obrigatorio": true,
          "descricao": "Produto que esta verba atende. Uma entrada por produto: a mesma verba em dois produtos e' recusada."
        },
        {
          "nome": "codigo",
          "formato": "ALFANUMERICO (1..4 caracteres)",
          "obrigatorio": true,
          "descricao": "Codigo da verba na folha DESTA prefeitura, ate' 4 caracteres. Pode divergir do que o banco usa em outras."
        },
        {
          "nome": "competencia_inicial",
          "formato": "ALFANUMERICO (padrão ^\\d{4}(?:0[1-9]|1[0-2])$)",
          "obrigatorio": false,
          "descricao": "AAAAMM a partir de quando a verba vale. Opcional: omitido, a vigencia abre na competencia mais antiga em que esta verba ja descontava nesta unidade (quarentena ou holerite), para que a divida que ja estava na folha seja reconhecida. Informe um valor so quando a verba passa a valer de uma competencia especifica. Competencia anterior a vigencia atual, com o mesmo banco e produto, estende a vigencia para tras; com banco ou produto diferente e recusada."
        },
        {
          "nome": "situacao",
          "formato": "ENUM (ativa | inativa)",
          "obrigatorio": false,
          "descricao": "Default `ativa`. Inativa para de originar naquele produto e NAO solta os descontos que ja estao na folha."
        },
        {
          "nome": "correcao_cadastral",
          "formato": "BOOLEANO",
          "obrigatorio": false,
          "descricao": "`true` = a verba SEMPRE foi esta e houve erro de digitacao: reescreve a vigencia atual. Sem isto, competencia anterior a' vigente e' recusada, porque reescreveria meses ja processados."
        }
      ]
    },
    {
      "nome": "data_corte",
      "formato": "NUMERICO inteiro (1..31)",
      "obrigatorio": true,
      "descricao": "@deprecated Sem efeito: o dia do corte e' definido no orgao e vale para todos os convenios dele. O valor enviado e' guardado como registro comercial, e a resposta devolve o dia do orgao."
    },
    {
      "nome": "dia_repasse",
      "formato": "NUMERICO inteiro (1..31)",
      "obrigatorio": true,
      "descricao": "@deprecated Sem efeito: o dia do repasse e' definido no orgao. O valor enviado e' guardado como registro comercial, e a resposta devolve o dia do orgao."
    }
  ],
  "POST /v1/external/averbadora/convenios/{id}/config": [
    {
      "nome": "prazoTravaHoras",
      "formato": "NUMERICO inteiro (1..720)",
      "obrigatorio": false,
      "descricao": "Validade da reserva de margem, em horas. Sem configuracao a plataforma aplica 48h — este default descreve o comportamento real.",
      "padrao": "48"
    },
    {
      "nome": "prazoPortabilidadeDU",
      "formato": "NUMERICO inteiro (1..30)",
      "obrigatorio": false,
      "descricao": "Validade da trava de portabilidade, em dias uteis. Sem configuracao a plataforma aplica 7.",
      "padrao": "7"
    },
    {
      "nome": "maxParcelas",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": true,
      "descricao": "Teto de parcelas do convenio. OBRIGATORIO — nao ha padrao. A averbacao recusa acima disto, e o teto duro da plataforma (120) continua valendo por cima."
    },
    {
      "nome": "taxaMaxAm",
      "formato": "NUMERICO (0..20)",
      "obrigatorio": true,
      "descricao": "Teto de CET mensal em PERCENTUAL (1.79 = 1,79% a.m.), para emprestimo, portabilidade e refinanciamento — cartao nao passa por ele. Atencao a unidade: o `taxa_am` que os bancos enviam e' FRACAO (0.0179). Zero ou ausente desliga o teto (o minimo de 0,5% a.m. continua valendo)."
    },
    {
      "nome": "idadeMin",
      "formato": "NUMERICO inteiro (0..120)",
      "obrigatorio": false,
      "descricao": "Idade minima. INFORMATIVA: nenhuma validacao da plataforma recusa averbacao por idade. Ausente = nao definida."
    },
    {
      "nome": "idadeMax",
      "formato": "NUMERICO inteiro (1..120)",
      "obrigatorio": false,
      "descricao": "Idade maxima. Tambem informativa, pelo mesmo motivo."
    },
    {
      "nome": "vinculosAceitos",
      "formato": "LISTA de ENUM (CLT | ESTATUTARIO | COMISSIONADO | APOSENTADO | PENSIONISTA)",
      "obrigatorio": true,
      "descricao": "Vinculos que a prefeitura declara aceitar. INFORMATIVO: nenhum handler consulta esta lista para recusar operacao."
    },
    {
      "nome": "formatoImportacao",
      "formato": "ENUM (CSV | EXCEL | API)",
      "obrigatorio": true,
      "descricao": "Formato combinado para a prefeitura enviar a base."
    },
    {
      "nome": "regrasEspeciais",
      "formato": "ALFANUMERICO (até 2000 caracteres)",
      "obrigatorio": false,
      "descricao": "Texto livre com as particularidades deste convenio. E' o unico canal de regra por convenio que nao cabe em campo estruturado, e chega ao banco na documentacao dele.",
      "padrao": "\"\""
    },
    {
      "nome": "vigenciaInicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia (AAAA-MM-DD). Tabela de banco nao pode comecar antes disto."
    },
    {
      "nome": "vigenciaFim",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Fim da vigencia (AAAA-MM-DD). Ausente = indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se a configuracao esta valendo.",
      "padrao": "true"
    },
    {
      "nome": "percentualComissao",
      "formato": "NUMERICO (0..1)",
      "obrigatorio": false,
      "descricao": "Fatia do valor financiado repassada ao banco, em FRACAO (0.02 = 2%). Ausente faz o relatorio de faturamento cair em 2%."
    }
  ],
  "PATCH /v1/external/averbadora/servidores/{matricula}": [
    {
      "nome": "nome",
      "formato": "ALFANUMERICO (mín. 2 caracteres)",
      "obrigatorio": false,
      "descricao": "Novo nome."
    },
    {
      "nome": "cpf",
      "formato": "ALFANUMERICO (padrão ^\\d{11}$)",
      "obrigatorio": false,
      "descricao": "Novo CPF, 11 digitos."
    },
    {
      "nome": "vinculo",
      "formato": "ENUM (CLT | ESTATUTARIO | COMISSIONADO)",
      "obrigatorio": false,
      "descricao": "Novo vinculo."
    },
    {
      "nome": "situacaoFuncional",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": false,
      "descricao": "Nova situacao funcional."
    },
    {
      "nome": "salarioLiquido",
      "formato": "NUMERICO (mín. 0)",
      "obrigatorio": false,
      "descricao": "Novo salario liquido. Reduzir isto reduz a margem na hora, e contratos ja averbados NAO sao revistos."
    },
    {
      "nome": "email",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Novo e-mail. String vazia limpa o campo."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Novo telefone."
    },
    {
      "nome": "cargo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Novo cargo."
    },
    {
      "nome": "endereco",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Novo endereco."
    },
    {
      "nome": "dataAdmissao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nova data de admissao."
    },
    {
      "nome": "dataNascimento",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nova data de nascimento."
    },
    {
      "nome": "camposCustom",
      "formato": "MAPA (chave livre -> ALFANUMERICO)",
      "obrigatorio": false,
      "descricao": "Campos extras definidos pela prefeitura. Note o camelCase."
    }
  ],
  "POST /v1/external/averbadora/pre-reservas/{id}/cancelar": [
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (3..200 caracteres)",
      "obrigatorio": true,
      "descricao": "Justificativa do cancelamento, registrada na auditoria. Cancelar libera a margem travada."
    }
  ],
  "POST /v1/external/averbadora/folhas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Folha a atualizar. Omitir cria uma nova."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura da folha. Precisa existir."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(?:0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` limpa."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": false,
      "descricao": "Situacao, so' conferida. Folha nova nasce `aberta`; na edicao, omitir mantem o status gravado, e um valor diferente dele e' recusado. Fechar e consolidar tem rotas proprias — `POST /folhas/{id}/consolidar` e' a unica que leva a `consolidada`."
    }
  ],
  "POST /v1/external/averbadora/adf/confirmar": [
    {
      "nome": "ids",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "IDs de ADF a aplicar, no formato `ADF-AAAAMM-...`. Diferente da camada prefeitura, aqui o lote pode cruzar PREFEITURAS — confira o que esta enviando. Maximo de 500."
    }
  ],
  "POST /v1/external/averbadora/adf/falha": [
    {
      "nome": "ids",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "IDs de ADF a reprovar. Pode cruzar prefeituras. Maximo de 500."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO (mín. 3 caracteres)",
      "obrigatorio": true,
      "descricao": "Motivo da reprovacao, repassado ao banco de cada contrato."
    }
  ],
  "POST /v1/external/averbadora/bate-carteira": [
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco a confrontar. Precisa existir — senao 404."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO (padrão ^\\d{4}(?:0[1-9]|1[0-2])$)",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Restringe o confronto a uma prefeitura."
    }
  ],
  "POST /v1/external/averbadora/comunicados": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Comunicado a atualizar. Omitir cria um novo."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO (mín. 1 caracteres)",
      "obrigatorio": true,
      "descricao": "Texto do comunicado."
    },
    {
      "nome": "linkLabel",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Rotulo do link. Note o camelCase."
    },
    {
      "nome": "linkHref",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Destino do link. Note o camelCase."
    },
    {
      "nome": "publico",
      "formato": "ENUM (banco | servidor | prefeitura)",
      "obrigatorio": true,
      "descricao": "Quem VE o comunicado. Escolher errado publica para o publico errado — nao ha previa."
    }
  ],
  "POST /v1/external/averbadora/vitrine": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Banner a atualizar. Omitir cria um novo."
    },
    {
      "nome": "bancoId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco anunciante. Precisa existir. Note o camelCase."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo do banner."
    },
    {
      "nome": "imagemUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL da imagem. Note o camelCase."
    },
    {
      "nome": "precoPorClique",
      "formato": "NUMERICO (0..1000)",
      "obrigatorio": false,
      "descricao": "Preco por clique cobrado do banco, em reais."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o banner esta circulando.",
      "padrao": "true"
    }
  ],
  "POST /v1/external/averbadora/termos/{tipo}": [
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO (3..200 caracteres)",
      "obrigatorio": false,
      "descricao": "Novo titulo."
    },
    {
      "nome": "descricao",
      "formato": "ALFANUMERICO (até 500 caracteres)",
      "obrigatorio": false,
      "descricao": "Nova descricao."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO (10..20000 caracteres)",
      "obrigatorio": false,
      "descricao": "Novo texto do termo. Aceita marcadores de nome e CPF, preenchidos com o titular na hora da leitura."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o termo esta vigente."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO (até 20 caracteres)",
      "obrigatorio": false,
      "descricao": "Versao. E' ela que fica gravada em cada aceite — mudar o corpo SEM mudar a versao faz aceites antigos apontarem para um texto que ja nao e' o que a pessoa leu."
    }
  ],
  "POST /v1/external/averbadora/eventos/disparar": [
    {
      "nome": "event",
      "formato": "ENUM (proposta.criada | proposta.aprovada | proposta.rejeitada | proposta.expirada | contrato.averbado | contrato.suspenso | contrato.cancelado | contrato.quitado | contrato.alongado | contrato.alterado | folha.sincronizada | folha.fechada | servidor.bloqueado | servidor.desbloqueado | comunicado.publicado | portabilidade.solicitada | portabilidade.proposta_informada | portabilidade.confirmada | portabilidade.saldo_informado | portabilidade.pagamento_informado | portabilidade.concluida | portabilidade.cancelada | portabilidade.expirada | refinanciamento.solicitado | refinanciamento.ofertado | refinanciamento.aceito | refinanciamento.recusado | refinanciamento.cancelado | refinanciamento.expirado | refinanciamento.efetivado | webhook.test)",
      "obrigatorio": true,
      "descricao": "Evento a disparar. Ver o anexo de eventos de webhook."
    },
    {
      "nome": "environment",
      "formato": "ENUM (production | sandbox)",
      "obrigatorio": false,
      "descricao": "Ambiente dos webhooks a acionar. Omitir usa o ambiente do proprio token — em `production` os parceiros recebem de verdade."
    },
    {
      "nome": "payload",
      "formato": "MAPA (chave livre -> UNKNOWN)",
      "obrigatorio": false,
      "descricao": "Corpo do evento, livre. Entregue como veio.",
      "padrao": "{}"
    }
  ],
};

/**
 * Campos de RESPOSTA, dos schemas aplicados com `parse()` na saida. Em
 * endpoint de lista, descreve UM item de `data`; nos demais, o objeto `data`.
 * O envelope (`_meta`) e igual em toda a camada e e documentado a parte.
 */
export const CAMPOS_RESPOSTA_POR_ENDPOINT: Record<string, CampoDoc[]> = {
  "GET /v1/external/banco/margem": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor na prefeitura — identidade da operacao."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome completo do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD). O CPF completo nunca sai da API externa."
    },
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional (Efetivo, Comissionado, Aposentado...) conforme a folha da prefeitura."
    },
    {
      "nome": "situacao_funcional",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao do servidor na folha (Ativo, Licenca, Desligado...)."
    },
    {
      "nome": "comprometido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas que ja consomem a margem de EMPRESTIMO desta matricula, em reais. Conta proposta em analise (nao so contrato ativo — a margem e' reservada na proposta) e emprestimo consignado em outro banco informado pela prefeitura no tombamento."
    },
    {
      "nome": "margens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Um item por bucket de margem.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ENUM (EMPRESTIMO | CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
          "obrigatorio": true,
          "descricao": "Bucket de margem: QUAL margem a operacao consome. Por padrao os tres sao independentes; com margem informada de saldo compartilhado, consumir um reduz os outros."
        },
        {
          "nome": "total",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Margem bruta do bucket, em reais — percentual ou saldo definido pela regra da unidade pagadora: percentual sobre o salario (a lei do ente pode impor um teto somado) ou saldo informado pelo orgao (ver `fonte`)."
        },
        {
          "nome": "disponivel",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Quanto ainda cabe, em reais: total menos o que ja esta comprometido NESTE bucket. E' o valor que a parcela precisa respeitar — a averbacao recusa com 422 `margem_insuficiente` acima dele."
        },
        {
          "nome": "fonte",
          "formato": "ENUM (calculada | informada)",
          "obrigatorio": false,
          "descricao": "De onde veio a margem. `calculada`: percentual da regra da unidade pagadora sobre o salario liquido. `informada`: valor mandado pela folha no arquivo, que substitui o calculo (nos buckets de cartao, zero enquanto a folha nao os informa)."
        },
        {
          "nome": "percentual",
          "formato": "NUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Percentual da regra aplicado neste bucket, em FRACAO (0.4 = 40%). `null` quando a margem e' informada pela folha."
        },
        {
          "nome": "arquivo_competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Competencia (AAAAMM) do arquivo da folha que informou a margem. `null` quando a margem e' calculada."
        },
        {
          "nome": "arquivo_importado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Quando esse arquivo foi importado, ISO 8601. `null` quando a margem e' calculada."
        }
      ]
    }
  ],
  "GET /v1/external/banco/contratos": [
    {
      "nome": "cronograma_pendente",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Prazo do legado não informado. Não significa contrato quitado nem crédito indeterminado declarado."
    },
    {
      "nome": "parcelas_restantes",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Quantidade restante; null enquanto o cronograma está pendente."
    },
    {
      "nome": "saldo_devedor_conhecido",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Saldo calculado do cronograma; null quando desconhecido."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato na averbadora. E' a chave usada em todas as operacoes."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual. Ver o anexo de situacoes de contrato para a lista e as transicoes."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ENUM (EMPRESTIMO | REFIN | ECONSIGNADO)",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela mensal em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado, em meses."
    },
    {
      "nome": "data_lancamento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data de lancamento no formato DD/MM/AAAA."
    },
    {
      "nome": "folha_status",
      "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao da ADF na folha da prefeitura. `null` enquanto o contrato nao foi enviado a nenhuma folha."
    },
    {
      "nome": "origem",
      "formato": "ENUM (LEGADO | ATLAS)",
      "obrigatorio": true,
      "descricao": "Como esta obrigacao entrou no sistema. `ATLAS` = originada na plataforma. `LEGADO` = reconhecida a partir de desconto ja existente na folha (migracao), NUNCA originada aqui."
    },
    {
      "nome": "contrato_oficial",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "`false` para obrigacao LEGADA: nao ha contrato assinado nesta plataforma. Ser cliente da Atlas nao converte legado em oficial."
    },
    {
      "nome": "identificacao_atual",
      "formato": "ENUM (CONTRATO | RUBRICA)",
      "obrigatorio": true,
      "descricao": "O que identifica esta obrigacao hoje. `RUBRICA` = so' existe o codigo de verba da folha; `CONTRATO` = ha contrato desta plataforma."
    },
    {
      "nome": "forma_desconto",
      "formato": "ENUM (PARCELA | LANCAMENTO)",
      "obrigatorio": true,
      "descricao": "`LANCAMENTO` = RESERVA DE CARTAO: compromete a margem do cartao e nao desconta por si; o desconto de cada competencia e' o lancamento informado em `/cartoes/reservas/{adf}/lancamentos`, e `valor_parcela` e' o VALOR RESERVADO. `PARCELA` = todo outro contrato."
    }
  ],
  "GET /v1/external/banco/contratos/{adf}": [
    {
      "nome": "cronograma_pendente",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Prazo do legado pendente de informação."
    },
    {
      "nome": "parcelas_restantes",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Null quando o cronograma ainda não foi informado."
    },
    {
      "nome": "saldo_devedor_conhecido",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Null quando o saldo não pode ser calculado."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato na averbadora."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual. Ver o anexo de situacoes de contrato."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ENUM (EMPRESTIMO | REFIN | ECONSIGNADO)",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "tipo_margem",
      "formato": "ENUM (EMPRESTIMO | CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Bucket de margem consumido. Derivado do tipo quando o contrato e' anterior ao campo: ECONSIGNADO vira CARTAO_CONSIGNADO, o resto vira EMPRESTIMO."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "id_matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador unico da matricula no padrao da prefeitura — ver a documentacao do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio ao qual o contrato pertence."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica de desconto na folha da prefeitura."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero do contrato no sistema do proprio banco, quando informado."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor bruto financiado, em reais."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor liquido apurado, em reais."
    },
    {
      "nome": "valor_liquido_liberado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor efetivamente liberado ao servidor, quando informado na averbacao."
    },
    {
      "nome": "valor_carencia",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encargo do periodo de carencia, quando houve."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Emprestimo e refinanciamento: o CET mensal em FRACAO (0.0179 = 1,79% a.m.). Cartao: juros do rotativo."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Custo Efetivo Total mensal em fracao. Emprestimo: e' o proprio `taxa_am` informado pelo banco (juros, IOF, tarifas e seguro dentro). Cartao: calculado pela averbadora a partir dos juros, com IOF."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "IOF em reais. Emprestimo: 0 — o imposto esta dentro do CET informado pelo banco, e a averbadora nao o separa."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado, em meses."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela mensal em reais."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dias entre a liberacao do credito e a primeira parcela."
    },
    {
      "nome": "saldo_devedor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Saldo devedor atual, em reais."
    },
    {
      "nome": "parcelas_pagas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantidade de parcelas ja descontadas em folha."
    },
    {
      "nome": "data_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data do contrato (DD/MM/AAAA)."
    },
    {
      "nome": "lancamento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data de lancamento na averbadora (DD/MM/AAAA)."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Criacao em ISO 8601, com hora. Use este, e nao `lancamento`, para calcular a trava de 48h."
    },
    {
      "nome": "expiracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Expiracao da reserva (DD/MM/AAAA). `null` quando nao e' reserva."
    },
    {
      "nome": "expiracao_iso",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Expiracao em ISO 8601, com hora — precisao de segundos para o contador."
    },
    {
      "nome": "vencimento_1a_parcela",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Vencimento da primeira parcela, quando informado."
    },
    {
      "nome": "data_liberacao_credito",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data da liberacao do credito ao servidor, quando informada."
    },
    {
      "nome": "folha_primeiro_desconto",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do primeiro desconto (Mes/AAAA)."
    },
    {
      "nome": "folha_ultimo_desconto",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do ultimo desconto previsto (Mes/AAAA)."
    },
    {
      "nome": "folha_status",
      "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao global da ADF na folha. `null` enquanto nao foi enviado a nenhuma folha."
    },
    {
      "nome": "folha_motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo informado pela prefeitura quando a folha reprovou o desconto."
    },
    {
      "nome": "folha_competencias",
      "formato": "MAPA (chave livre -> ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto))",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao POR competencia (chave AAAAMM). E' o dado autoritativo: o contrato aparece em varias folhas, uma por mes de parcela."
    },
    {
      "nome": "banco_origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Banco de origem, quando o contrato substitui operacao de terceiro."
    },
    {
      "nome": "contrato_origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato de origem na portabilidade."
    },
    {
      "nome": "saldo_devedor_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo devedor quitado no banco de origem, em reais."
    },
    {
      "nome": "adf_vinculada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "ADF relacionada — refinanciamento ou portabilidade."
    },
    {
      "nome": "tem_ccb",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha contrato assinado (CCB) anexado. O arquivo em si nao e' exposto pela API externa."
    },
    {
      "nome": "ccb_anexado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o CCB atual foi anexado (ISO 8601)."
    },
    {
      "nome": "iniciada_pelo_banco",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Verdadeiro quando a operacao partiu do banco, e nao do servidor. Nesse caso, prefeituras que exigem senha de averbacao seguram o contrato em 'Aguardando Confirmacao do Servidor'."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Observacoes livres registradas na averbacao."
    },
    {
      "nome": "editada_pelo_banco",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Snapshot do que o servidor pediu antes da contra-proposta. `null` quando o banco nao editou.",
      "campos": [
        {
          "nome": "valor_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor financiado que o servidor havia pedido, antes da contra-proposta."
        },
        {
          "nome": "parcelas_original",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo pedido originalmente."
        },
        {
          "nome": "taxa_am_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal original, em fracao."
        },
        {
          "nome": "parcela_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela original em reais."
        },
        {
          "nome": "editado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando a edicao ocorreu (ISO 8601)."
        },
        {
          "nome": "editado_por",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Ator da edicao."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo registrado na contra-proposta."
        }
      ]
    },
    {
      "nome": "situacao_em",
      "formato": "MAPA (chave livre -> ALFANUMERICO)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando cada situacao foi alcancada (chave = situacao em minusculas, valor = ISO 8601). Ausente em contratos anteriores ao campo."
    },
    {
      "nome": "parcelas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Serie de parcelas projetada — ver a ressalva no campo `vencimento`.",
      "campos": [
        {
          "nome": "numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Numero da parcela, a partir de 1."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia de vencimento (Mes/AAAA). ATENCAO: hoje esta serie e' projetada a partir de uma competencia base fixa e limitada as 24 primeiras parcelas — nao use como calendario de cobranca."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor da parcela em reais."
        },
        {
          "nome": "situacao",
          "formato": "ENUM (Paga | A vencer | Em aberto)",
          "obrigatorio": true,
          "descricao": "Paga = ja descontada; Em aberto = a proxima; A vencer = futuras."
        }
      ]
    },
    {
      "nome": "resultados_desconto",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Resultados resolvidos por competencia, com o valor efetivamente descontado.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia AAAAMM do resultado."
        },
        {
          "nome": "resultado",
          "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
          "obrigatorio": true,
          "descricao": "Situacao apurada para o contrato nesta competencia."
        },
        {
          "nome": "valor_instruido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor enviado no movimento, em reais."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor efetivamente descontado pela folha, em reais."
        },
        {
          "nome": "parcial",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Verdadeiro quando o desconto foi menor que o valor instruido."
        },
        {
          "nome": "residuo",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Diferenca ainda nao descontada."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo publicado pela folha, quando informado."
        }
      ]
    },
    {
      "nome": "eventos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Historico de transicoes, do mais antigo ao mais recente.",
      "campos": [
        {
          "nome": "id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sequencial do evento dentro do contrato."
        },
        {
          "nome": "evento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da transicao aplicada (criado, aprovado, averbado, cancelado...)."
        },
        {
          "nome": "de_estado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao antes da transicao. `null` no evento de criacao."
        },
        {
          "nome": "para_estado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao depois da transicao."
        },
        {
          "nome": "ator",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quem executou. Chamada pela API externa aparece como `token:<id>`."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Justificativa informada na acao, quando houve."
        },
        {
          "nome": "trace_id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Correlaciona o evento com a requisicao que o gerou — use ao abrir chamado."
        },
        {
          "nome": "criado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data/hora do evento em ISO 8601."
        }
      ]
    }
  ],
  "POST /v1/external/banco/contratos/averbar": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato recem-criado. Guarde: e' a chave de todas as operacoes seguintes."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao inicial. Depende da prefeitura e de `reserva`: pode nascer aguardando confirmacao do servidor."
    },
    {
      "nome": "expiracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Prazo da reserva (DD/MM/AAAA). `null` quando nao e' reserva."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela calculada pela averbadora, em reais — confira contra a sua simulacao."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal em fracao. Emprestimo: o CET informado pelo banco; cartao: calculado pela averbadora (inclui IOF)."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/acao": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contrato afetado."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao DEPOIS da acao. Acao invalida para a situacao atual responde 409, nao 200."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/editar": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contrato editado."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao depois da contra-proposta — a proposta segue em analise."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Novo valor financiado, em reais. O corpo da requisicao chama este campo de `valor`; a resposta usa `valor_financiado`, o mesmo nome da averbacao."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Novo prazo, em meses."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Novo CET mensal em fracao (cartao: juros do rotativo)."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Nova parcela recalculada pela averbadora, em reais."
    }
  ],
  "GET /v1/external/banco/tabelas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da tabela."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a tabela pertence."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal desta faixa de prazo, em FRACAO (0.0179 = 1,79% a.m.). E' o CET maximo que o banco pratica no prazo, e nao pode passar do teto de CET do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome comercial do produto. `null` em tabela criada antes de 20/08/2026."
    },
    {
      "nome": "prazo_min_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Primeira parcela coberta por esta tabela. Tabela anterior a 20/08/2026 devolve 1."
    },
    {
      "nome": "prazo_max_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Ultima parcela coberta por esta tabela — nunca acima do teto do convenio."
    },
    {
      "nome": "vigencia_inicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia (AAAA-MM-DD)."
    },
    {
      "nome": "vigencia_fim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia (AAAA-MM-DD). `null` quando indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Tabela disponivel para novas propostas."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    }
  ],
  "POST /v1/external/banco/tabelas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da tabela."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a tabela pertence."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal desta faixa de prazo, em FRACAO (0.0179 = 1,79% a.m.). E' o CET maximo que o banco pratica no prazo, e nao pode passar do teto de CET do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome comercial do produto. `null` em tabela criada antes de 20/08/2026."
    },
    {
      "nome": "prazo_min_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Primeira parcela coberta por esta tabela. Tabela anterior a 20/08/2026 devolve 1."
    },
    {
      "nome": "prazo_max_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Ultima parcela coberta por esta tabela — nunca acima do teto do convenio."
    },
    {
      "nome": "vigencia_inicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia (AAAA-MM-DD)."
    },
    {
      "nome": "vigencia_fim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia (AAAA-MM-DD). `null` quando indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Tabela disponivel para novas propostas."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    }
  ],
  "GET /v1/external/banco/tabelas/{id}": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da tabela."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a tabela pertence."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal desta faixa de prazo, em FRACAO (0.0179 = 1,79% a.m.). E' o CET maximo que o banco pratica no prazo, e nao pode passar do teto de CET do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome comercial do produto. `null` em tabela criada antes de 20/08/2026."
    },
    {
      "nome": "prazo_min_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Primeira parcela coberta por esta tabela. Tabela anterior a 20/08/2026 devolve 1."
    },
    {
      "nome": "prazo_max_meses",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Ultima parcela coberta por esta tabela — nunca acima do teto do convenio."
    },
    {
      "nome": "vigencia_inicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia (AAAA-MM-DD)."
    },
    {
      "nome": "vigencia_fim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia (AAAA-MM-DD). `null` quando indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Tabela disponivel para novas propostas."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    }
  ],
  "GET /v1/external/banco/me": [
    {
      "nome": "camada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Camada do token. Um token so acessa `/v1/external/<camada>/*` correspondente."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador do banco dono do token. Todo dado da camada e' filtrado por ele."
    },
    {
      "nome": "environment",
      "formato": "ENUM (production | sandbox)",
      "obrigatorio": true,
      "descricao": "Ambiente do token. Em `sandbox` nada afeta folha real; em `production` as escritas valem."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos concedidos. Endpoint fora deles responde 403 dizendo qual falta. Token legado com `banco:read`/`banco:write` aparece ja expandido nos granulares."
    }
  ],
  "GET /v1/external/banco/servidores": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor — identidade da operacao."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome completo."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD). A API externa nunca devolve CPF completo."
    },
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional conforme a folha da prefeitura."
    },
    {
      "nome": "situacao_funcional",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao do servidor na folha."
    }
  ],
  "GET /v1/external/banco/falhas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contrato que falhou em folha."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual do contrato — contem 'Falha em folha'."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado."
    },
    {
      "nome": "motivo_falha",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo informado pela prefeitura ao reprovar o desconto. `null` quando ela nao detalhou."
    }
  ],
  "POST /v1/external/banco/contratos/{adf}/tratar-falha": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contrato afetado."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao DEPOIS da acao. Acao invalida para a situacao atual responde 409, nao 200."
    }
  ],
  "GET /v1/external/banco/ofertas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da oferta."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo exibido ao servidor."
    },
    {
      "nome": "mensagem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto da oferta."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal anunciado, em FRACAO (0.0179 = 1,79% a.m.). Oferta de cartao: juros do rotativo."
    },
    {
      "nome": "parcelas_max",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo maximo anunciado."
    },
    {
      "nome": "valor_max",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor maximo anunciado, em reais."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Produto ofertado: credito_novo, portabilidade, refinanciamento ou cartao_consignado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a oferta esta circulando. Pausada continua existindo, so nao aparece."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Depois desta data a oferta some. `null` = sem prazo."
    },
    {
      "nome": "icone",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Emoji exibido antes do titulo. `null` quando nao definido."
    },
    {
      "nome": "filtro",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Publico alvo. Campo vazio ou nulo significa 'nao restringe por este criterio'.",
      "campos": [
        {
          "nome": "convenio_ids",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Convenios alvo. `null` ou vazio = qualquer convenio do banco."
        },
        {
          "nome": "vinculos",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vinculos alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "situacao_funcional",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacoes funcionais alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "unidade_ids",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Unidades pagadoras alvo. `prefeitura_ids` e o nome historico e continua saindo, com os mesmos valores."
        },
        {
          "nome": "prefeitura_ids",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prefeituras alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "salario_min",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Piso de salario liquido para a oferta aparecer."
        },
        {
          "nome": "salario_max",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Teto de salario liquido."
        },
        {
          "nome": "idade_min",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade minima do publico alvo."
        },
        {
          "nome": "idade_max",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade maxima do publico alvo."
        }
      ]
    }
  ],
  "POST /v1/external/banco/ofertas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da oferta."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo exibido ao servidor."
    },
    {
      "nome": "mensagem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto da oferta."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal anunciado, em FRACAO (0.0179 = 1,79% a.m.). Oferta de cartao: juros do rotativo."
    },
    {
      "nome": "parcelas_max",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo maximo anunciado."
    },
    {
      "nome": "valor_max",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor maximo anunciado, em reais."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Produto ofertado: credito_novo, portabilidade, refinanciamento ou cartao_consignado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a oferta esta circulando. Pausada continua existindo, so nao aparece."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Depois desta data a oferta some. `null` = sem prazo."
    },
    {
      "nome": "icone",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Emoji exibido antes do titulo. `null` quando nao definido."
    },
    {
      "nome": "filtro",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Publico alvo. Campo vazio ou nulo significa 'nao restringe por este criterio'.",
      "campos": [
        {
          "nome": "convenio_ids",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Convenios alvo. `null` ou vazio = qualquer convenio do banco."
        },
        {
          "nome": "vinculos",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vinculos alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "situacao_funcional",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacoes funcionais alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "unidade_ids",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Unidades pagadoras alvo. `prefeitura_ids` e o nome historico e continua saindo, com os mesmos valores."
        },
        {
          "nome": "prefeitura_ids",
          "formato": "LISTA de NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prefeituras alvo. `null` ou vazio = qualquer."
        },
        {
          "nome": "salario_min",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Piso de salario liquido para a oferta aparecer."
        },
        {
          "nome": "salario_max",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Teto de salario liquido."
        },
        {
          "nome": "idade_min",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade minima do publico alvo."
        },
        {
          "nome": "idade_max",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade maxima do publico alvo."
        }
      ]
    }
  ],
  "POST /v1/external/banco/tabelas/{id}/reativar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Recurso afetado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado DEPOIS da operacao. Estas rotas nunca apagam — desativam e reativam."
    }
  ],
  "PATCH /v1/external/banco/ofertas/{id}/pausar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Recurso afetado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado DEPOIS da operacao. Estas rotas nunca apagam — desativam e reativam."
    }
  ],
  "PATCH /v1/external/banco/ofertas/{id}/reativar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Recurso afetado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado DEPOIS da operacao. Estas rotas nunca apagam — desativam e reativam."
    }
  ],
  "GET /v1/external/banco/visao-geral": [
    {
      "nome": "carteira",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos em situacao 'Ativo' — a carteira viva do banco."
    },
    {
      "nome": "pendencias",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos aguardando alguma acao (situacao comeca com 'Aguardando')."
    },
    {
      "nome": "propostas",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Funil da proposta, nos mesmos baldes do painel do portal.",
      "campos": [
        {
          "nome": "em_analise",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Aguardando decisao do banco."
        },
        {
          "nome": "aprovadas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Aprovadas, ainda nao averbadas."
        },
        {
          "nome": "formalizadas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Averbadas — ja valem em folha."
        },
        {
          "nome": "recusadas_expiradas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Fora da carteira: recusadas, canceladas, expiradas ou estornadas."
        }
      ]
    },
    {
      "nome": "volume_por_convenio",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "So conta contrato aprovado ou averbado — proposta em analise nao entra no volume.",
      "campos": [
        {
          "nome": "nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do convenio."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Soma do valor financiado, em reais."
        }
      ]
    }
  ],
  "GET /v1/external/banco/convenios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Prefeitura conveniada."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao conveniada.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica de desconto usada na folha desta prefeitura."
    },
    {
      "nome": "data_corte",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes em que a folha fecha para novas ADFs."
    },
    {
      "nome": "dia_repasse",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes do repasse."
    },
    {
      "nome": "validadas",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Regras que a API APLICA — violar significa operacao recusada.",
      "campos": [
        {
          "nome": "max_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Teto de parcelas do convenio. `null` = a prefeitura nao definiu teto proprio; vale so o da plataforma."
        },
        {
          "nome": "max_parcelas_plataforma",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Teto duro da plataforma. Nenhum convenio passa disto."
        },
        {
          "nome": "taxa_max_am_pct",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Teto de CET em PERCENTUAL (6 = 6% a.m.) para emprestimo, portabilidade e refinanciamento — cartao nao passa por ele. Atencao: o `taxa_am` das requisicoes e' FRACAO. `null` = sem teto (o minimo de 0,5% a.m. continua valendo)."
        },
        {
          "nome": "exige_ccb",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se a prefeitura exige contrato assinado anexado antes de aprovar."
        },
        {
          "nome": "exige_senha_averbacao",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se a operacao iniciada pelo banco espera aceite do servidor."
        },
        {
          "nome": "exige_senha_proposta",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se a proposta exige senha de transacao do servidor."
        },
        {
          "nome": "prazo_trava_horas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Validade da reserva de margem, em horas."
        },
        {
          "nome": "prazo_portabilidade_du",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Validade da trava de portabilidade, em dias uteis."
        },
        {
          "nome": "vigencia_inicio",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Inicio da vigencia do convenio (AAAA-MM-DD)."
        },
        {
          "nome": "vigencia_fim",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Fim da vigencia (AAAA-MM-DD). `null` = indeterminado."
        },
        {
          "nome": "config_ativo",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Se a configuracao do convenio esta ativa. `null` = nao configurado."
        }
      ]
    },
    {
      "nome": "informativas",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Regras que a prefeitura declara mas que NENHUM ponto do sistema valida. Nao implemente contra elas como se fossem barreira.",
      "campos": [
        {
          "nome": "idade_min",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade minima declarada pela prefeitura."
        },
        {
          "nome": "idade_max",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Idade maxima declarada."
        },
        {
          "nome": "vinculos_aceitos",
          "formato": "LISTA de ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vinculos que a prefeitura declara aceitar."
        },
        {
          "nome": "max_comprometimento_pct",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Comprometimento maximo declarado. Informativo: a margem real vem da regra de margem da unidade pagadora, e este campo nao entra no calculo."
        },
        {
          "nome": "exige_banco_2fa",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se a prefeitura declara exigir 2FA do banco."
        },
        {
          "nome": "_nota",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Aviso, em texto, de que este bloco NAO e validado pela API."
        }
      ]
    },
    {
      "nome": "regras_especiais",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Texto livre com particularidades do convenio, escrito pela averbadora."
    },
    {
      "nome": "id_unico",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Formato do identificador unico de matricula — e' o que voce recebe em `id_matricula`. `null` quando a prefeitura ainda nao configurou o padrao.",
      "campos": [
        {
          "nome": "prefixo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Prefixo dos identificadores emitidos por esta prefeitura."
        },
        {
          "nome": "formato",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Formato do identificador."
        },
        {
          "nome": "exemplo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Exemplo de identificador no padrao desta prefeitura."
        }
      ]
    },
    {
      "nome": "configurado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se existe configuracao para este convenio. Quando `false`, todo campo de teto vem `null` e nada e' recusado por eles."
    }
  ],
  "GET /v1/external/banco/comunicados": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do comunicado."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto do comunicado."
    },
    {
      "nome": "link_label",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Rotulo do link, quando ha um."
    },
    {
      "nome": "link_href",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Destino do link, quando ha um."
    },
    {
      "nome": "ativo_ate",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Ate quando o comunicado vale. `null` = sem prazo."
    }
  ],
  "GET /v1/external/banco/portabilidade/elegiveis": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula encontrada numa folha onde voce tem convenio ativo."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matricula."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "contratos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Dividas do servidor em OUTROS credores. As suas ficam de fora: para elas, use refinanciamento.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que pode ser origem."
        },
        {
          "nome": "banco_credora_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Credora atual."
        },
        {
          "nome": "banco_credora_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da credora."
        },
        {
          "nome": "convenio_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Convenio do contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela atual, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "elegivel",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se pode abrir portabilidade agora."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Por que nao pode, quando `elegivel` e' false."
        }
      ]
    }
  ],
  "GET /v1/external/banco/portabilidade": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "GET /v1/external/banco/portabilidade/{id}": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/proposta": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/saldo": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/decisao": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/pagamento": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/liquidar": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/portabilidade/{id}/cancelar": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "GET /v1/external/banco/relatorios/consignacoes": [
    {
      "nome": "linhas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Uma linha por contrato, INCLUSIVE os fora da carteira.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato."
        },
        {
          "nome": "matricula",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Matricula do servidor."
        },
        {
          "nome": "nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do servidor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Situacao do contrato."
        },
        {
          "nome": "convenio_id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Convenio."
        },
        {
          "nome": "tipo_contrato",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Natureza da operacao."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor financiado, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo."
        },
        {
          "nome": "data_contrato",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data do contrato (DD/MM/AAAA)."
        }
      ]
    },
    {
      "nome": "total_valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma do valor financiado. Exclui cancelados, recusados e expirados — por isso pode nao bater com a soma de `linhas`."
    },
    {
      "nome": "quantidade",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos contados no total — tambem exclui os fora da carteira."
    }
  ],
  "GET /v1/external/banco/relatorios/faturamento": [
    {
      "nome": "meses",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Uma linha por competencia, em ordem crescente.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia AAAA-MM, derivada da data do contrato mais um mes de carencia. `sem_data` quando o contrato nao tem data."
        },
        {
          "nome": "contratos",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Contratos na competencia."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Soma financiada na competencia, em reais."
        },
        {
          "nome": "comissao_estimada",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Estimativa de comissao: valor financiado x percentual do convenio. Convenio sem percentual configurado usa 2%."
        }
      ]
    }
  ],
  "GET /v1/external/banco/webhooks": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do webhook."
    },
    {
      "nome": "url",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Destino das entregas."
    },
    {
      "nome": "events",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Eventos assinados — ver o anexo de eventos de webhook."
    },
    {
      "nome": "active",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se esta recebendo entregas."
    },
    {
      "nome": "secret_prefix",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Primeiros caracteres do secret, para voce identificar qual e'. O secret completo so aparece na criacao."
    },
    {
      "nome": "created_at",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao em ISO 8601."
    }
  ],
  "POST /v1/external/banco/webhooks": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do webhook criado."
    },
    {
      "nome": "url",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Destino cadastrado."
    },
    {
      "nome": "events",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Eventos assinados."
    },
    {
      "nome": "secret",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Secret de assinatura, em texto puro. Aparece UMA vez — guarde agora. E' com ele que voce valida o header `X-Atlas-Signature`."
    },
    {
      "nome": "warning",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Aviso de que o secret nao sera exibido de novo."
    }
  ],
  "PATCH /v1/external/banco/webhooks/{id}/toggle": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Webhook afetado."
    },
    {
      "nome": "active",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado depois da alternancia."
    }
  ],
  "GET /v1/external/banco/webhooks/{id}/deliveries": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da entrega."
    },
    {
      "nome": "event",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Evento entregue."
    },
    {
      "nome": "status",
      "formato": "ENUM (pending | success | failed)",
      "obrigatorio": true,
      "descricao": "Situacao da entrega."
    },
    {
      "nome": "http_status",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Codigo HTTP que o seu endpoint devolveu. `null` enquanto pendente."
    },
    {
      "nome": "attempt",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Numero da tentativa."
    },
    {
      "nome": "scheduled_at",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quando foi agendada (ISO 8601)."
    },
    {
      "nome": "delivered_at",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando foi entregue. `null` se ainda nao houve entrega."
    },
    {
      "nome": "error",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Erro da tentativa, quando falhou."
    },
    {
      "nome": "payload_preview",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Trecho do corpo enviado, para conferencia."
    }
  ],
  "POST /v1/external/banco/ccb/upload": [
    {
      "nome": "key",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Chave do arquivo armazenado. Use-a apenas como referencia — o download e' feito pelo portal."
    },
    {
      "nome": "size",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Tamanho em bytes."
    },
    {
      "nome": "content_type",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Tipo MIME identificado. Aceitos: PDF, DOCX, XLS e XLSX, ate 15 MB."
    }
  ],
  "GET /v1/external/banco/refinanciamentos": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do refinanciamento."
    },
    {
      "nome": "estado",
      "formato": "ENUM (solicitado | ofertado | aceito | efetivado | recusado | cancelado | expirado)",
      "obrigatorio": true,
      "descricao": "Ver o anexo de estados de refinanciamento."
    },
    {
      "nome": "iniciado_por",
      "formato": "ENUM (servidor | banco)",
      "obrigatorio": true,
      "descricao": "Quem abriu a negociação."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio das origens e do novo contrato."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matrícula."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "pedido",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que o servidor pediu. `null` quando a negociação nasceu de oferta do banco.",
      "campos": [
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcela desejada pelo servidor, em reais."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor desejado pelo servidor, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo desejado, em meses."
        }
      ]
    },
    {
      "nome": "proposta",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condições na mesa. `null` enquanto o pedido espera resposta.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do novo contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do novo contrato."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do novo contrato, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal em FRAÇÃO."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRAÇÃO."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "troco",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor liberado ao servidor após quitar as origens."
        }
      ]
    },
    {
      "nome": "origens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Contratos que o refinanciamento quita.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato de origem, quitado pelo refinanciamento."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora."
        },
        {
          "nome": "saldo_quitacao",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo de quitação informado pelo banco. `null` = vale o saldo devedor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situação atual do contrato de origem."
        }
      ]
    },
    {
      "nome": "parcelas_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas de origem."
    },
    {
      "nome": "diferenca_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Parcela nova menos as de origem: o que a margem precisa ter livre."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade do pedido ou da oferta aberta (ISO 8601)."
    },
    {
      "nome": "adf_sucessor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato REFIN criado no aceite. Siga-o em `GET /contratos/{adf}` para averbar."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de recusa, cancelamento ou expiração."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encerramento (ISO 8601)."
    }
  ],
  "GET /v1/external/banco/refinanciamentos/{id}": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do refinanciamento."
    },
    {
      "nome": "estado",
      "formato": "ENUM (solicitado | ofertado | aceito | efetivado | recusado | cancelado | expirado)",
      "obrigatorio": true,
      "descricao": "Ver o anexo de estados de refinanciamento."
    },
    {
      "nome": "iniciado_por",
      "formato": "ENUM (servidor | banco)",
      "obrigatorio": true,
      "descricao": "Quem abriu a negociação."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio das origens e do novo contrato."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matrícula."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "pedido",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que o servidor pediu. `null` quando a negociação nasceu de oferta do banco.",
      "campos": [
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcela desejada pelo servidor, em reais."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor desejado pelo servidor, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo desejado, em meses."
        }
      ]
    },
    {
      "nome": "proposta",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condições na mesa. `null` enquanto o pedido espera resposta.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do novo contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do novo contrato."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do novo contrato, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal em FRAÇÃO."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRAÇÃO."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "troco",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor liberado ao servidor após quitar as origens."
        }
      ]
    },
    {
      "nome": "origens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Contratos que o refinanciamento quita.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato de origem, quitado pelo refinanciamento."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora."
        },
        {
          "nome": "saldo_quitacao",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo de quitação informado pelo banco. `null` = vale o saldo devedor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situação atual do contrato de origem."
        }
      ]
    },
    {
      "nome": "parcelas_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas de origem."
    },
    {
      "nome": "diferenca_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Parcela nova menos as de origem: o que a margem precisa ter livre."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade do pedido ou da oferta aberta (ISO 8601)."
    },
    {
      "nome": "adf_sucessor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato REFIN criado no aceite. Siga-o em `GET /contratos/{adf}` para averbar."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de recusa, cancelamento ou expiração."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encerramento (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/refinanciamentos": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do refinanciamento."
    },
    {
      "nome": "estado",
      "formato": "ENUM (solicitado | ofertado | aceito | efetivado | recusado | cancelado | expirado)",
      "obrigatorio": true,
      "descricao": "Ver o anexo de estados de refinanciamento."
    },
    {
      "nome": "iniciado_por",
      "formato": "ENUM (servidor | banco)",
      "obrigatorio": true,
      "descricao": "Quem abriu a negociação."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio das origens e do novo contrato."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matrícula."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "pedido",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que o servidor pediu. `null` quando a negociação nasceu de oferta do banco.",
      "campos": [
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcela desejada pelo servidor, em reais."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor desejado pelo servidor, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo desejado, em meses."
        }
      ]
    },
    {
      "nome": "proposta",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condições na mesa. `null` enquanto o pedido espera resposta.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do novo contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do novo contrato."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do novo contrato, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal em FRAÇÃO."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRAÇÃO."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "troco",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor liberado ao servidor após quitar as origens."
        }
      ]
    },
    {
      "nome": "origens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Contratos que o refinanciamento quita.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato de origem, quitado pelo refinanciamento."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora."
        },
        {
          "nome": "saldo_quitacao",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo de quitação informado pelo banco. `null` = vale o saldo devedor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situação atual do contrato de origem."
        }
      ]
    },
    {
      "nome": "parcelas_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas de origem."
    },
    {
      "nome": "diferenca_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Parcela nova menos as de origem: o que a margem precisa ter livre."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade do pedido ou da oferta aberta (ISO 8601)."
    },
    {
      "nome": "adf_sucessor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato REFIN criado no aceite. Siga-o em `GET /contratos/{adf}` para averbar."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de recusa, cancelamento ou expiração."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encerramento (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/responder": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do refinanciamento."
    },
    {
      "nome": "estado",
      "formato": "ENUM (solicitado | ofertado | aceito | efetivado | recusado | cancelado | expirado)",
      "obrigatorio": true,
      "descricao": "Ver o anexo de estados de refinanciamento."
    },
    {
      "nome": "iniciado_por",
      "formato": "ENUM (servidor | banco)",
      "obrigatorio": true,
      "descricao": "Quem abriu a negociação."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio das origens e do novo contrato."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matrícula."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "pedido",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que o servidor pediu. `null` quando a negociação nasceu de oferta do banco.",
      "campos": [
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcela desejada pelo servidor, em reais."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor desejado pelo servidor, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo desejado, em meses."
        }
      ]
    },
    {
      "nome": "proposta",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condições na mesa. `null` enquanto o pedido espera resposta.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do novo contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do novo contrato."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do novo contrato, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal em FRAÇÃO."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRAÇÃO."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "troco",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor liberado ao servidor após quitar as origens."
        }
      ]
    },
    {
      "nome": "origens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Contratos que o refinanciamento quita.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato de origem, quitado pelo refinanciamento."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora."
        },
        {
          "nome": "saldo_quitacao",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo de quitação informado pelo banco. `null` = vale o saldo devedor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situação atual do contrato de origem."
        }
      ]
    },
    {
      "nome": "parcelas_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas de origem."
    },
    {
      "nome": "diferenca_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Parcela nova menos as de origem: o que a margem precisa ter livre."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade do pedido ou da oferta aberta (ISO 8601)."
    },
    {
      "nome": "adf_sucessor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato REFIN criado no aceite. Siga-o em `GET /contratos/{adf}` para averbar."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de recusa, cancelamento ou expiração."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encerramento (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/cancelar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do refinanciamento."
    },
    {
      "nome": "estado",
      "formato": "ENUM (solicitado | ofertado | aceito | efetivado | recusado | cancelado | expirado)",
      "obrigatorio": true,
      "descricao": "Ver o anexo de estados de refinanciamento."
    },
    {
      "nome": "iniciado_por",
      "formato": "ENUM (servidor | banco)",
      "obrigatorio": true,
      "descricao": "Quem abriu a negociação."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convênio das origens e do novo contrato."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Unidade pagadora da matrícula."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matrícula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "pedido",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que o servidor pediu. `null` quando a negociação nasceu de oferta do banco.",
      "campos": [
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcela desejada pelo servidor, em reais."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor desejado pelo servidor, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo desejado, em meses."
        }
      ]
    },
    {
      "nome": "proposta",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condições na mesa. `null` enquanto o pedido espera resposta.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do novo contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do novo contrato."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do novo contrato, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal em FRAÇÃO."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRAÇÃO."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "troco",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor liberado ao servidor após quitar as origens."
        }
      ]
    },
    {
      "nome": "origens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Contratos que o refinanciamento quita.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato de origem, quitado pelo refinanciamento."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora."
        },
        {
          "nome": "saldo_quitacao",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo de quitação informado pelo banco. `null` = vale o saldo devedor."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situação atual do contrato de origem."
        }
      ]
    },
    {
      "nome": "parcelas_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas de origem."
    },
    {
      "nome": "diferenca_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Parcela nova menos as de origem: o que a margem precisa ter livre."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade do pedido ou da oferta aberta (ISO 8601)."
    },
    {
      "nome": "adf_sucessor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato REFIN criado no aceite. Siga-o em `GET /contratos/{adf}` para averbar."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de recusa, cancelamento ou expiração."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encerramento (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/bate-carteira": [
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco consultado — sempre o dono do token."
    },
    {
      "nome": "banco_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia consultada, em AAAAMM."
    },
    {
      "nome": "total_linhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos da carteira na competencia — uma linha por contrato."
    },
    {
      "nome": "soma_saldo_devedor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma dos saldos devedores, em reais."
    },
    {
      "nome": "soma_valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma do valor instruido na competencia, em reais."
    },
    {
      "nome": "linhas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Uma linha por contrato da carteira: o contrato ativo na competencia, ou com resultado da folha nela.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia da linha, em AAAAMM."
        },
        {
          "nome": "banco_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Banco da operacao."
        },
        {
          "nome": "banco_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do banco."
        },
        {
          "nome": "unidade_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prefeitura da matricula."
        },
        {
          "nome": "unidade_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da prefeitura."
        },
        {
          "nome": "organizacao_tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "organizacao_sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
        },
        {
          "nome": "cpf_masked",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "CPF mascarado (LGPD)."
        },
        {
          "nome": "matricula",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Matricula do servidor."
        },
        {
          "nome": "id_unico",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador unico no padrao da prefeitura."
        },
        {
          "nome": "adf_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato no seu sistema, quando informado."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela em reais."
        },
        {
          "nome": "parcelas_restantes",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas que faltam, quando conhecido."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo devedor, quando conhecido."
        },
        {
          "nome": "origem",
          "formato": "ENUM (tombamento | pre_reserva_confirmada)",
          "obrigatorio": true,
          "descricao": "De onde o contrato veio: `tombamento` e' contrato legado, informado na carga da folha; `pre_reserva_confirmada` e' operacao averbada na Atlas."
        },
        {
          "nome": "status",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Conciliacao da linha: `conciliado`, `divergente`, `nao_descontado`, `nao_encontrado_na_folha` ou `aguardando_folha`. Para o que a folha fez, leia `status_folha`."
        },
        {
          "nome": "data",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data do resultado da folha; sem resultado, a data do contrato (AAAA-MM-DD)."
        },
        {
          "nome": "status_folha",
          "formato": "ENUM (aplicada | parcial | nao_descontada | suspensa | aguardando_retorno)",
          "obrigatorio": true,
          "descricao": "O que a folha fez com o contrato na competencia: `aplicada` (descontou), `parcial` (descontou menos que o instruido), `nao_descontada` (falha ou desligamento), `suspensa` (sem folha no mes ou teto do orgao) ou `aguardando_retorno` (o retorno ainda nao chegou)."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha descontou, em reais. `null` enquanto o retorno nao chega."
        },
        {
          "nome": "residuo",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que faltou descontar da parcela instruida, em reais. `null` enquanto o retorno nao chega."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo informado pela folha, quando houver."
        }
      ]
    },
    {
      "nome": "gerado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quando o confronto foi gerado (ISO 8601)."
    }
  ],
  "POST /v1/external/banco/cartoes/reservas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "ADF da reserva. E' por ela que os lancamentos saem no movimento."
    },
    {
      "nome": "produto",
      "formato": "ENUM (CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Cartao de credito consignado ou cartao beneficio."
    },
    {
      "nome": "valor_reservado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Margem do cartao comprometida pela reserva, em reais: o disponivel inteiro do balde no ato da reserva."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela contratada, em reais: o teto de cada lancamento. Reserva anterior a 14/10/2026 traz o mesmo valor de `valor_reservado`."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero do contrato no sistema do banco."
    },
    {
      "nome": "parcelas_total",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo da reserva, em parcelas. 0 = sem prazo."
    },
    {
      "nome": "parcelas_lancadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parcelas ja' lancadas."
    },
    {
      "nome": "parcela_vigente",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "A proxima parcela a lancar. `null` quando todas ja' foram lancadas."
    },
    {
      "nome": "parcelas_faltam",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quantas faltam lancar. `null` na reserva sem prazo."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao da reserva. So' reserva ativa aceita lancamento."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio sob o qual a reserva foi feita."
    },
    {
      "nome": "competencia_inicial",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Primeira competencia que aceita lancamento, AAAAMM."
    },
    {
      "nome": "lancamentos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Os lancamentos da reserva, da competencia mais antiga para a mais nova.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia do desconto, AAAAMM."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor a descontar nesta competencia, em reais. Nunca passa do valor reservado."
        },
        {
          "nome": "estado",
          "formato": "ENUM (informado | cancelado)",
          "obrigatorio": true,
          "descricao": "`informado` vai no movimento da competencia; `cancelado` nao vai."
        },
        {
          "nome": "congelado",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` quando o corte da competencia passou ou a folha fechou: o lancamento nao muda mais."
        },
        {
          "nome": "versao",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sobe a cada reenvio da mesma competencia."
        },
        {
          "nome": "atualizado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Instante da ultima alteracao (ISO 8601)."
        },
        {
          "nome": "resultado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha fez com o lancamento (`aplicada`, `parcial`, `falha`...). `null` enquanto o retorno nao chegou."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Quanto a folha efetivamente descontou. `null` sem retorno."
        },
        {
          "nome": "parcela_numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Que parcela da reserva este lançamento é. Conta só os lançamentos informados: mês sem lançamento não gasta parcela. `null` no cancelado."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Número do contrato no sistema do banco."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vencimento, AAAA-MM-DD."
        },
        {
          "nome": "data_liberacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data de liberação do crédito, AAAA-MM-DD."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor total financiado, em reais."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor líquido liberado, em reais."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "juros_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros a.m. em fração."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "CET a.m. em fração."
        },
        {
          "nome": "dias_carencia",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Dias de carência."
        },
        {
          "nome": "valor_carencia",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor da carência, em reais."
        },
        {
          "nome": "observacoes",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Observações do lançamento."
        }
      ]
    }
  ],
  "GET /v1/external/banco/cartoes/reservas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "ADF da reserva. E' por ela que os lancamentos saem no movimento."
    },
    {
      "nome": "produto",
      "formato": "ENUM (CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Cartao de credito consignado ou cartao beneficio."
    },
    {
      "nome": "valor_reservado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Margem do cartao comprometida pela reserva, em reais: o disponivel inteiro do balde no ato da reserva."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela contratada, em reais: o teto de cada lancamento. Reserva anterior a 14/10/2026 traz o mesmo valor de `valor_reservado`."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero do contrato no sistema do banco."
    },
    {
      "nome": "parcelas_total",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo da reserva, em parcelas. 0 = sem prazo."
    },
    {
      "nome": "parcelas_lancadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parcelas ja' lancadas."
    },
    {
      "nome": "parcela_vigente",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "A proxima parcela a lancar. `null` quando todas ja' foram lancadas."
    },
    {
      "nome": "parcelas_faltam",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quantas faltam lancar. `null` na reserva sem prazo."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao da reserva. So' reserva ativa aceita lancamento."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio sob o qual a reserva foi feita."
    },
    {
      "nome": "competencia_inicial",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Primeira competencia que aceita lancamento, AAAAMM."
    },
    {
      "nome": "lancamentos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Os lancamentos da reserva, da competencia mais antiga para a mais nova.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia do desconto, AAAAMM."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor a descontar nesta competencia, em reais. Nunca passa do valor reservado."
        },
        {
          "nome": "estado",
          "formato": "ENUM (informado | cancelado)",
          "obrigatorio": true,
          "descricao": "`informado` vai no movimento da competencia; `cancelado` nao vai."
        },
        {
          "nome": "congelado",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` quando o corte da competencia passou ou a folha fechou: o lancamento nao muda mais."
        },
        {
          "nome": "versao",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sobe a cada reenvio da mesma competencia."
        },
        {
          "nome": "atualizado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Instante da ultima alteracao (ISO 8601)."
        },
        {
          "nome": "resultado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha fez com o lancamento (`aplicada`, `parcial`, `falha`...). `null` enquanto o retorno nao chegou."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Quanto a folha efetivamente descontou. `null` sem retorno."
        },
        {
          "nome": "parcela_numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Que parcela da reserva este lançamento é. Conta só os lançamentos informados: mês sem lançamento não gasta parcela. `null` no cancelado."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Número do contrato no sistema do banco."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vencimento, AAAA-MM-DD."
        },
        {
          "nome": "data_liberacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data de liberação do crédito, AAAA-MM-DD."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor total financiado, em reais."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor líquido liberado, em reais."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "juros_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros a.m. em fração."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "CET a.m. em fração."
        },
        {
          "nome": "dias_carencia",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Dias de carência."
        },
        {
          "nome": "valor_carencia",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor da carência, em reais."
        },
        {
          "nome": "observacoes",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Observações do lançamento."
        }
      ]
    }
  ],
  "GET /v1/external/banco/cartoes/reservas/{adf}": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "ADF da reserva. E' por ela que os lancamentos saem no movimento."
    },
    {
      "nome": "produto",
      "formato": "ENUM (CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Cartao de credito consignado ou cartao beneficio."
    },
    {
      "nome": "valor_reservado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Margem do cartao comprometida pela reserva, em reais: o disponivel inteiro do balde no ato da reserva."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela contratada, em reais: o teto de cada lancamento. Reserva anterior a 14/10/2026 traz o mesmo valor de `valor_reservado`."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero do contrato no sistema do banco."
    },
    {
      "nome": "parcelas_total",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo da reserva, em parcelas. 0 = sem prazo."
    },
    {
      "nome": "parcelas_lancadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parcelas ja' lancadas."
    },
    {
      "nome": "parcela_vigente",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "A proxima parcela a lancar. `null` quando todas ja' foram lancadas."
    },
    {
      "nome": "parcelas_faltam",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quantas faltam lancar. `null` na reserva sem prazo."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao da reserva. So' reserva ativa aceita lancamento."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio sob o qual a reserva foi feita."
    },
    {
      "nome": "competencia_inicial",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Primeira competencia que aceita lancamento, AAAAMM."
    },
    {
      "nome": "lancamentos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Os lancamentos da reserva, da competencia mais antiga para a mais nova.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia do desconto, AAAAMM."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor a descontar nesta competencia, em reais. Nunca passa do valor reservado."
        },
        {
          "nome": "estado",
          "formato": "ENUM (informado | cancelado)",
          "obrigatorio": true,
          "descricao": "`informado` vai no movimento da competencia; `cancelado` nao vai."
        },
        {
          "nome": "congelado",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` quando o corte da competencia passou ou a folha fechou: o lancamento nao muda mais."
        },
        {
          "nome": "versao",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sobe a cada reenvio da mesma competencia."
        },
        {
          "nome": "atualizado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Instante da ultima alteracao (ISO 8601)."
        },
        {
          "nome": "resultado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha fez com o lancamento (`aplicada`, `parcial`, `falha`...). `null` enquanto o retorno nao chegou."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Quanto a folha efetivamente descontou. `null` sem retorno."
        },
        {
          "nome": "parcela_numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Que parcela da reserva este lançamento é. Conta só os lançamentos informados: mês sem lançamento não gasta parcela. `null` no cancelado."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Número do contrato no sistema do banco."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vencimento, AAAA-MM-DD."
        },
        {
          "nome": "data_liberacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data de liberação do crédito, AAAA-MM-DD."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor total financiado, em reais."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor líquido liberado, em reais."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "juros_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros a.m. em fração."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "CET a.m. em fração."
        },
        {
          "nome": "dias_carencia",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Dias de carência."
        },
        {
          "nome": "valor_carencia",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor da carência, em reais."
        },
        {
          "nome": "observacoes",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Observações do lançamento."
        }
      ]
    }
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "ADF da reserva."
    },
    {
      "nome": "acao",
      "formato": "ENUM (criar | substituir | reativar | manter)",
      "obrigatorio": true,
      "descricao": "O que a chamada fez. `manter` = o mesmo valor ja' estava informado, nada mudou (reenvio idempotente)."
    },
    {
      "nome": "lancamento",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O lancamento como ficou gravado.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia do desconto, AAAAMM."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor a descontar nesta competencia, em reais. Nunca passa do valor reservado."
        },
        {
          "nome": "estado",
          "formato": "ENUM (informado | cancelado)",
          "obrigatorio": true,
          "descricao": "`informado` vai no movimento da competencia; `cancelado` nao vai."
        },
        {
          "nome": "congelado",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` quando o corte da competencia passou ou a folha fechou: o lancamento nao muda mais."
        },
        {
          "nome": "versao",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sobe a cada reenvio da mesma competencia."
        },
        {
          "nome": "atualizado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Instante da ultima alteracao (ISO 8601)."
        },
        {
          "nome": "resultado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha fez com o lancamento (`aplicada`, `parcial`, `falha`...). `null` enquanto o retorno nao chegou."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Quanto a folha efetivamente descontou. `null` sem retorno."
        },
        {
          "nome": "parcela_numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Que parcela da reserva este lançamento é. Conta só os lançamentos informados: mês sem lançamento não gasta parcela. `null` no cancelado."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Número do contrato no sistema do banco."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Vencimento, AAAA-MM-DD."
        },
        {
          "nome": "data_liberacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data de liberação do crédito, AAAA-MM-DD."
        },
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor total financiado, em reais."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor líquido liberado, em reais."
        },
        {
          "nome": "iof",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IOF, em reais."
        },
        {
          "nome": "juros_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros a.m. em fração."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "CET a.m. em fração."
        },
        {
          "nome": "dias_carencia",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Dias de carência."
        },
        {
          "nome": "valor_carencia",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Valor da carência, em reais."
        },
        {
          "nome": "observacoes",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Observações do lançamento."
        }
      ]
    }
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos/cancelar": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do desconto, AAAAMM."
    },
    {
      "nome": "valor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor a descontar nesta competencia, em reais. Nunca passa do valor reservado."
    },
    {
      "nome": "estado",
      "formato": "ENUM (informado | cancelado)",
      "obrigatorio": true,
      "descricao": "`informado` vai no movimento da competencia; `cancelado` nao vai."
    },
    {
      "nome": "congelado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "`true` quando o corte da competencia passou ou a folha fechou: o lancamento nao muda mais."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Sobe a cada reenvio da mesma competencia."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Instante da ultima alteracao (ISO 8601)."
    },
    {
      "nome": "resultado",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "O que a folha fez com o lancamento (`aplicada`, `parcial`, `falha`...). `null` enquanto o retorno nao chegou."
    },
    {
      "nome": "valor_descontado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quanto a folha efetivamente descontou. `null` sem retorno."
    },
    {
      "nome": "parcela_numero",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Que parcela da reserva este lançamento é. Conta só os lançamentos informados: mês sem lançamento não gasta parcela. `null` no cancelado."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Número do contrato no sistema do banco."
    },
    {
      "nome": "vencimento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Vencimento, AAAA-MM-DD."
    },
    {
      "nome": "data_liberacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data de liberação do crédito, AAAA-MM-DD."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor total financiado, em reais."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor líquido liberado, em reais."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "IOF, em reais."
    },
    {
      "nome": "juros_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Juros a.m. em fração."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "CET a.m. em fração."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Dias de carência."
    },
    {
      "nome": "valor_carencia",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor da carência, em reais."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Observações do lançamento."
    }
  ],
  "POST /v1/external/banco/cartoes/lancamentos": [
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do lote, AAAAMM."
    },
    {
      "nome": "gravados",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Itens gravados ou mantidos."
    },
    {
      "nome": "recusados",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Itens recusados — os demais do lote continuam valendo."
    },
    {
      "nome": "itens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Um resultado por item do lote, na ordem enviada.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "ADF da reserva do item."
        },
        {
          "nome": "ok",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` quando o lancamento foi gravado (ou ja' estava igual)."
        },
        {
          "nome": "acao",
          "formato": "ENUM (criar | substituir | reativar | manter)",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que foi feito. `null` quando recusado."
        },
        {
          "nome": "erro",
          "formato": "OBJETO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo da recusa. `null` quando ok.",
          "campos": [
            {
              "nome": "code",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": ""
            },
            {
              "nome": "message",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": ""
            }
          ]
        }
      ]
    }
  ],
  "GET /v1/external/prefeitura/me": [
    {
      "nome": "camada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Camada do token."
    },
    {
      "nome": "environment",
      "formato": "ENUM (production | sandbox)",
      "obrigatorio": true,
      "descricao": "Ambiente. Em `sandbox` nada afeta folha real; em `production` confirmar ADF muda desconto de verdade."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos concedidos ao token."
    },
    {
      "nome": "data",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Identificacao da prefeitura que o token representa.",
      "campos": [
        {
          "nome": "unidade_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prefeitura que este token representa. Todo dado da camada e' filtrado por ela."
        },
        {
          "nome": "nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da prefeitura."
        },
        {
          "nome": "convenios",
          "formato": "LISTA de OBJETO",
          "obrigatorio": true,
          "descricao": "Convenios ativos desta prefeitura.",
          "campos": [
            {
              "nome": "id",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "Identificador do convenio."
            },
            {
              "nome": "nome",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "Nome do convenio."
            }
          ]
        }
      ]
    }
  ],
  "GET /v1/external/prefeitura/servidores": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula na prefeitura — identidade do vinculo. Um CPF pode ter varias."
    },
    {
      "nome": "id_matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador unico no padrao desta prefeitura."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome completo."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD). O CPF completo nunca sai da API externa."
    },
    {
      "nome": "cargo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Cargo. `null` quando a base importada nao trouxe."
    },
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional (Efetivo, Comissionado, Aposentado...)."
    },
    {
      "nome": "situacao_funcional",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao na folha (Ativo, Licenca, Desligado...)."
    },
    {
      "nome": "salario_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor da coluna de salario liquido da carga, em reais. E' a base de calculo das margens, EXCETO quando `base_margem` e' `margem_da_folha`: ai' e' a margem de emprestimo que a folha mandou pronta."
    },
    {
      "nome": "base_margem",
      "formato": "ENUM (informada | liquida_legal | margem_da_folha)",
      "obrigatorio": true,
      "descricao": "Sobre o que a margem da unidade incide na regra vigente: `informada` (percentual sobre `salario_liquido`), `liquida_legal` (percentual sobre a base legal do contracheque) ou `margem_da_folha` (`salario_liquido` ja' e' a margem de emprestimo que a folha mandou; os cartoes saem dela na proporcao da regra)."
    },
    {
      "nome": "email",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "E-mail, quando informado."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Telefone, quando informado."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando este vinculo entrou na base (ISO 8601)."
    },
    {
      "nome": "data_admissao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data de admissao, quando informada."
    }
  ],
  "GET /v1/external/prefeitura/folhas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da folha."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona da folha."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da folha.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` quando nao definida."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": true,
      "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
    }
  ],
  "POST /v1/external/prefeitura/folhas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da folha."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona da folha."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da folha.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` quando nao definida."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": true,
      "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
    }
  ],
  "PATCH /v1/external/prefeitura/folhas/{id}": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da folha."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona da folha."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da folha.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` quando nao definida."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": true,
      "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
    }
  ],
  "GET /v1/external/prefeitura/folhas/{id}/resultados": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "ADF do contrato — e a chave que liga a linha da remessa a linha do retorno."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do desconto (YYYYMM)."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco credor do contrato."
    },
    {
      "nome": "resultado",
      "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
      "obrigatorio": true,
      "descricao": "O que a folha fez com o desconto. `parcial` ENCERRA a parcela do mes como `aplicada` encerra — o que muda e' `residuo`, a diferenca que a folha deixou de descontar e que o banco credor cobra por fora. O residuo nao volta para a parcela seguinte. `suspensa_sem_folha`: o servidor nao teve folha no mes. `suspensa_por_teto`: a Atlas tirou o desconto do movimento para caber no teto global da regra do orgao. Nas duas, a parcela nao avanca e nao ha residuo."
    },
    {
      "nome": "valor_instruido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor que a remessa mandou descontar, em reais."
    },
    {
      "nome": "valor_efetivo",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor que a folha de fato retirou, em reais."
    },
    {
      "nome": "residuo",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Diferenca entre instruido e efetivo — o que o banco cobra por fora."
    },
    {
      "nome": "origem",
      "formato": "ENUM (retorno | manual | presumido)",
      "obrigatorio": true,
      "descricao": "`presumido` = a competencia fechou pela data de repasse sem o retorno ter chegado."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Justificativa informada pela prefeitura (ex.: margem insuficiente)."
    },
    {
      "nome": "registrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quando o resultado foi registrado (ISO 8601)."
    }
  ],
  "GET /v1/external/prefeitura/contratos": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato na averbadora."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco do contrato."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao do contrato."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor financiado, em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela a descontar em folha, em reais."
    },
    {
      "nome": "folha_status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao da ADF deste contrato na folha. `null` enquanto nao foi enviado a nenhuma."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Criacao do contrato (ISO 8601)."
    },
    {
      "nome": "lancamento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data de lancamento (DD/MM/AAAA)."
    }
  ],
  "GET /v1/external/prefeitura/adf": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da ADF, no formato `ADF-AAAAMM-...`. E' ESTE campo que vai em `ids` ao confirmar ou reportar falha — nao o `adf`."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato na averbadora."
    },
    {
      "nome": "id_unico",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do servidor no padrao desta prefeitura."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia da folha, em AAAAMM."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco credor."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor a descontar nesta competencia, em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo total do contrato."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor financiado, em reais."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao da ADF: `recebida` (aguardando voce), `aplicada` (descontada) ou `falha` (nao entrou)."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo informado ao reportar falha."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Ultima mudanca de status (ISO 8601)."
    }
  ],
  "GET /v1/external/prefeitura/adf/competencias": [
    {
      "nome": "competencias",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Competencias com ADFs, da mais recente para a mais antiga. `total - aplicadas - falhas` e' o que ainda esta pendente de decisao sua.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia em AAAAMM."
        },
        {
          "nome": "total",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "ADFs na competencia."
        },
        {
          "nome": "aplicadas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Ja descontadas em folha."
        },
        {
          "nome": "falhas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Reportadas como nao aplicadas."
        }
      ]
    },
    {
      "nome": "competencia_atual",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia da folha aberta — o padrao de `GET /adf` sem filtro."
    }
  ],
  "POST /v1/external/prefeitura/adf/confirmar": [
    {
      "nome": "aplicadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `aplicada`."
    },
    {
      "nome": "falhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `falha`."
    },
    {
      "nome": "solicitadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantos ids vieram na requisicao."
    },
    {
      "nome": "adfs",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contratos efetivamente alterados. MENOR que `solicitadas` quando algum id nao existe, ja estava naquele estado, ou pertence a outra prefeitura — compare os dois numeros antes de dar a operacao por concluida."
    }
  ],
  "POST /v1/external/prefeitura/adf/falha": [
    {
      "nome": "aplicadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `aplicada`."
    },
    {
      "nome": "falhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `falha`."
    },
    {
      "nome": "solicitadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantos ids vieram na requisicao."
    },
    {
      "nome": "adfs",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contratos efetivamente alterados. MENOR que `solicitadas` quando algum id nao existe, ja estava naquele estado, ou pertence a outra prefeitura — compare os dois numeros antes de dar a operacao por concluida."
    }
  ],
  "GET /v1/external/prefeitura/dashboard": [
    {
      "nome": "servidores",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Total de vinculos na base."
    },
    {
      "nome": "servidores_ativos",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Vinculos que nao estao desligados nem aposentados."
    },
    {
      "nome": "contratos_averbados",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos valendo em folha."
    },
    {
      "nome": "convenios",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Convenios ativos."
    },
    {
      "nome": "bancos_atuantes",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Bancos com ao menos um contrato averbado."
    },
    {
      "nome": "folha_atual",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Folha aberta, ou a ultima existente. `null` quando nao ha folha nenhuma.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia em AAAAMM."
        },
        {
          "nome": "status",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Situacao da folha."
        },
        {
          "nome": "data_corte",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data de corte."
        },
        {
          "nome": "data_repasse",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data de repasse."
        }
      ]
    },
    {
      "nome": "pendencias",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O que esta esperando acao sua.",
      "campos": [
        {
          "nome": "folhas_abertas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Folhas ainda em aberto."
        },
        {
          "nome": "anuencia_pendente",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "1 quando nao ha anuencia vigente, 0 quando ha."
        }
      ]
    }
  ],
  "GET /v1/external/prefeitura/convenios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco conveniado."
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica de desconto usada na sua folha."
    },
    {
      "nome": "data_corte",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes em que a folha fecha para novas ADFs."
    },
    {
      "nome": "dia_repasse",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes do repasse."
    },
    {
      "nome": "max_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Teto de parcelas do convenio. `null` = nao definido; vale o teto da plataforma, 120."
    },
    {
      "nome": "taxa_max_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Teto de CET em PERCENTUAL (6 = 6% a.m.) para emprestimo, portabilidade e refinanciamento; cartao nao passa por ele. `null` = sem teto."
    },
    {
      "nome": "prazo_trava_horas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da reserva de margem, em horas. Sem configuracao, a plataforma aplica 48h."
    },
    {
      "nome": "formato_importacao",
      "formato": "ENUM (CSV | EXCEL | API)",
      "obrigatorio": true,
      "descricao": "Formato combinado para o envio da base."
    }
  ],
  "GET /v1/external/prefeitura/relatorios/servidores-por-vinculo": [
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional."
    },
    {
      "nome": "total",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantidade de servidores. Ordenado do maior para o menor."
    }
  ],
  "GET /v1/external/prefeitura/relatorios/contratos-por-banco": [
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco."
    },
    {
      "nome": "contratos",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos averbados deste banco."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma das parcelas mensais, em reais — o quanto ele representa na sua folha."
    }
  ],
  "GET /v1/external/prefeitura/anuencia": [
    {
      "nome": "versao_atual",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao vigente do termo. Se for diferente da aceita, ha anuencia nova a assinar."
    },
    {
      "nome": "vigente",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite em vigor. `null` quando nao ha nenhum.",
      "campos": [
        {
          "nome": "id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador do registro."
        },
        {
          "nome": "versao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Versao do termo aceita."
        },
        {
          "nome": "escopo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Escopo do aceite."
        },
        {
          "nome": "aceito_por",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quem aceitou."
        },
        {
          "nome": "aceito_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando (ISO 8601)."
        },
        {
          "nome": "ip",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IP de origem do aceite, quando capturado."
        }
      ]
    },
    {
      "nome": "historico",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Todos os aceites ja registrados — nunca apagados.",
      "campos": [
        {
          "nome": "id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador do registro."
        },
        {
          "nome": "versao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Versao do termo aceita."
        },
        {
          "nome": "escopo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Escopo do aceite."
        },
        {
          "nome": "aceito_por",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quem aceitou."
        },
        {
          "nome": "aceito_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando (ISO 8601)."
        },
        {
          "nome": "ip",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "IP de origem do aceite, quando capturado."
        }
      ]
    }
  ],
  "POST /v1/external/prefeitura/anuencia": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do registro."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao do termo aceita."
    },
    {
      "nome": "escopo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopo do aceite."
    },
    {
      "nome": "aceito_por",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quem aceitou."
    },
    {
      "nome": "aceito_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quando (ISO 8601)."
    },
    {
      "nome": "ip",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "IP de origem do aceite, quando capturado."
    }
  ],
  "GET /v1/external/servidor/me": [
    {
      "nome": "camada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Camada do token."
    },
    {
      "nome": "environment",
      "formato": "ENUM (production | sandbox)",
      "obrigatorio": true,
      "descricao": "Ambiente do token."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos concedidos."
    },
    {
      "nome": "data",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "Identidade do servidor que o token representa.",
      "campos": [
        {
          "nome": "matricula",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Matricula principal do token."
        },
        {
          "nome": "nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do servidor."
        },
        {
          "nome": "cpf_masked",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "CPF mascarado (LGPD)."
        },
        {
          "nome": "vinculo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Vinculo funcional."
        },
        {
          "nome": "situacao_funcional",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Situacao na folha."
        },
        {
          "nome": "organizacao",
          "formato": "OBJETO",
          "obrigatorio": false,
          "descricao": "A organizacao (orgao ou entidade) a que esta matricula pertence. `nome_exibicao` e' o nome que se mostra ao servidor.",
          "campos": [
            {
              "nome": "tipo",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
            },
            {
              "nome": "esfera",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
            },
            {
              "nome": "sigla",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "nulavel": true,
              "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
            },
            {
              "nome": "nome_exibicao",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
            }
          ]
        }
      ]
    }
  ],
  "GET /v1/external/servidor/matriculas": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula na prefeitura."
    },
    {
      "nome": "id_matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador unico no padrao da prefeitura."
    },
    {
      "nome": "origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "De onde este vinculo veio para a base."
    },
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional."
    },
    {
      "nome": "situacao_funcional",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao na folha."
    },
    {
      "nome": "salario_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor da coluna de salario liquido da carga DESTA matricula, em reais. E' a base de calculo da margem, EXCETO quando `base_margem` e' `margem_da_folha`: ai' e' a margem de emprestimo que a folha mandou pronta."
    },
    {
      "nome": "base_margem",
      "formato": "ENUM (informada | liquida_legal | margem_da_folha)",
      "obrigatorio": true,
      "descricao": "Sobre o que a margem da unidade incide na regra vigente: `informada` (percentual sobre `salario_liquido`), `liquida_legal` (percentual sobre a base legal do contracheque) ou `margem_da_folha` (`salario_liquido` ja' e' a margem de emprestimo que a folha mandou; os cartoes saem dela na proporcao da regra)."
    },
    {
      "nome": "cargo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Cargo, quando informado."
    }
  ],
  "GET /v1/external/servidor/margem": [
    {
      "nome": "salario_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor da coluna de salario liquido da carga, em reais. E' a base de calculo, EXCETO quando `base_margem` e' `margem_da_folha`: ai' e' a margem de emprestimo que a folha mandou pronta."
    },
    {
      "nome": "base_margem",
      "formato": "ENUM (informada | liquida_legal | margem_da_folha)",
      "obrigatorio": false,
      "descricao": "Sobre o que a margem da unidade incide na regra vigente: `informada` (percentual sobre `salario_liquido`), `liquida_legal` (percentual sobre a base legal do contracheque) ou `margem_da_folha` (`salario_liquido` ja' e' a margem de emprestimo que a folha mandou; os cartoes saem dela na proporcao da regra)."
    },
    {
      "nome": "comprometido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Quanto ja consome a margem de EMPRESTIMO: parcelas de propostas e contratos, mais consignados em outros bancos informados pela prefeitura no tombamento."
    },
    {
      "nome": "margens",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Um item por bucket.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ENUM (EMPRESTIMO | CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
          "obrigatorio": true,
          "descricao": "Bucket de margem: QUAL margem a operacao consome. Por padrao independentes; com saldo compartilhado, consumir um reduz os outros."
        },
        {
          "nome": "total",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Margem bruta do bucket, em reais — percentual ou saldo definido pela regra da unidade pagadora (ver `fonte`)."
        },
        {
          "nome": "disponivel",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Quanto ainda cabe neste bucket, em reais."
        },
        {
          "nome": "fonte",
          "formato": "ENUM (calculada | informada)",
          "obrigatorio": false,
          "descricao": "De onde veio a margem. `calculada`: percentual da regra da unidade pagadora sobre o salario liquido. `informada`: valor mandado pela folha no arquivo, que substitui o calculo."
        },
        {
          "nome": "percentual",
          "formato": "NUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Percentual da regra aplicado neste bucket, em FRACAO (0.4 = 40%). `null` quando a margem e' informada pela folha."
        },
        {
          "nome": "arquivo_competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Competencia (AAAAMM) do arquivo da folha que informou a margem. `null` quando calculada."
        },
        {
          "nome": "arquivo_importado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Quando esse arquivo foi importado, ISO 8601. `null` quando calculada."
        }
      ]
    }
  ],
  "GET /v1/external/servidor/ofertas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da tabela de emprestimo que originou a oferta."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco ofertante."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Taxa mensal em FRACAO."
    },
    {
      "nome": "parcelas_max",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo maximo desta tabela."
    },
    {
      "nome": "parcela_max_estimada",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela estimada usando 90% da margem disponivel, em reais."
    },
    {
      "nome": "valor_aprovado_estimado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "ESTIMATIVA de quanto seria aprovado com essa parcela e esse prazo. Nao e' proposta nem reserva — o valor real sai da simulacao e da analise do banco."
    }
  ],
  "GET /v1/external/servidor/ofertas-banco": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da oferta."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco que publicou."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo da campanha."
    },
    {
      "nome": "mensagem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto da oferta."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Produto: credito_novo, portabilidade, refinanciamento ou cartao_consignado."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal anunciado, em FRACAO."
    },
    {
      "nome": "parcelas_max",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo maximo anunciado."
    },
    {
      "nome": "valor_max",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor maximo anunciado, em reais."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Publicacao (ISO 8601)."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da campanha. `null` = sem prazo."
    }
  ],
  "POST /v1/external/servidor/propostas/simular": [
    {
      "nome": "valor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor simulado, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo simulado."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET usado na simulacao, em FRACAO."
    },
    {
      "nome": "parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela resultante, em reais."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Custo Efetivo Total mensal, em fracao — o mesmo `taxa_am` enviado: a parcela sai dele."
    },
    {
      "nome": "cet_aa",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET anual, em fracao."
    },
    {
      "nome": "total_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma de tudo que seria pago ao fim do contrato, em reais."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "IOF, em reais. 0 na simulacao de emprestimo: o imposto ja esta dentro do CET do banco."
    },
    {
      "nome": "margem_disponivel",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Margem de EMPRESTIMO disponivel no momento da simulacao."
    },
    {
      "nome": "cabe_na_margem",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a parcela cabe na margem AGORA. Simular nao reserva nada: entre a simulacao e a proposta, outra operacao pode consumir a folga."
    }
  ],
  "POST /v1/external/servidor/propostas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da proposta. Guarde: e' a chave para acompanhar e cancelar."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao inicial. Nasce como reserva aguardando analise do banco."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco do convenio."
    },
    {
      "nome": "valor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor solicitado, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo solicitado."
    },
    {
      "nome": "parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela calculada, em reais."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Prazo da reserva (DD/MM/AAAA). Passado ele, a margem e' liberada e a proposta expira."
    }
  ],
  "GET /v1/external/servidor/propostas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da proposta."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Id INTERNO do banco. Os demais endpoints desta camada trazem o nome do banco; este ficou com o id por historico."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela, em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo."
    },
    {
      "nome": "data",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data de lancamento (DD/MM/AAAA)."
    }
  ],
  "GET /v1/external/servidor/contratos": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do contrato."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela mensal, em reais."
    },
    {
      "nome": "parcelas_pagas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parcelas ja descontadas."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado."
    },
    {
      "nome": "saldo_devedor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Saldo devedor atual, em reais."
    }
  ],
  "GET /v1/external/servidor/averbacoes-pendentes": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da averbacao aguardando o seu aceite."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco que iniciou a operacao."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula usada."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "valor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor financiado, em reais."
    },
    {
      "nome": "parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo."
    },
    {
      "nome": "parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela mensal, em reais."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Taxa mensal em FRACAO."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "CET mensal em fracao — juros, IOF, tarifas e seguro. E' o numero a comparar entre propostas."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o banco iniciou (ISO 8601)."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero no sistema do banco, quando informado."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Observacoes registradas pelo banco."
    }
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/confirmar": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Averbacao afetada."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao depois da sua decisao."
    }
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/rejeitar": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Averbacao afetada."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao depois da sua decisao."
    }
  ],
  "GET /v1/external/servidor/portabilidade/elegiveis": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula consultada."
    },
    {
      "nome": "contratos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Emprestimos desta matricula, com o motivo de quem nao pode ser portado agora.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que pode ser origem."
        },
        {
          "nome": "banco_credora_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Credora atual."
        },
        {
          "nome": "banco_credora_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da credora."
        },
        {
          "nome": "convenio_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Convenio do contrato."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela atual, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "elegivel",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se pode abrir portabilidade agora."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Por que nao pode, quando `elegivel` e' false."
        }
      ]
    },
    {
      "nome": "bancos_destino",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Bancos que podem receber a portabilidade. A credora do contrato escolhido nao vale.",
      "campos": [
        {
          "nome": "banco_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Banco com convenio ativo na sua folha."
        },
        {
          "nome": "banco_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do banco."
        },
        {
          "nome": "convenio_id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Convenio dele na sua folha."
        }
      ]
    }
  ],
  "GET /v1/external/servidor/portabilidade": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "GET /v1/external/servidor/portabilidade/{id}": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/servidor/portabilidade": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "POST /v1/external/servidor/portabilidade/{id}/confirmar": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "GET /v1/external/servidor/beneficios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do beneficio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do beneficio."
    },
    {
      "nome": "categorias",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Categorias (saude, alimentacao, educacao, lazer, telemedicina)."
    },
    {
      "nome": "local",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Onde o beneficio vale."
    },
    {
      "nome": "desconto",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Desconto oferecido, como texto."
    },
    {
      "nome": "descricao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Descricao curta."
    },
    {
      "nome": "origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quem oferece."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Banco patrocinador, quando ha. `null` quando e' da prefeitura ou da averbadora."
    },
    {
      "nome": "valor_mensal",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Mensalidade em reais. `null` significa que o beneficio NAO pode ser contratado — sem valor mensal ele nao vira desconto em folha."
    }
  ],
  "POST /v1/external/servidor/beneficios/{id}/contratar": [
    {
      "nome": "intencao",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "A solicitacao criada, ou a que ja existia.",
      "campos": [
        {
          "nome": "id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador da solicitacao."
        },
        {
          "nome": "beneficio_id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Beneficio solicitado."
        },
        {
          "nome": "beneficio_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do beneficio."
        },
        {
          "nome": "valor_mensal",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Mensalidade que virara desconto em folha, em reais."
        },
        {
          "nome": "matricula",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Matricula que recebera o desconto."
        },
        {
          "nome": "status",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Situacao da solicitacao. Nasce `pendente` e espera aprovacao da averbadora."
        },
        {
          "nome": "criado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Solicitacao (ISO 8601)."
        }
      ]
    },
    {
      "nome": "deduplicado",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Presente e `true` quando ja havia solicitacao pendente para o mesmo beneficio e matricula: nada novo foi criado, e a resposta e' 200 em vez de 201."
    }
  ],
  "GET /v1/external/servidor/comunicados": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto."
    },
    {
      "nome": "link_label",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Rotulo do link, quando ha."
    },
    {
      "nome": "link_href",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Destino do link, quando ha."
    },
    {
      "nome": "ativo_ate",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Validade. `null` = sem prazo."
    }
  ],
  "GET /v1/external/servidor/termos": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do termo."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "descricao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Do que trata."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao vigente. E' ela que fica registrada no aceite."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima alteracao (ISO 8601)."
    }
  ],
  "GET /v1/external/servidor/termos/{tipo}": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do termo."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao deste texto."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto ja preenchido com os dados do titular do token. Nome e CPF vem SEMPRE do servidor autenticado — nao ha como pedir o termo em nome de outra pessoa."
    }
  ],
  "GET /v1/external/averbadora/me": [
    {
      "nome": "camada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Camada do token."
    },
    {
      "nome": "environment",
      "formato": "ENUM (production | sandbox)",
      "obrigatorio": true,
      "descricao": "Ambiente. Em `production` as escritas desta camada afetam bancos, prefeituras e folhas de verdade."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos concedidos."
    }
  ],
  "GET /v1/external/averbadora/dashboard": [
    {
      "nome": "contratos_total",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos na plataforma, de todos os bancos."
    },
    {
      "nome": "bancos_ativos",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Bancos com status ativo."
    },
    {
      "nome": "prefeituras_ativas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeituras com status ativo."
    },
    {
      "nome": "servidores_cadastrados",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Vinculos na base, somando todas as prefeituras."
    },
    {
      "nome": "convenios",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Convenios cadastrados."
    }
  ],
  "GET /v1/external/averbadora/contratos": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco credor — presente porque esta camada ve todos os bancos."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor financiado, em reais."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela, em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo."
    },
    {
      "nome": "parcelas_pagas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parcelas ja descontadas."
    },
    {
      "nome": "folha_status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao da ADF na folha. `null` se ainda nao enviada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Criacao (ISO 8601)."
    }
  ],
  "GET /v1/external/averbadora/contratos/{adf}": [
    {
      "nome": "cronograma_pendente",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Prazo do legado pendente de informação."
    },
    {
      "nome": "parcelas_restantes",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Null quando o cronograma ainda não foi informado."
    },
    {
      "nome": "saldo_devedor_conhecido",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Null quando o saldo não pode ser calculado."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato na averbadora."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao atual. Ver o anexo de situacoes de contrato."
    },
    {
      "nome": "tipo_contrato",
      "formato": "ENUM (EMPRESTIMO | REFIN | ECONSIGNADO)",
      "obrigatorio": true,
      "descricao": "Natureza da operacao."
    },
    {
      "nome": "tipo_margem",
      "formato": "ENUM (EMPRESTIMO | CARTAO_CONSIGNADO | CARTAO_BENEFICIOS)",
      "obrigatorio": true,
      "descricao": "Bucket de margem consumido. Derivado do tipo quando o contrato e' anterior ao campo: ECONSIGNADO vira CARTAO_CONSIGNADO, o resto vira EMPRESTIMO."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "id_matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador unico da matricula no padrao da prefeitura — ver a documentacao do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio ao qual o contrato pertence."
    },
    {
      "nome": "convenio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do convenio."
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica de desconto na folha da prefeitura."
    },
    {
      "nome": "numero_contrato_banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Numero do contrato no sistema do proprio banco, quando informado."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor bruto financiado, em reais."
    },
    {
      "nome": "valor_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor liquido apurado, em reais."
    },
    {
      "nome": "valor_liquido_liberado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor efetivamente liberado ao servidor, quando informado na averbacao."
    },
    {
      "nome": "valor_carencia",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Encargo do periodo de carencia, quando houve."
    },
    {
      "nome": "taxa_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Emprestimo e refinanciamento: o CET mensal em FRACAO (0.0179 = 1,79% a.m.). Cartao: juros do rotativo."
    },
    {
      "nome": "cet_am",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Custo Efetivo Total mensal em fracao. Emprestimo: e' o proprio `taxa_am` informado pelo banco (juros, IOF, tarifas e seguro dentro). Cartao: calculado pela averbadora a partir dos juros, com IOF."
    },
    {
      "nome": "iof",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "IOF em reais. Emprestimo: 0 — o imposto esta dentro do CET informado pelo banco, e a averbadora nao o separa."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo contratado, em meses."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela mensal em reais."
    },
    {
      "nome": "dias_carencia",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dias entre a liberacao do credito e a primeira parcela."
    },
    {
      "nome": "saldo_devedor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Saldo devedor atual, em reais."
    },
    {
      "nome": "parcelas_pagas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantidade de parcelas ja descontadas em folha."
    },
    {
      "nome": "data_contrato",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data do contrato (DD/MM/AAAA)."
    },
    {
      "nome": "lancamento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data de lancamento na averbadora (DD/MM/AAAA)."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Criacao em ISO 8601, com hora. Use este, e nao `lancamento`, para calcular a trava de 48h."
    },
    {
      "nome": "expiracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Expiracao da reserva (DD/MM/AAAA). `null` quando nao e' reserva."
    },
    {
      "nome": "expiracao_iso",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Expiracao em ISO 8601, com hora — precisao de segundos para o contador."
    },
    {
      "nome": "vencimento_1a_parcela",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Vencimento da primeira parcela, quando informado."
    },
    {
      "nome": "data_liberacao_credito",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data da liberacao do credito ao servidor, quando informada."
    },
    {
      "nome": "folha_primeiro_desconto",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do primeiro desconto (Mes/AAAA)."
    },
    {
      "nome": "folha_ultimo_desconto",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia do ultimo desconto previsto (Mes/AAAA)."
    },
    {
      "nome": "folha_status",
      "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao global da ADF na folha. `null` enquanto nao foi enviado a nenhuma folha."
    },
    {
      "nome": "folha_motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo informado pela prefeitura quando a folha reprovou o desconto."
    },
    {
      "nome": "folha_competencias",
      "formato": "MAPA (chave livre -> ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto))",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Situacao POR competencia (chave AAAAMM). E' o dado autoritativo: o contrato aparece em varias folhas, uma por mes de parcela."
    },
    {
      "nome": "banco_origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Banco de origem, quando o contrato substitui operacao de terceiro."
    },
    {
      "nome": "contrato_origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato de origem na portabilidade."
    },
    {
      "nome": "saldo_devedor_origem",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo devedor quitado no banco de origem, em reais."
    },
    {
      "nome": "adf_vinculada",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "ADF relacionada — refinanciamento ou portabilidade."
    },
    {
      "nome": "tem_ccb",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha contrato assinado (CCB) anexado. O arquivo em si nao e' exposto pela API externa."
    },
    {
      "nome": "ccb_anexado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o CCB atual foi anexado (ISO 8601)."
    },
    {
      "nome": "iniciada_pelo_banco",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Verdadeiro quando a operacao partiu do banco, e nao do servidor. Nesse caso, prefeituras que exigem senha de averbacao seguram o contrato em 'Aguardando Confirmacao do Servidor'."
    },
    {
      "nome": "observacoes",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Observacoes livres registradas na averbacao."
    },
    {
      "nome": "editada_pelo_banco",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Snapshot do que o servidor pediu antes da contra-proposta. `null` quando o banco nao editou.",
      "campos": [
        {
          "nome": "valor_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor financiado que o servidor havia pedido, antes da contra-proposta."
        },
        {
          "nome": "parcelas_original",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo pedido originalmente."
        },
        {
          "nome": "taxa_am_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Taxa mensal original, em fracao."
        },
        {
          "nome": "parcela_original",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela original em reais."
        },
        {
          "nome": "editado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando a edicao ocorreu (ISO 8601)."
        },
        {
          "nome": "editado_por",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Ator da edicao."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo registrado na contra-proposta."
        }
      ]
    },
    {
      "nome": "situacao_em",
      "formato": "MAPA (chave livre -> ALFANUMERICO)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando cada situacao foi alcancada (chave = situacao em minusculas, valor = ISO 8601). Ausente em contratos anteriores ao campo."
    },
    {
      "nome": "parcelas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Serie de parcelas projetada — ver a ressalva no campo `vencimento`.",
      "campos": [
        {
          "nome": "numero",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Numero da parcela, a partir de 1."
        },
        {
          "nome": "vencimento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia de vencimento (Mes/AAAA). ATENCAO: hoje esta serie e' projetada a partir de uma competencia base fixa e limitada as 24 primeiras parcelas — nao use como calendario de cobranca."
        },
        {
          "nome": "valor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor da parcela em reais."
        },
        {
          "nome": "situacao",
          "formato": "ENUM (Paga | A vencer | Em aberto)",
          "obrigatorio": true,
          "descricao": "Paga = ja descontada; Em aberto = a proxima; A vencer = futuras."
        }
      ]
    },
    {
      "nome": "resultados_desconto",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Resultados resolvidos por competencia, com o valor efetivamente descontado.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia AAAAMM do resultado."
        },
        {
          "nome": "resultado",
          "formato": "ENUM (recebida | aplicada | parcial | falha | interrompida_desligamento | suspensa_sem_folha | suspensa_por_teto)",
          "obrigatorio": true,
          "descricao": "Situacao apurada para o contrato nesta competencia."
        },
        {
          "nome": "valor_instruido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor enviado no movimento, em reais."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor efetivamente descontado pela folha, em reais."
        },
        {
          "nome": "parcial",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Verdadeiro quando o desconto foi menor que o valor instruido."
        },
        {
          "nome": "residuo",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Diferenca ainda nao descontada."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo publicado pela folha, quando informado."
        }
      ]
    },
    {
      "nome": "eventos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Historico de transicoes, do mais antigo ao mais recente.",
      "campos": [
        {
          "nome": "id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Sequencial do evento dentro do contrato."
        },
        {
          "nome": "evento",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da transicao aplicada (criado, aprovado, averbado, cancelado...)."
        },
        {
          "nome": "de_estado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao antes da transicao. `null` no evento de criacao."
        },
        {
          "nome": "para_estado",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao depois da transicao."
        },
        {
          "nome": "ator",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quem executou. Chamada pela API externa aparece como `token:<id>`."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Justificativa informada na acao, quando houve."
        },
        {
          "nome": "trace_id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Correlaciona o evento com a requisicao que o gerou — use ao abrir chamado."
        },
        {
          "nome": "criado_em",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data/hora do evento em ISO 8601."
        }
      ]
    }
  ],
  "GET /v1/external/averbadora/pre-reservas": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da pre-reserva."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao — todas comecam com 'Aguardando'."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio."
    },
    {
      "nome": "valor_financiado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor, em reais."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Parcela, em reais."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo."
    },
    {
      "nome": "expiracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a reserva expira e libera a margem sozinha."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Criacao (ISO 8601)."
    }
  ],
  "POST /v1/external/averbadora/pre-reservas/{id}/cancelar": [
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Pre-reserva cancelada."
    },
    {
      "nome": "situacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao depois do cancelamento. A margem travada e' liberada na hora."
    }
  ],
  "GET /v1/external/averbadora/folhas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da folha."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona da folha."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da folha.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` quando nao definida."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": true,
      "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
    },
    {
      "nome": "conferencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Estado de conferencia da competencia: `confirmada` ou `presumida`. `presumida` = fechou pela data de repasse sem o retorno da prefeitura ter chegado — os descontos entram como aplicados, mas ninguem confirmou. Ausente em folha aberta."
    },
    {
      "nome": "corteEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "O INSTANTE do corte (ISO com offset). E' ele quem manda no congelamento, e nao `data_corte`, que e' data sem hora. Note o camelCase."
    },
    {
      "nome": "unidadeId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeituraId` e o nome historico e continua saindo, com o mesmo valor. Note o camelCase."
    },
    {
      "nome": "prefeituraId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "@deprecated Use `prefeitura_id`. Mantido porque a listagem sempre entregou o objeto cru em camelCase."
    },
    {
      "nome": "dataCorte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "@deprecated Use `data_corte`. Note o camelCase."
    },
    {
      "nome": "dataRepasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "@deprecated Use `data_repasse`. Note o camelCase."
    }
  ],
  "POST /v1/external/averbadora/folhas/{id}/consolidar": [
    {
      "nome": "folha",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "A folha, agora consolidada.",
      "campos": [
        {
          "nome": "id",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador da folha."
        },
        {
          "nome": "unidade_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prefeitura dona da folha."
        },
        {
          "nome": "prefeitura",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da prefeitura."
        },
        {
          "nome": "organizacao",
          "formato": "OBJETO",
          "obrigatorio": false,
          "descricao": "Classificacao institucional da organizacao dona da folha.",
          "campos": [
            {
              "nome": "tipo",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
            },
            {
              "nome": "esfera",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
            },
            {
              "nome": "sigla",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "nulavel": true,
              "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
            },
            {
              "nome": "nome_exibicao",
              "formato": "ALFANUMERICO",
              "obrigatorio": true,
              "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
            }
          ]
        },
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia em AAAAMM."
        },
        {
          "nome": "data_corte",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data limite para novas averbacoes entrarem nesta folha."
        },
        {
          "nome": "data_repasse",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Data prevista do repasse. `null` quando nao definida."
        },
        {
          "nome": "status",
          "formato": "ENUM (aberta | fechada | consolidada)",
          "obrigatorio": true,
          "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
        }
      ]
    },
    {
      "nome": "parcelas_incrementadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos que tiveram uma parcela contada como paga."
    },
    {
      "nome": "contratos_quitados",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos que chegaram ao fim do prazo e foram quitados nesta consolidacao."
    }
  ],
  "GET /v1/external/averbadora/adf": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da ADF (`ADF-AAAAMM-...`). E' este campo que vai em `ids`."
    },
    {
      "nome": "adf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do contrato."
    },
    {
      "nome": "id_unico",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do servidor no padrao da prefeitura."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura — presente porque a visao aqui e' global."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da ADF.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Banco credor."
    },
    {
      "nome": "valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor a descontar."
    },
    {
      "nome": "total_parcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prazo do contrato."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "`recebida`, `aplicada` ou `falha`."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo da falha, quando houver."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Ultima mudanca de status."
    }
  ],
  "GET /v1/external/averbadora/adf/competencias": [
    {
      "nome": "competencias",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Competencias com ADFs, da mais recente para a mais antiga.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia em AAAAMM."
        },
        {
          "nome": "total",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "ADFs na competencia, somando todas as prefeituras."
        },
        {
          "nome": "aplicadas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Ja descontadas."
        },
        {
          "nome": "falhas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Reprovadas."
        }
      ]
    },
    {
      "nome": "competencia_atual",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia da folha mais recente."
    }
  ],
  "POST /v1/external/averbadora/adf/confirmar": [
    {
      "nome": "aplicadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `aplicada`."
    },
    {
      "nome": "falhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `falha`."
    },
    {
      "nome": "solicitadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantos ids vieram na requisicao."
    },
    {
      "nome": "adfs",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contratos efetivamente alterados. MENOR que `solicitadas` quando algum id nao existe, ja estava naquele estado, ou pertence a outra prefeitura — compare os dois numeros antes de dar a operacao por concluida."
    }
  ],
  "POST /v1/external/averbadora/adf/falha": [
    {
      "nome": "aplicadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `aplicada`."
    },
    {
      "nome": "falhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": false,
      "descricao": "Quantas ADFs mudaram para `falha`."
    },
    {
      "nome": "solicitadas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantos ids vieram na requisicao."
    },
    {
      "nome": "adfs",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Contratos efetivamente alterados. MENOR que `solicitadas` quando algum id nao existe, ja estava naquele estado, ou pertence a outra prefeitura — compare os dois numeros antes de dar a operacao por concluida."
    }
  ],
  "POST /v1/external/averbadora/bate-carteira": [
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco consultado — sempre o dono do token."
    },
    {
      "nome": "banco_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia consultada, em AAAAMM."
    },
    {
      "nome": "total_linhas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Contratos da carteira na competencia — uma linha por contrato."
    },
    {
      "nome": "soma_saldo_devedor",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma dos saldos devedores, em reais."
    },
    {
      "nome": "soma_valor_parcela",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Soma do valor instruido na competencia, em reais."
    },
    {
      "nome": "linhas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Uma linha por contrato da carteira: o contrato ativo na competencia, ou com resultado da folha nela.",
      "campos": [
        {
          "nome": "competencia",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Competencia da linha, em AAAAMM."
        },
        {
          "nome": "banco_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Banco da operacao."
        },
        {
          "nome": "banco_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome do banco."
        },
        {
          "nome": "unidade_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_id",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prefeitura da matricula."
        },
        {
          "nome": "unidade_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
        },
        {
          "nome": "prefeitura_nome",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Nome da prefeitura."
        },
        {
          "nome": "organizacao_tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "organizacao_sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "nulavel": true,
          "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
        },
        {
          "nome": "cpf_masked",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "CPF mascarado (LGPD)."
        },
        {
          "nome": "matricula",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Matricula do servidor."
        },
        {
          "nome": "id_unico",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Identificador unico no padrao da prefeitura."
        },
        {
          "nome": "adf_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato no seu sistema, quando informado."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela em reais."
        },
        {
          "nome": "parcelas_restantes",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas que faltam, quando conhecido."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Saldo devedor, quando conhecido."
        },
        {
          "nome": "origem",
          "formato": "ENUM (tombamento | pre_reserva_confirmada)",
          "obrigatorio": true,
          "descricao": "De onde o contrato veio: `tombamento` e' contrato legado, informado na carga da folha; `pre_reserva_confirmada` e' operacao averbada na Atlas."
        },
        {
          "nome": "status",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Conciliacao da linha: `conciliado`, `divergente`, `nao_descontado`, `nao_encontrado_na_folha` ou `aguardando_folha`. Para o que a folha fez, leia `status_folha`."
        },
        {
          "nome": "data",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Data do resultado da folha; sem resultado, a data do contrato (AAAA-MM-DD)."
        },
        {
          "nome": "status_folha",
          "formato": "ENUM (aplicada | parcial | nao_descontada | suspensa | aguardando_retorno)",
          "obrigatorio": true,
          "descricao": "O que a folha fez com o contrato na competencia: `aplicada` (descontou), `parcial` (descontou menos que o instruido), `nao_descontada` (falha ou desligamento), `suspensa` (sem folha no mes ou teto do orgao) ou `aguardando_retorno` (o retorno ainda nao chegou)."
        },
        {
          "nome": "valor_descontado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que a folha descontou, em reais. `null` enquanto o retorno nao chega."
        },
        {
          "nome": "residuo",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "O que faltou descontar da parcela instruida, em reais. `null` enquanto o retorno nao chega."
        },
        {
          "nome": "motivo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Motivo informado pela folha, quando houver."
        }
      ]
    },
    {
      "nome": "gerado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quando o confronto foi gerado (ISO 8601)."
    }
  ],
  "GET /v1/external/averbadora/portabilidade": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
  "GET /v1/external/averbadora/ofertas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da oferta."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que publicou."
    },
    {
      "nome": "banco",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo da campanha."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Produto ofertado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Flag do cadastro. NAO leve sozinho: veja `expirada`."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Publicacao (ISO 8601)."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data de expiracao. `null` = sem prazo."
    },
    {
      "nome": "expirada",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a data de expiracao ja passou. Calculado na leitura — o `ativo` continua `true` mesmo depois de expirar."
    },
    {
      "nome": "filtro",
      "formato": "MAPA (chave livre -> UNKNOWN)",
      "obrigatorio": true,
      "descricao": "Publico alvo, no formato interno (camelCase). Diferente do `filtro` da camada banco, que sai normalizado."
    }
  ],
  "GET /v1/external/averbadora/comunicados": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do comunicado."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto do comunicado."
    },
    {
      "nome": "link_label",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Rotulo do link, quando ha um."
    },
    {
      "nome": "link_href",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Destino do link, quando ha um."
    },
    {
      "nome": "ativo_ate",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Ate quando o comunicado vale. `null` = sem prazo."
    }
  ],
  "PATCH /v1/external/averbadora/beneficios/{id}/pausar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Recurso afetado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado DEPOIS da operacao. Estas rotas nunca apagam — desativam e reativam."
    }
  ],
  "PATCH /v1/external/averbadora/beneficios/{id}/reativar": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Recurso afetado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Estado DEPOIS da operacao. Estas rotas nunca apagam — desativam e reativam."
    }
  ],
  "POST /v1/external/averbadora/eventos/disparar": [
    {
      "nome": "event",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Evento disparado."
    },
    {
      "nome": "deliveries",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Quantas entregas foram enfileiradas. ZERO significa que nenhum parceiro assina este evento neste ambiente — nao e' erro, e nao ha aviso alem deste numero."
    }
  ],
  "GET /v1/external/averbadora/bancos": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador do banco."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao. `pausado` derruba os tokens do banco na hora."
    },
    {
      "nome": "adapter",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Adaptador bancario em uso: `sandbox` nao toca sistema externo."
    },
    {
      "nome": "contatoEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Contato tecnico. Note o camelCase, herdado do cadastro."
    },
    {
      "nome": "loginEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail de acesso ao portal do banco. Note o camelCase."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos padrao do CADASTRO — nao sao os escopos dos tokens emitidos."
    },
    {
      "nome": "mtlsHabilitado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a integracao exige mTLS. Note o camelCase."
    },
    {
      "nome": "baseUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL base para health check. Preenchida, o monitor pinga `/health` a cada 20s; ausente, o banco aparece como 'sem endpoint'."
    },
    {
      "nome": "ultimoTeste",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Quando o health check rodou pela ultima vez."
    },
    {
      "nome": "ultimoTesteOk",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Resultado do ultimo health check."
    },
    {
      "nome": "hasPassword",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha senha cadastrada. O hash NAO e' devolvido — este booleano existe para a tela saber o estado sem ver a credencial."
    },
    {
      "nome": "twoFactorEnabled",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o operador tem 2FA ativo. O segredo TOTP nao e' devolvido, pelo mesmo motivo."
    },
    {
      "nome": "cnpj",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "CNPJ, so digitos."
    },
    {
      "nome": "razaoSocial",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Razao social da Receita. Note o camelCase."
    },
    {
      "nome": "nomeFantasia",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nome fantasia. Note o camelCase."
    },
    {
      "nome": "dataFundacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Data de fundacao. Note o camelCase."
    },
    {
      "nome": "atividade",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Atividade principal (CNAE)."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Telefone."
    },
    {
      "nome": "endereco",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Endereco da consulta de CNPJ.",
      "campos": [
        {
          "nome": "logradouro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Logradouro."
        },
        {
          "nome": "numero",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Numero."
        },
        {
          "nome": "complemento",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Complemento."
        },
        {
          "nome": "bairro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Bairro."
        },
        {
          "nome": "cep",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "CEP, so digitos."
        },
        {
          "nome": "municipio",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Municipio."
        },
        {
          "nome": "uf",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "UF."
        }
      ]
    }
  ],
  "POST /v1/external/averbadora/bancos": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador do banco."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao. `pausado` derruba os tokens do banco na hora."
    },
    {
      "nome": "adapter",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Adaptador bancario em uso: `sandbox` nao toca sistema externo."
    },
    {
      "nome": "contatoEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Contato tecnico. Note o camelCase, herdado do cadastro."
    },
    {
      "nome": "loginEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail de acesso ao portal do banco. Note o camelCase."
    },
    {
      "nome": "scopes",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Escopos padrao do CADASTRO — nao sao os escopos dos tokens emitidos."
    },
    {
      "nome": "mtlsHabilitado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a integracao exige mTLS. Note o camelCase."
    },
    {
      "nome": "baseUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL base para health check. Preenchida, o monitor pinga `/health` a cada 20s; ausente, o banco aparece como 'sem endpoint'."
    },
    {
      "nome": "ultimoTeste",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Quando o health check rodou pela ultima vez."
    },
    {
      "nome": "ultimoTesteOk",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Resultado do ultimo health check."
    },
    {
      "nome": "hasPassword",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha senha cadastrada. O hash NAO e' devolvido — este booleano existe para a tela saber o estado sem ver a credencial."
    },
    {
      "nome": "twoFactorEnabled",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o operador tem 2FA ativo. O segredo TOTP nao e' devolvido, pelo mesmo motivo."
    },
    {
      "nome": "cnpj",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "CNPJ, so digitos."
    },
    {
      "nome": "razaoSocial",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Razao social da Receita. Note o camelCase."
    },
    {
      "nome": "nomeFantasia",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nome fantasia. Note o camelCase."
    },
    {
      "nome": "dataFundacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Data de fundacao. Note o camelCase."
    },
    {
      "nome": "atividade",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Atividade principal (CNAE)."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Telefone."
    },
    {
      "nome": "endereco",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Endereco da consulta de CNPJ.",
      "campos": [
        {
          "nome": "logradouro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Logradouro."
        },
        {
          "nome": "numero",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Numero."
        },
        {
          "nome": "complemento",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Complemento."
        },
        {
          "nome": "bairro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Bairro."
        },
        {
          "nome": "cep",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "CEP, so digitos."
        },
        {
          "nome": "municipio",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Municipio."
        },
        {
          "nome": "uf",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "UF."
        }
      ]
    }
  ],
  "GET /v1/external/averbadora/prefeituras": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da organizacao (unidade pagadora)."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional. Ausente nas linhas ainda nao classificadas — leia como `prefeitura`."
    },
    {
      "nome": "esfera",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`municipal` | `estadual` | `federal`."
    },
    {
      "nome": "poder",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`executivo` | `legislativo` | `judiciario` | `autonomo` | `outro`."
    },
    {
      "nome": "classificacao_adm",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`direta` | `indireta` | `nao_se_aplica`."
    },
    {
      "nome": "sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Como a casa se chama."
    },
    {
      "nome": "nome_exibicao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "O nome que o SERVIDOR daquela organizacao le. Sai do dominio, nunca de um `if` no cliente."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF."
    },
    {
      "nome": "municipioIbge",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Codigo IBGE do municipio. Note o camelCase."
    },
    {
      "nome": "modoIntegracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Como troca dados com a plataforma. Note o camelCase."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao."
    },
    {
      "nome": "loginEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail de acesso ao portal da prefeitura."
    },
    {
      "nome": "contatoEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail do responsavel — separado do de acesso."
    },
    {
      "nome": "servidoresCount",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Vinculos na base desta prefeitura. Note o camelCase."
    },
    {
      "nome": "ultimaSincronizacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Ultima sincronizacao da folha."
    },
    {
      "nome": "folhaSincUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL publica do CSV da folha, no modo CSV."
    },
    {
      "nome": "ultimaSincResultado",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Resultado da ultima sincronizacao automatica.",
      "campos": [
        {
          "nome": "novos",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Vinculos criados."
        },
        {
          "nome": "atualizados",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Vinculos atualizados."
        },
        {
          "nome": "erro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Erro da sincronizacao, quando houve."
        },
        {
          "nome": "ts",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando rodou."
        }
      ]
    },
    {
      "nome": "exigeCcb",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se exige contrato assinado antes de o banco aprovar. VALIDADO."
    },
    {
      "nome": "exigeBanco2FA",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se declara exigir 2FA do banco. Informativo — nao validado."
    },
    {
      "nome": "exigeSenhaAverbacao",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se averbacao iniciada pelo banco espera aceite do servidor com senha. VALIDADO. Organizacao nova nasce falso; registro antigo sem o campo e lido como verdadeiro."
    },
    {
      "nome": "exigeSenhaProposta",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o servidor precisa digitar a senha ao criar proposta. VALIDADO. Padrao falso."
    },
    {
      "nome": "permiteServidorEditarContato",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o servidor pode editar email e telefone pelo app. Falso deixa a tela somente-leitura."
    },
    {
      "nome": "exclusividadesCartaoConsig",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Texto livre com condicoes exclusivas de cartao consignado desta prefeitura."
    },
    {
      "nome": "hasPassword",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha senha cadastrada. O hash nao e' devolvido."
    },
    {
      "nome": "twoFactorEnabled",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o operador tem 2FA ativo. O segredo nao e' devolvido."
    },
    {
      "nome": "cnpj",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "CNPJ, so digitos."
    },
    {
      "nome": "razaoSocial",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Razao social."
    },
    {
      "nome": "nomeFantasia",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nome fantasia."
    },
    {
      "nome": "dataFundacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Data de fundacao."
    },
    {
      "nome": "atividade",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Atividade principal."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Telefone."
    },
    {
      "nome": "endereco",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Endereco da consulta de CNPJ.",
      "campos": [
        {
          "nome": "logradouro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Logradouro."
        },
        {
          "nome": "numero",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Numero."
        },
        {
          "nome": "complemento",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Complemento."
        },
        {
          "nome": "bairro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Bairro."
        },
        {
          "nome": "cep",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "CEP, so digitos."
        },
        {
          "nome": "municipio",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Municipio."
        },
        {
          "nome": "uf",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "UF."
        }
      ]
    }
  ],
  "POST /v1/external/averbadora/prefeituras": [
    {
      "nome": "id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da organizacao (unidade pagadora)."
    },
    {
      "nome": "tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional. Ausente nas linhas ainda nao classificadas — leia como `prefeitura`."
    },
    {
      "nome": "esfera",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`municipal` | `estadual` | `federal`."
    },
    {
      "nome": "poder",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`executivo` | `legislativo` | `judiciario` | `autonomo` | `outro`."
    },
    {
      "nome": "classificacao_adm",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "`direta` | `indireta` | `nao_se_aplica`."
    },
    {
      "nome": "sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Como a casa se chama."
    },
    {
      "nome": "nome_exibicao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "O nome que o SERVIDOR daquela organizacao le. Sai do dominio, nunca de um `if` no cliente."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF."
    },
    {
      "nome": "municipioIbge",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Codigo IBGE do municipio. Note o camelCase."
    },
    {
      "nome": "modoIntegracao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Como troca dados com a plataforma. Note o camelCase."
    },
    {
      "nome": "status",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao."
    },
    {
      "nome": "loginEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail de acesso ao portal da prefeitura."
    },
    {
      "nome": "contatoEmail",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "E-mail do responsavel — separado do de acesso."
    },
    {
      "nome": "servidoresCount",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Vinculos na base desta prefeitura. Note o camelCase."
    },
    {
      "nome": "ultimaSincronizacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Ultima sincronizacao da folha."
    },
    {
      "nome": "folhaSincUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL publica do CSV da folha, no modo CSV."
    },
    {
      "nome": "ultimaSincResultado",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Resultado da ultima sincronizacao automatica.",
      "campos": [
        {
          "nome": "novos",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Vinculos criados."
        },
        {
          "nome": "atualizados",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Vinculos atualizados."
        },
        {
          "nome": "erro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Erro da sincronizacao, quando houve."
        },
        {
          "nome": "ts",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Quando rodou."
        }
      ]
    },
    {
      "nome": "exigeCcb",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se exige contrato assinado antes de o banco aprovar. VALIDADO."
    },
    {
      "nome": "exigeBanco2FA",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se declara exigir 2FA do banco. Informativo — nao validado."
    },
    {
      "nome": "exigeSenhaAverbacao",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se averbacao iniciada pelo banco espera aceite do servidor com senha. VALIDADO. Organizacao nova nasce falso; registro antigo sem o campo e lido como verdadeiro."
    },
    {
      "nome": "exigeSenhaProposta",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o servidor precisa digitar a senha ao criar proposta. VALIDADO. Padrao falso."
    },
    {
      "nome": "permiteServidorEditarContato",
      "formato": "BOOLEANO",
      "obrigatorio": false,
      "descricao": "Se o servidor pode editar email e telefone pelo app. Falso deixa a tela somente-leitura."
    },
    {
      "nome": "exclusividadesCartaoConsig",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Texto livre com condicoes exclusivas de cartao consignado desta prefeitura."
    },
    {
      "nome": "hasPassword",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se ha senha cadastrada. O hash nao e' devolvido."
    },
    {
      "nome": "twoFactorEnabled",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o operador tem 2FA ativo. O segredo nao e' devolvido."
    },
    {
      "nome": "cnpj",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "CNPJ, so digitos."
    },
    {
      "nome": "razaoSocial",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Razao social."
    },
    {
      "nome": "nomeFantasia",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Nome fantasia."
    },
    {
      "nome": "dataFundacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Data de fundacao."
    },
    {
      "nome": "atividade",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Atividade principal."
    },
    {
      "nome": "telefone",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Telefone."
    },
    {
      "nome": "endereco",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Endereco da consulta de CNPJ.",
      "campos": [
        {
          "nome": "logradouro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Logradouro."
        },
        {
          "nome": "numero",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Numero."
        },
        {
          "nome": "complemento",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Complemento."
        },
        {
          "nome": "bairro",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Bairro."
        },
        {
          "nome": "cep",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "CEP, so digitos."
        },
        {
          "nome": "municipio",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "Municipio."
        },
        {
          "nome": "uf",
          "formato": "ALFANUMERICO",
          "obrigatorio": false,
          "descricao": "UF."
        }
      ]
    }
  ],
  "GET /v1/external/averbadora/convenios/{id}/config": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a configuracao pertence."
    },
    {
      "nome": "prazoTravaHoras",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da reserva, em horas. Note o camelCase."
    },
    {
      "nome": "prazoPortabilidadeDU",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da trava de portabilidade, em dias uteis."
    },
    {
      "nome": "maxParcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Teto de parcelas do convenio."
    },
    {
      "nome": "taxaMaxAm",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Teto de CET em PERCENTUAL (1.79 = 1,79% a.m.), nao em fracao. Vale para emprestimo, portabilidade e refinanciamento; cartao nao passa por ele."
    },
    {
      "nome": "idadeMin",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade minima. Informativa — nao validada."
    },
    {
      "nome": "idadeMax",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade maxima. Informativa — nao validada."
    },
    {
      "nome": "maxComprometimentoPct",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comprometimento maximo em FRACAO. Informativo — nao validado."
    },
    {
      "nome": "vinculosAceitos",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculos declarados. Informativo — nao validado."
    },
    {
      "nome": "formatoImportacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Formato combinado para o envio da base."
    },
    {
      "nome": "regrasEspeciais",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto livre com as particularidades do convenio."
    },
    {
      "nome": "vigenciaInicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia."
    },
    {
      "nome": "vigenciaFim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia. `null` = indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a configuracao esta valendo."
    },
    {
      "nome": "percentualComissao",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comissao em FRACAO. `null` faz o faturamento cair em 2%."
    },
    {
      "nome": "atualizadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima alteracao (ISO 8601). Note o camelCase."
    }
  ],
  "GET /v1/external/averbadora/convenios-configs": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a configuracao pertence."
    },
    {
      "nome": "prazoTravaHoras",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da reserva, em horas. Note o camelCase."
    },
    {
      "nome": "prazoPortabilidadeDU",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da trava de portabilidade, em dias uteis."
    },
    {
      "nome": "maxParcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Teto de parcelas do convenio."
    },
    {
      "nome": "taxaMaxAm",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Teto de CET em PERCENTUAL (1.79 = 1,79% a.m.), nao em fracao. Vale para emprestimo, portabilidade e refinanciamento; cartao nao passa por ele."
    },
    {
      "nome": "idadeMin",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade minima. Informativa — nao validada."
    },
    {
      "nome": "idadeMax",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade maxima. Informativa — nao validada."
    },
    {
      "nome": "maxComprometimentoPct",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comprometimento maximo em FRACAO. Informativo — nao validado."
    },
    {
      "nome": "vinculosAceitos",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculos declarados. Informativo — nao validado."
    },
    {
      "nome": "formatoImportacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Formato combinado para o envio da base."
    },
    {
      "nome": "regrasEspeciais",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto livre com as particularidades do convenio."
    },
    {
      "nome": "vigenciaInicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia."
    },
    {
      "nome": "vigenciaFim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia. `null` = indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a configuracao esta valendo."
    },
    {
      "nome": "percentualComissao",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comissao em FRACAO. `null` faz o faturamento cair em 2%."
    },
    {
      "nome": "atualizadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima alteracao (ISO 8601). Note o camelCase."
    }
  ],
  "POST /v1/external/averbadora/convenios/{id}/config": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio a que a configuracao pertence."
    },
    {
      "nome": "prazoTravaHoras",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da reserva, em horas. Note o camelCase."
    },
    {
      "nome": "prazoPortabilidadeDU",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Validade da trava de portabilidade, em dias uteis."
    },
    {
      "nome": "maxParcelas",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Teto de parcelas do convenio."
    },
    {
      "nome": "taxaMaxAm",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Teto de CET em PERCENTUAL (1.79 = 1,79% a.m.), nao em fracao. Vale para emprestimo, portabilidade e refinanciamento; cartao nao passa por ele."
    },
    {
      "nome": "idadeMin",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade minima. Informativa — nao validada."
    },
    {
      "nome": "idadeMax",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Idade maxima. Informativa — nao validada."
    },
    {
      "nome": "maxComprometimentoPct",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comprometimento maximo em FRACAO. Informativo — nao validado."
    },
    {
      "nome": "vinculosAceitos",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculos declarados. Informativo — nao validado."
    },
    {
      "nome": "formatoImportacao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Formato combinado para o envio da base."
    },
    {
      "nome": "regrasEspeciais",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto livre com as particularidades do convenio."
    },
    {
      "nome": "vigenciaInicio",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Inicio da vigencia."
    },
    {
      "nome": "vigenciaFim",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim da vigencia. `null` = indeterminado."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se a configuracao esta valendo."
    },
    {
      "nome": "percentualComissao",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Comissao em FRACAO. `null` faz o faturamento cair em 2%."
    },
    {
      "nome": "atualizadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima alteracao (ISO 8601). Note o camelCase."
    }
  ],
  "PATCH /v1/external/averbadora/servidores/{matricula}": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula alterada."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome apos a alteracao."
    },
    {
      "nome": "atualizado",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Confirmacao. Sempre `true` — falha vira erro, nao `false`."
    }
  ],
  "GET /v1/external/averbadora/auditoria": [
    {
      "nome": "entries",
      "formato": "LISTA de MAPA (chave livre -> UNKNOWN)",
      "obrigatorio": true,
      "descricao": "Registros de auditoria, do mais recente ao mais antigo. Forma variavel por categoria — nao ha contrato fechado por entrada."
    },
    {
      "nome": "categorias",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Categorias disponiveis para filtrar a auditoria.",
      "campos": [
        {
          "nome": "value",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Valor a passar no filtro `?categoria`."
        },
        {
          "nome": "label",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Rotulo legivel da categoria."
        }
      ]
    }
  ],
  "GET /v1/external/averbadora/servidores": [
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula na prefeitura."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome completo."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "vinculo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Vinculo funcional."
    },
    {
      "nome": "situacao_funcional",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Situacao na folha."
    },
    {
      "nome": "salario_liquido",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Valor da coluna de salario liquido da carga. E' a base de calculo das margens, EXCETO quando `base_margem` e' `margem_da_folha`: ai' e' a margem de emprestimo que a folha mandou pronta."
    },
    {
      "nome": "base_margem",
      "formato": "ENUM (informada | liquida_legal | margem_da_folha)",
      "obrigatorio": true,
      "descricao": "Sobre o que a margem da unidade incide na regra vigente: `informada` (percentual sobre `salario_liquido`), `liquida_legal` (percentual sobre a base legal do contracheque) ou `margem_da_folha` (`salario_liquido` ja' e' a margem de emprestimo que a folha mandou; os cartoes saem dela na proporcao da regra)."
    }
  ],
  "GET /v1/external/averbadora/vitrine": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do banner."
    },
    {
      "nome": "bancoId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco anunciante. Note o camelCase."
    },
    {
      "nome": "bancoNome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco. Note o camelCase."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo do banner."
    },
    {
      "nome": "imagemUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL da imagem. Note o camelCase."
    },
    {
      "nome": "impressoes",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Exibicoes acumuladas."
    },
    {
      "nome": "cliques",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Cliques acumulados."
    },
    {
      "nome": "precoPorClique",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "descricao": "Quanto o banco paga por clique, em reais. Ausente = banner institucional, cliques nao geram receita."
    },
    {
      "nome": "receitaMes",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Receita do MES CORRENTE. Recalculada a cada leitura a partir dos eventos de clique — nao e' persistida, e no banco o valor guardado e' sempre zero."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o banner esta circulando."
    }
  ],
  "POST /v1/external/averbadora/vitrine": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do banner."
    },
    {
      "nome": "bancoId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco anunciante. Note o camelCase."
    },
    {
      "nome": "bancoNome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco. Note o camelCase."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo do banner."
    },
    {
      "nome": "imagemUrl",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "URL da imagem. Note o camelCase."
    },
    {
      "nome": "impressoes",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Exibicoes acumuladas."
    },
    {
      "nome": "cliques",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Cliques acumulados."
    },
    {
      "nome": "precoPorClique",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "descricao": "Quanto o banco paga por clique, em reais. Ausente = banner institucional, cliques nao geram receita."
    },
    {
      "nome": "receitaMes",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "descricao": "Receita do MES CORRENTE. Recalculada a cada leitura a partir dos eventos de clique — nao e' persistida, e no banco o valor guardado e' sempre zero."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se o banner esta circulando."
    }
  ],
  "POST /v1/external/averbadora/comunicados": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do comunicado."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto do comunicado."
    },
    {
      "nome": "link_label",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Rotulo do link, quando ha um."
    },
    {
      "nome": "link_href",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Destino do link, quando ha um."
    },
    {
      "nome": "ativo_ate",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Ate quando o comunicado vale. `null` = sem prazo."
    }
  ],
  "GET /v1/external/averbadora/termos": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Tipo do termo — e' o identificador e o parametro de rota."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "descricao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Descricao do editor — NAO entra no texto aceito."
    },
    {
      "nome": "variaveis",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Placeholders disponiveis para este tipo, preenchidos na leitura."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto com `{{placeholders}}`, renderizado como HTML basico."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao vigente — e' ela que fica gravada em cada aceite. Mudar o corpo sem mudar a versao faz aceites antigos apontarem para um texto que a pessoa nao leu."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se esta vigente."
    },
    {
      "nome": "criadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao (ISO 8601). Note o camelCase."
    }
  ],
  "POST /v1/external/averbadora/termos/{tipo}": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Tipo do termo — e' o identificador e o parametro de rota."
    },
    {
      "nome": "titulo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Titulo."
    },
    {
      "nome": "descricao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Descricao do editor — NAO entra no texto aceito."
    },
    {
      "nome": "variaveis",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Placeholders disponiveis para este tipo, preenchidos na leitura."
    },
    {
      "nome": "corpo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Texto com `{{placeholders}}`, renderizado como HTML basico."
    },
    {
      "nome": "versao",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Versao vigente — e' ela que fica gravada em cada aceite. Mudar o corpo sem mudar a versao faz aceites antigos apontarem para um texto que a pessoa nao leu."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se esta vigente."
    },
    {
      "nome": "criadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao (ISO 8601). Note o camelCase."
    }
  ],
  "GET /v1/external/averbadora/webhooks": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do webhook."
    },
    {
      "nome": "audience",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Camada do parceiro dono."
    },
    {
      "nome": "partner_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Parceiro dono."
    },
    {
      "nome": "url",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Destino das entregas."
    },
    {
      "nome": "events",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Eventos assinados."
    },
    {
      "nome": "active",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se esta recebendo entregas."
    },
    {
      "nome": "environment",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ambiente das entregas."
    }
  ],
  "GET /v1/external/averbadora/convenios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Prefeitura conveniada."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco do convenio."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura do convenio."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao conveniada. `prefeitura` acima continua sendo o nome cru do cadastro.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica unica, do modelo antigo. Vazia quando o convenio declara verba por produto — leia `verbas`."
    },
    {
      "nome": "verbas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": false,
      "descricao": "Uma verba por produto ofertado. E' esta declaracao que classifica o desconto na folha.",
      "campos": [
        {
          "nome": "produto",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "EMPRESTIMO, CARTAO_CONSIGNADO ou CARTAO_BENEFICIOS."
        },
        {
          "nome": "codigo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Rubrica de desconto na folha desta prefeitura, para este produto."
        },
        {
          "nome": "competencia_inicial",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "AAAAMM a partir de quando esta verba vale."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`ativa` ou `inativa`. Inativa nao origina, e nao solta o passado."
        }
      ]
    },
    {
      "nome": "data_corte",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes em que a folha fecha para novas averbacoes. Vem do ORGAO (ADR-0012) e e' o mesmo para todos os convenios dele — nao e' negociado por banco."
    },
    {
      "nome": "dia_repasse",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes do repasse, no mes seguinte a competencia. Vem do ORGAO, como `data_corte`."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Convenio desativado sai das listagens operacionais mas o registro permanece — desativar nunca apaga."
    }
  ],
  "POST /v1/external/averbadora/convenios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do convenio."
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Prefeitura conveniada."
    },
    {
      "nome": "uf",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "UF."
    },
    {
      "nome": "banco_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco do convenio."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura do convenio."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao conveniada. `prefeitura` acima continua sendo o nome cru do cadastro.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "codigo_verba",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Rubrica unica, do modelo antigo. Vazia quando o convenio declara verba por produto — leia `verbas`."
    },
    {
      "nome": "verbas",
      "formato": "LISTA de OBJETO",
      "obrigatorio": false,
      "descricao": "Uma verba por produto ofertado. E' esta declaracao que classifica o desconto na folha.",
      "campos": [
        {
          "nome": "produto",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "EMPRESTIMO, CARTAO_CONSIGNADO ou CARTAO_BENEFICIOS."
        },
        {
          "nome": "codigo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Rubrica de desconto na folha desta prefeitura, para este produto."
        },
        {
          "nome": "competencia_inicial",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "AAAAMM a partir de quando esta verba vale."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`ativa` ou `inativa`. Inativa nao origina, e nao solta o passado."
        }
      ]
    },
    {
      "nome": "data_corte",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes em que a folha fecha para novas averbacoes. Vem do ORGAO (ADR-0012) e e' o mesmo para todos os convenios dele — nao e' negociado por banco."
    },
    {
      "nome": "dia_repasse",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Dia do mes do repasse, no mes seguinte a competencia. Vem do ORGAO, como `data_corte`."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Convenio desativado sai das listagens operacionais mas o registro permanece — desativar nunca apaga."
    }
  ],
  "GET /v1/external/averbadora/servidores/campos-config/{prefeituraId}": [
    {
      "nome": "unidadeId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeituraId` e o nome historico e continua saindo, com o mesmo valor. Note o camelCase."
    },
    {
      "nome": "prefeituraId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura da configuracao. Note o camelCase."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona desta configuracao.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "campos",
      "formato": "LISTA de OBJETO",
      "obrigatorio": true,
      "descricao": "Campos aceitos por esta prefeitura, na ordem de exibicao.",
      "campos": [
        {
          "nome": "key",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Chave do campo — e' o nome a usar no payload de importacao."
        },
        {
          "nome": "label",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Rotulo exibido na tabela e no CSV."
        },
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo do campo (texto, email, numero, data...)."
        },
        {
          "nome": "obrigatorio",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se a importacao RECUSA a linha que nao trouxer este campo."
        },
        {
          "nome": "visivel",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "Se aparece na tabela e no template de CSV."
        },
        {
          "nome": "ordem",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Posicao de exibicao."
        },
        {
          "nome": "sistema",
          "formato": "BOOLEANO",
          "obrigatorio": true,
          "descricao": "`true` = campo nativo do cadastro; `false` = campo livre criado por esta prefeitura."
        },
        {
          "nome": "travado",
          "formato": "BOOLEANO",
          "obrigatorio": false,
          "descricao": "Campo de identidade (CPF e matricula): visivel e obrigatorio nao podem ser alterados."
        }
      ]
    },
    {
      "nome": "atualizadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima alteracao da config. Note o camelCase."
    }
  ],
  "POST /v1/external/averbadora/folhas": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da folha."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona da folha."
    },
    {
      "nome": "prefeitura",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona da folha.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "competencia",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Competencia em AAAAMM."
    },
    {
      "nome": "data_corte",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Data limite para novas averbacoes entrarem nesta folha."
    },
    {
      "nome": "data_repasse",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Data prevista do repasse. `null` quando nao definida."
    },
    {
      "nome": "status",
      "formato": "ENUM (aberta | fechada | consolidada)",
      "obrigatorio": true,
      "descricao": "Situacao da folha. Fechar abre sozinha a competencia seguinte; `consolidada` e' estado final."
    }
  ],
  "GET /v1/external/averbadora/beneficios": [
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador do beneficio."
    },
    {
      "nome": "unidadeId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeituraId` e o nome historico e continua saindo, com o mesmo valor. Note o camelCase."
    },
    {
      "nome": "prefeituraId",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura dona — o beneficio e' isolado por cidade. Note o camelCase."
    },
    {
      "nome": "organizacao",
      "formato": "OBJETO",
      "obrigatorio": false,
      "descricao": "Classificacao institucional da organizacao dona do beneficio.",
      "campos": [
        {
          "nome": "tipo",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Tipo institucional: `prefeitura`, `camara_municipal`, `assembleia_legislativa`, `tribunal`, `autarquia`, `instituto_previdencia`... `prefeitura` nas linhas ainda nao classificadas."
        },
        {
          "nome": "esfera",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "`municipal` | `estadual` | `federal`. `municipal` nas linhas ainda nao classificadas."
        },
        {
          "nome": "sigla",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Sigla institucional (`ALECE`). `null` quando a organizacao nao declarou uma."
        },
        {
          "nome": "nome_exibicao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "O nome que o SERVIDOR desta organizacao le. Sai do dominio, nunca de um `if` no cliente: fora do tipo `prefeitura` a sigla vence o nome, e o prefixo \"Prefeitura de \" nao alcanca quem nao e' prefeitura."
        }
      ]
    },
    {
      "nome": "nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome."
    },
    {
      "nome": "categorias",
      "formato": "LISTA de ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Categorias (saude, alimentacao, educacao, lazer, telemedicina)."
    },
    {
      "nome": "local",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Onde vale."
    },
    {
      "nome": "icone",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Emoji do card."
    },
    {
      "nome": "cor",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Cor de destaque, em hex."
    },
    {
      "nome": "descontoLabel",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Desconto, como texto. Note o camelCase."
    },
    {
      "nome": "descontoComplemento",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Complemento do desconto. Note o camelCase."
    },
    {
      "nome": "origem",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Quem disponibiliza: banco, averbadora, prefeitura ou convenio."
    },
    {
      "nome": "ativo",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Se esta disponivel aos servidores."
    },
    {
      "nome": "criadoEm",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Criacao (ISO 8601). Note o camelCase."
    },
    {
      "nome": "valorMensal",
      "formato": "NUMERICO",
      "obrigatorio": false,
      "descricao": "Mensalidade em reais. AUSENTE impede a contratacao — sem valor o beneficio nao vira desconto em folha, e o endpoint de contratar responde 400."
    }
  ],
  "GET /v1/external/averbadora/logs": [
    {
      "nome": "ts",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Momento do registro (ISO 8601, UTC)."
    },
    {
      "nome": "level",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nivel: debug, info, warn ou error."
    },
    {
      "nome": "source",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Modulo que registrou."
    },
    {
      "nome": "perfil",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Perfil envolvido (banco, prefeitura, servidor, averbadora)."
    },
    {
      "nome": "message",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Mensagem."
    },
    {
      "nome": "trace_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Correlaciona com a requisicao que gerou a linha."
    }
  ],
  "POST /v1/external/servidor/portabilidade/{id}/cancelar": [
    {
      "nome": "analise_manual",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Credora sem cadastro ativo: comprovante obrigatório e análise pela averbadora."
    },
    {
      "nome": "aceite_credora_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite da credora, registrado junto com o saldo de quitação."
    },
    {
      "nome": "fluxo_legado",
      "formato": "BOOLEANO",
      "obrigatorio": true,
      "descricao": "Operação anterior ao hotfix; mantém a continuidade do rito já iniciado."
    },
    {
      "nome": "versao",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Versão atual da proposta; envie no aceite do servidor."
    },
    {
      "nome": "condicoes_confirmadas_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Aceite explícito das condições pelo servidor, em ISO 8601."
    },
    {
      "nome": "valor_pago",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Valor pago à credora, em reais; separado do financiamento."
    },
    {
      "nome": "comprovante_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Nome do comprovante opcional do pagamento."
    },
    {
      "nome": "id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Identificador da portabilidade (`PT-...`)."
    },
    {
      "nome": "estado",
      "formato": "ENUM (aguardando_proposta | aguardando_servidor | solicitada | aguardando_pagamento | aguardando_liquidacao | concluida | cancelada | expirada)",
      "obrigatorio": true,
      "descricao": "Etapa do rito. Ver o anexo de estados de portabilidade."
    },
    {
      "nome": "quem_age",
      "formato": "ENUM (servidor | credora | proponente | averbadora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quem precisa agir para a etapa andar. `null` depois de encerrada."
    },
    {
      "nome": "papel",
      "formato": "ENUM (proponente | credora)",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Camada banco: o SEU papel nesta portabilidade. `null` nas outras camadas."
    },
    {
      "nome": "solicitado_por",
      "formato": "ENUM (proponente | servidor)",
      "obrigatorio": true,
      "descricao": "Quem abriu."
    },
    {
      "nome": "canal",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Canal de abertura: `portal_banco`, `api_externa` ou `servidor_app`."
    },
    {
      "nome": "banco_proponente_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco de DESTINO: abre, informa o pagamento e recebe o contrato."
    },
    {
      "nome": "banco_proponente_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do banco proponente."
    },
    {
      "nome": "banco_credora_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Banco que tem a divida hoje: informa o saldo e liquida."
    },
    {
      "nome": "banco_credora_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da credora."
    },
    {
      "nome": "convenio_id",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Convenio do PROPONENTE na folha do servidor — e' nele que o contrato destino nasce."
    },
    {
      "nome": "unidade_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Identificador da unidade pagadora. `prefeitura_id` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_id",
      "formato": "NUMERICO inteiro",
      "obrigatorio": true,
      "descricao": "Prefeitura (unidade pagadora) da matricula."
    },
    {
      "nome": "unidade_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da unidade pagadora. `prefeitura_nome` e o nome historico e continua saindo, com o mesmo valor."
    },
    {
      "nome": "prefeitura_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome da prefeitura."
    },
    {
      "nome": "organizacao_tipo",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "descricao": "Tipo institucional da organizacao desta linha (`prefeitura`, `camara_municipal`, `autarquia`...). `prefeitura` nas linhas ainda nao classificadas."
    },
    {
      "nome": "organizacao_sigla",
      "formato": "ALFANUMERICO",
      "obrigatorio": false,
      "nulavel": true,
      "descricao": "Sigla institucional da organizacao desta linha. `null` quando ela nao declarou uma."
    },
    {
      "nome": "matricula",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Matricula do servidor."
    },
    {
      "nome": "servidor_nome",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Nome do servidor."
    },
    {
      "nome": "cpf_masked",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "CPF mascarado (LGPD)."
    },
    {
      "nome": "origem",
      "formato": "OBJETO",
      "obrigatorio": true,
      "descricao": "O contrato portado.",
      "campos": [
        {
          "nome": "adf",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "descricao": "Contrato que sai da credora."
        },
        {
          "nome": "parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela da origem na abertura, em reais."
        },
        {
          "nome": "saldo_devedor",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Saldo devedor registrado na averbadora na abertura, em reais."
        },
        {
          "nome": "total_parcelas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Prazo da origem."
        },
        {
          "nome": "parcelas_pagas",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Parcelas ja descontadas."
        },
        {
          "nome": "situacao",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Situacao atual do contrato de origem (`Quitado` depois da liquidacao)."
        }
      ]
    },
    {
      "nome": "saldo_devedor_informado",
      "formato": "NUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Saldo para quitacao informado pela credora, em reais. `null` ate a etapa de saldo."
    },
    {
      "nome": "saldo_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando a credora informou o saldo (ISO 8601)."
    },
    {
      "nome": "condicoes",
      "formato": "OBJETO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Condicoes do destino informadas na proposta antes do aceite. `null` antes disso.",
      "campos": [
        {
          "nome": "valor_financiado",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Valor do contrato destino, em reais. Cobre o saldo informado."
        },
        {
          "nome": "valor_liquido",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Liberado ao servidor alem da quitacao, em reais."
        },
        {
          "nome": "valor_parcela",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "Parcela do contrato destino, em reais."
        },
        {
          "nome": "prazo",
          "formato": "NUMERICO inteiro",
          "obrigatorio": true,
          "descricao": "Prazo do contrato destino, em meses."
        },
        {
          "nome": "taxa_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Juros mensais em FRACAO, quando informados."
        },
        {
          "nome": "cet_am",
          "formato": "NUMERICO",
          "obrigatorio": true,
          "descricao": "CET mensal em FRACAO — sujeito ao teto de CET do convenio."
        },
        {
          "nome": "numero_contrato_banco",
          "formato": "ALFANUMERICO",
          "obrigatorio": true,
          "nulavel": true,
          "descricao": "Numero do contrato destino no sistema do proponente."
        }
      ]
    },
    {
      "nome": "pagamento_informado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Quando o proponente informou o pagamento (ISO 8601)."
    },
    {
      "nome": "adf_destino",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Contrato criado na liquidacao, ja averbado no proponente."
    },
    {
      "nome": "motivo",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Motivo de cancelamento ou expiracao."
    },
    {
      "nome": "expira_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Fim do prazo da etapa atual (ISO 8601). `null` depois de encerrada."
    },
    {
      "nome": "criado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Abertura (ISO 8601)."
    },
    {
      "nome": "atualizado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "descricao": "Ultima mudanca (ISO 8601)."
    },
    {
      "nome": "encerrado_em",
      "formato": "ALFANUMERICO",
      "obrigatorio": true,
      "nulavel": true,
      "descricao": "Conclusao, cancelamento ou expiracao (ISO 8601)."
    }
  ],
};

export interface CodigoErroDoc {
  codigo: string;
  status: number;
  origem: "middleware" | "handler" | "dominio";
  quando: string;
  acao: string;
}

/**
 * Todo `error.code` da camada banco. Conferido contra o codigo-fonte por
 * banco.anexos.test.ts: codigo emitido por um handler e nao catalogado aqui
 * derruba o teste.
 */
export const CODIGOS_ERRO_POR_CAMADA: Record<string, CodigoErroDoc[]> = {
  "banco": [
    {
      "codigo": "banco_portabilidade_atrasada",
      "status": 422,
      "origem": "dominio",
      "quando": "O banco tem etapa de portabilidade vencida sob sua responsabilidade. Novas averbações, reservas e confirmações ficam bloqueadas.",
      "acao": "Regularize todas as etapas bancárias vencidas. O desbloqueio é automático. A credora aceita informando o saldo na mesma ação ou recusa com motivo."
    },
    {
      "codigo": "condicoes_nao_aceitas",
      "status": 422,
      "origem": "dominio",
      "quando": "O pedido ainda não tem aceite explícito das condições pelo servidor.",
      "acao": "Apresente a proposta e aguarde o servidor aceitar antes de continuar."
    },
    {
      "codigo": "proposta_ja_aceita",
      "status": 409,
      "origem": "dominio",
      "quando": "Tentativa de alterar condições que o servidor já aceitou.",
      "acao": "Cancele e apresente uma nova proposta se as condições precisarem mudar."
    },
    {
      "codigo": "proposta_desatualizada",
      "status": 409,
      "origem": "dominio",
      "quando": "A versão exibida ao servidor difere da versão atual da proposta.",
      "acao": "Recarregue o detalhe, exiba novamente as condições e envie a versão atual no aceite."
    },
    {
      "codigo": "pagamento_ausente",
      "status": 422,
      "origem": "dominio",
      "quando": "Não existe valor pago registrado antes da liquidação.",
      "acao": "Registre o pagamento à credora antes de confirmar a liquidação."
    },
    {
      "codigo": "analise_manual",
      "status": 409,
      "origem": "dominio",
      "quando": "A portabilidade está sob análise manual da averbadora (credora fora de operação), e a ação pedida cabe à averbadora.",
      "acao": "Aguarde a análise do comprovante; acompanhe por GET /portabilidade/{id} ou pelo webhook."
    },
    {
      "codigo": "analise_invalida",
      "status": 409,
      "origem": "dominio",
      "quando": "A análise manual foi pedida sobre um comprovante que mudou ou que não está mais pendente de análise.",
      "acao": "Recarregue a portabilidade e repita a ação com a versão e o comprovante atuais."
    },
    {
      "codigo": "unauthorized",
      "status": 401,
      "origem": "middleware",
      "quando": "Header `Authorization` ausente, token inexistente/revogado, store de tokens indisponivel, ou banco pausado pela averbadora.",
      "acao": "Confira o header `Authorization: Bearer atl_…`. A mensagem distingue os quatro casos — banco pausado nao se resolve com token novo, e' liberacao da averbadora."
    },
    {
      "codigo": "forbidden",
      "status": 403,
      "origem": "middleware",
      "quando": "Token de outra camada (audience) ou sem o escopo que o endpoint exige.",
      "acao": "A mensagem diz qual escopo falta. Peca a averbadora um token com o escopo, ou use o endpoint da camada correta."
    },
    {
      "codigo": "not_found",
      "status": 404,
      "origem": "handler",
      "quando": "Convenio, colaborador, contrato ou tabela nao existe — ou existe e pertence a outro banco.",
      "acao": "Nao distinga 'nao existe' de 'nao e seu': o isolamento por banco e' deliberado e responde 404 nos dois casos."
    },
    {
      "codigo": "validation_error",
      "status": 400,
      "origem": "handler",
      "quando": "Corpo ou query invalidos: campo faltando, tipo errado, valor fora do limite, prazo acima do teto do convenio, situacao que nao permite a operacao.",
      "acao": "Leia `error.details` — e' um mapa campo -> motivo. Recusa por regra de negocio tambem cai aqui, nao em 422: teto de parcelas do convenio, por exemplo."
    },
    {
      "codigo": "transicao_invalida",
      "status": 409,
      "origem": "dominio",
      "quando": "A acao pedida nao existe para a situacao atual do contrato (aprovar um contrato ja quitado, confirmar um cancelado).",
      "acao": "Releia a situacao atual antes de repetir. Retry nao muda o resultado — ver o anexo de acoes para as transicoes permitidas."
    },
    {
      "codigo": "previa_desatualizada",
      "status": 409,
      "origem": "dominio",
      "quando": "A confirmacao da importacao mensal referencia uma previa cujo hash nao corresponde mais ao arquivo enviado.",
      "acao": "Gere uma nova previa com o arquivo atual e confirme usando o hash devolvido por ela."
    },
    {
      "codigo": "reenvio_sem_snapshot",
      "status": 409,
      "origem": "dominio",
      "quando": "Reenvio de contrato em falha de folha sem a situacao anterior registrada — nao ha estado para restaurar.",
      "acao": "Use outra acao de tratamento (cancelar ou renegociar) em vez de reenviar."
    },
    {
      "codigo": "margem_insuficiente",
      "status": 422,
      "origem": "dominio",
      "quando": "A parcela resultante excede a margem disponivel do servidor no bucket do contrato.",
      "acao": "Consulte `GET /margem` antes de enviar e reduza o valor ou aumente o prazo. A margem conta proposta em analise, nao so contrato ativo."
    },
    {
      "codigo": "cartao_em_manutencao",
      "status": 503,
      "origem": "dominio",
      "quando": "A reserva e o lancamento de cartao (`/v1/external/banco/cartoes/*`) estao temporariamente indisponiveis.",
      "acao": "Nao repita em loop. Enquanto isso, averbe o cartao por `POST /contratos/averbar` com `tipo_contrato: \"ECONSIGNADO\"`, como antes."
    },
    {
      "codigo": "valor_acima_disponivel",
      "status": 422,
      "origem": "dominio",
      "quando": "O valor pedido para a reserva de cartao passa da margem disponivel no balde do cartao (credito consignado ou beneficio).",
      "acao": "Omita `valor` para reservar o disponivel inteiro, ou consulte `GET /margem` e envie um valor ate o disponivel do balde."
    },
    {
      "codigo": "reserva_ja_ativa",
      "status": 422,
      "origem": "dominio",
      "quando": "Seu banco ja tem uma reserva de cartao ativa deste produto para o servidor (mesma matricula, mesma folha). Uma reserva ativa por banco, vinculo e produto.",
      "acao": "Informe os lancamentos na reserva existente (`GET /cartoes/reservas?matricula=` traz a ADF). Para mudar o valor reservado, cancele a reserva pela acao `cancelar` e reserve de novo."
    },
    {
      "codigo": "reserva_inativa",
      "status": 422,
      "origem": "dominio",
      "quando": "A reserva de cartao foi cancelada, quitada ou saiu de operacao: nao aceita mais lancamento.",
      "acao": "Nao reenvie. Se o cartao continua valendo, crie uma reserva nova e lance nela."
    },
    {
      "codigo": "valor_acima_reservado",
      "status": 422,
      "origem": "dominio",
      "quando": "O lancamento da competencia passa da parcela contratada na reserva do cartao (`valor_parcela`).",
      "acao": "Envie ate o `valor_parcela` da reserva. O que passar dele e' cobrado pelo banco fora da folha."
    },
    {
      "codigo": "competencia_anterior_a_reserva",
      "status": 422,
      "origem": "dominio",
      "quando": "A competencia do lancamento e' anterior a `competencia_inicial` da reserva.",
      "acao": "Lance a partir de `competencia_inicial`, que vem em `GET /cartoes/reservas/{adf}`."
    },
    {
      "codigo": "competencia_congelada",
      "status": 422,
      "origem": "dominio",
      "quando": "O corte da competencia ja passou, ou a folha dela ja fechou: o movimento daquela competencia nao muda mais, e o lancamento nao pode ser criado, alterado nem cancelado.",
      "acao": "Lance na proxima competencia aberta. O corte e' o do orgao ou entidade, o mesmo que congela o movimento."
    },
    {
      "codigo": "parcelas_esgotadas",
      "status": 422,
      "origem": "dominio",
      "quando": "Todas as parcelas da reserva de cartao ja' foram lancadas: o prazo dela (`parcelas_total`) acabou.",
      "acao": "Nao lance mais nesta reserva. Reenviar uma competencia ja' lancada continua valendo; para continuar descontando, crie uma reserva nova."
    },
    {
      "codigo": "acao_invalida_reserva_cartao",
      "status": 422,
      "origem": "handler",
      "quando": "A acao pedida nao se aplica a reserva de cartao: ela nao tem prazo, parcela nem aprovacao, e so' sai de operacao.",
      "acao": "Numa reserva de cartao use apenas `cancelar`, `quitar` ou `suspender`. O desconto do mes e' o lancamento, em `/cartoes/reservas/{adf}/lancamentos`."
    },
    {
      "codigo": "reserva_cartao_nao_edita",
      "status": 422,
      "origem": "handler",
      "quando": "Tentativa de editar valores de uma reserva de cartao pelo portal. A edicao recalcula CET e parcela, que a reserva nao tem.",
      "acao": "Para mudar o valor reservado, cancele a reserva e reserve de novo."
    },
    {
      "codigo": "vinculo_bloqueado",
      "status": 422,
      "origem": "dominio",
      "quando": "O orgao ou a averbadora registrou bloqueio ativo para a folha e matricula informadas.",
      "acao": "Consulte a situacao do vinculo e solicite ao orgao ou a averbadora a resolucao do bloqueio antes de reenviar."
    },
    {
      "codigo": "servidor_nao_averbavel",
      "status": 422,
      "origem": "dominio",
      "quando": "A folha declarou o servidor cedido ou desligado. Nao ha holerite onde descontar, e a averbacao fica fechada ate a folha declara-lo ativo. Licenca NAO fecha: a margem sai do salario informado. `details.situacao` traz a situacao e `message` o motivo.",
      "acao": "Nao reenvie com outro valor: nenhum valor passa. Consulte `GET /margem` ou a ficha do servidor para ver a situacao, e aguarde a proxima carga da folha."
    },
    {
      "codigo": "margem_pendente_classificacao",
      "status": 422,
      "origem": "dominio",
      "quando": "O servidor tem desconto saindo do contracheque cuja rubrica ainda nao foi classificada nesta folha, e a averbadora ativou o bloqueio para ela. E' diferente de `margem_insuficiente`: ali a conta foi feita e nao coube; aqui a conta nao pode ser afirmada, porque o produto do desconto — e portanto o bucket que ele ocupa — e' desconhecido.",
      "acao": "Reduzir valor ou prazo NAO resolve: nenhum valor passa enquanto durar a pendencia. `GET /margem` traz `pendente_classificacao` com `valor_pendente` e `trava_ativa`. A liberacao depende da classificacao da rubrica pela averbadora, e o bloqueio vale so' para aquele vinculo (matricula naquela folha)."
    },
    {
      "codigo": "cet_acima_do_teto",
      "status": 422,
      "origem": "dominio",
      "quando": "O CET a.m. (`taxa_am`) passa do teto de CET que a prefeitura definiu para o convenio. Vale para emprestimo, refinanciamento, portabilidade, tabelas e ofertas; cartao (`ECONSIGNADO`) nao passa por este teto.",
      "acao": "Leia `taxa_max_am_pct` em `GET /convenios` — e' o teto de CET, em percentual. Quando vem `null`, o convenio nao tem teto configurado."
    },
    {
      "codigo": "cet_acima_da_tabela",
      "status": 422,
      "origem": "dominio",
      "quando": "No refinanciamento, o CET passa do CET que a SUA tabela publica para aquele prazo. Averbacao e edicao nao conferem mais a tabela (30/09/2026): ali so' o teto do convenio trava o CET.",
      "acao": "Reduza o CET ao da tabela, ou republique a tabela (dentro do teto do convenio)."
    },
    {
      "codigo": "cet_abaixo_do_minimo",
      "status": 422,
      "origem": "dominio",
      "quando": "CET a.m. abaixo de 0,5% — quase sempre erro de unidade ou digitacao (`taxa_am` e' FRACAO: 0.0179, nao 0.00179).",
      "acao": "Confira a conversao do percentual para fracao e reenvie."
    },
    {
      "codigo": "cet_abaixo_dos_juros",
      "status": 422,
      "origem": "dominio",
      "quando": "Na edicao, o CET (`taxa_am`) veio menor que os juros (`juros_am`). O CET inclui os juros e nunca fica abaixo deles.",
      "acao": "Confira se os dois campos nao foram trocados. `juros_am` e' opcional — se nao tiver o dado, omita."
    },
    {
      "codigo": "prazo_fora_da_tabela",
      "status": 422,
      "origem": "dominio",
      "quando": "Na averbacao ou na edicao, o prazo fica fora da faixa permitida: abaixo do menor prazo das suas tabelas ativas no convenio, ou acima do menor entre o teto do convenio e o maior prazo das tabelas. Sem tabela ativa, vale de 1x ate o teto do convenio.",
      "acao": "Escolha um prazo dentro da faixa informada na mensagem, ou publique uma tabela que cubra o prazo em `POST /tabelas`."
    },
    {
      "codigo": "numero_contrato_em_uso",
      "status": 422,
      "origem": "dominio",
      "quando": "O `numero_contrato_banco` enviado ja esta em outro contrato seu. Cada contrato precisa de um numero proprio, inclusive frente a contratos cancelados.",
      "acao": "Envie o numero correto do contrato no seu sistema. A `message` traz a ADF do contrato que ja usa o numero."
    },
    {
      "codigo": "calculo_invalido",
      "status": 422,
      "origem": "dominio",
      "quando": "A combinacao de valor, prazo e taxa nao produz parcela valida — tipicamente taxa zero ou negativa.",
      "acao": "Reveja os tres campos. Lembre que `taxa_am` e' FRACAO: 0.0179, nao 1.79."
    },
    {
      "codigo": "refin_inelegivel",
      "status": 422,
      "origem": "dominio",
      "quando": "Um contrato de origem nao pode ser refinanciado agora: nao e' seu, nao esta ativo, nao cumpriu as parcelas pagas que o convenio exige, ou esta em outra negociacao ou portabilidade aberta. A `message` traz o contrato e o motivo.",
      "acao": "Nao reenvie o mesmo conjunto: retire o contrato apontado, ou aguarde a condicao (parcelas pagas, fim da outra negociacao)."
    },
    {
      "codigo": "refinanciamento_em_andamento",
      "status": 409,
      "origem": "dominio",
      "quando": "Outro refinanciamento aberto ja' prende um dos contratos de origem.",
      "acao": "Liste `GET /refinanciamentos?estado=solicitado` ou `ofertado` e responda ou cancele a negociacao existente antes de abrir outra."
    },
    {
      "codigo": "refin_troco_invalido",
      "status": 422,
      "origem": "dominio",
      "quando": "O valor financiado nao cobre os saldos de quitacao, ou gera troco que o convenio nao permite (ou abaixo do troco minimo).",
      "acao": "Ajuste `valor_financiado` ou `saldos_quitacao`. As regras do convenio definem se ha troco e qual o minimo."
    },
    {
      "codigo": "refin_expirado",
      "status": 422,
      "origem": "dominio",
      "quando": "A validade do pedido ou da oferta venceu antes do aceite.",
      "acao": "A negociacao foi encerrada como `expirado`. Abra uma nova, se ainda fizer sentido, com `POST /refinanciamentos`."
    },
    {
      "codigo": "convenio_invalido",
      "status": 422,
      "origem": "dominio",
      "quando": "Refinanciamento: o convenio das origens nao esta mais ativo no aceite. Portabilidade: o banco de destino nao tem convenio ativo na folha do servidor.",
      "acao": "Nao ha como gerar o contrato sem convenio ativo. Fale com a averbadora para (re)ativa-lo."
    },
    {
      "codigo": "rate_limited",
      "status": 429,
      "origem": "middleware",
      "quando": "Excesso de requisicoes na janela do rate limit.",
      "acao": "Respeite `error.details.reset_in` (segundos) antes de tentar de novo. E' o unico codigo em que retry com espera e' a resposta certa."
    },
    {
      "codigo": "bank_unavailable",
      "status": 503,
      "origem": "middleware",
      "quando": "Dependencia bancaria indisponivel.",
      "acao": "Retry com backoff. Se persistir, abra chamado com o `trace_id` da resposta."
    },
    {
      "codigo": "contrato_inelegivel",
      "status": 422,
      "origem": "dominio",
      "quando": "O contrato nao pode ser origem de portabilidade agora: nao esta ativo em folha, nao e' emprestimo, ja foi quitado, esta em refinanciamento, ou deixou de estar ativo antes da liquidacao. A `message` traz o motivo.",
      "acao": "Nao repita o mesmo `adf`: consulte `GET /portabilidade/elegiveis`, que traz o motivo de cada contrato que nao pode ser portado."
    },
    {
      "codigo": "autoportabilidade",
      "status": 422,
      "origem": "dominio",
      "quando": "O banco de destino ja e' o credor do contrato. Trocar condicoes com o proprio credor nao e' portabilidade.",
      "acao": "Para renegociar com o mesmo banco, use o refinanciamento. Para portar, escolha outro banco de destino."
    },
    {
      "codigo": "portabilidade_em_andamento",
      "status": 409,
      "origem": "dominio",
      "quando": "Ja existe portabilidade aberta sobre este contrato — ou, num `quitar`, o contrato tem portabilidade aberta e so' sai pela liquidacao do rito.",
      "acao": "Liste `GET /portabilidade` e acompanhe (ou cancele, se for sua como proponente) a existente. A divida com portabilidade aberta e' quitada por `POST /portabilidade/{id}/liquidar`."
    },
    {
      "codigo": "nao_autorizado",
      "status": 403,
      "origem": "dominio",
      "quando": "A etapa nao e' do seu papel nesta portabilidade: so' a credora informa o saldo e liquida; so' o proponente informa o pagamento; a credora nao cancela.",
      "acao": "Confira `papel` e `quem_age` em `GET /portabilidade/{id}` antes de agir. Nao e' falha de token nem de escopo."
    },
    {
      "codigo": "portabilidade_expirada",
      "status": 422,
      "origem": "dominio",
      "quando": "O prazo de confirmação do servidor venceu. A portabilidade foi encerrada como expirada; atraso bancário mantém a pendência operável.",
      "acao": "Nao ha como retomar: se ainda fizer sentido, o proponente abre uma nova com `POST /portabilidade`."
    },
    {
      "codigo": "saldo_nao_coberto",
      "status": 422,
      "origem": "dominio",
      "quando": "O `valor_financiado` do pagamento e' menor que o saldo devedor informado pela credora.",
      "acao": "O contrato destino precisa quitar a origem: envie `valor_financiado` >= `saldo_devedor_informado`."
    },
    {
      "codigo": "margem_insuficiente_na_projecao",
      "status": 422,
      "origem": "dominio",
      "quando": "A parcela do destino nao cabe na margem do servidor nem contando a parcela da origem, que volta a ficar livre na liquidacao.",
      "acao": "Reduza `valor_parcela` (prazo maior ou valor menor). A `message` traz a margem livre e a parcela da origem."
    },
    {
      "codigo": "condicoes_ausentes",
      "status": 422,
      "origem": "dominio",
      "quando": "Liquidacao pedida sem as condicoes do destino gravadas.",
      "acao": "Estado inconsistente: abra chamado com o `trace_id` e o `id` da portabilidade."
    },
    {
      "codigo": "internal_error",
      "status": 500,
      "origem": "middleware",
      "quando": "Falha nao tratada do nosso lado. Inclui resposta que nao casou com o schema de saida do endpoint.",
      "acao": "Nao repita o mesmo request em laco. Abra chamado com o `trace_id` — sem ele a investigacao comeca por adivinhacao."
    }
  ],
  "prefeitura": [
    {
      "codigo": "unauthorized",
      "status": 401,
      "origem": "middleware",
      "quando": "Header `Authorization` ausente, token inexistente/revogado, ou store de tokens indisponivel.",
      "acao": "Confira o header `Authorization: Bearer atl_…`. A mensagem distingue os casos."
    },
    {
      "codigo": "forbidden",
      "status": 403,
      "origem": "middleware",
      "quando": "Token de outra camada (audience) ou sem o escopo que o endpoint exige.",
      "acao": "A mensagem diz qual escopo falta. Escopos de escrita (`:write`) sao separados dos de leitura de proposito — confirmar ADF e importar base nao deveriam caber num token de consulta."
    },
    {
      "codigo": "not_found",
      "status": 404,
      "origem": "handler",
      "quando": "Folha, lote ou prefeitura inexistente — ou pertencente a OUTRA prefeitura.",
      "acao": "O isolamento por prefeitura responde 404 nos dois casos, e nao 403: confirmar que o id existe ja seria vazamento. Um 404 num id que voce tem certeza que existe costuma significar que ele e' de outra prefeitura."
    },
    {
      "codigo": "validation_error",
      "status": 400,
      "origem": "handler",
      "quando": "Corpo ou query invalidos: CSV vazio, competencia fora do formato AAAAMM, folha da competencia ja existente, ou fechamento de folha sem nenhuma movimentacao.",
      "acao": "Leia `error.details` — e' um mapa campo -> motivo. O caso mais comum e' fechar folha sem movimentacao: se nao houve mudanca de pessoal no mes, importe uma linha com `tipo=desconto`."
    },
    {
      "codigo": "rate_limited",
      "status": 429,
      "origem": "middleware",
      "quando": "Excesso de requisicoes na janela do rate limit.",
      "acao": "Respeite `error.details.reset_in` (segundos). Importacao de base grande deve ser UMA chamada com o CSV inteiro, nao uma chamada por servidor."
    },
    {
      "codigo": "internal_error",
      "status": 500,
      "origem": "middleware",
      "quando": "Falha nao tratada do nosso lado. Inclui resposta que nao casou com o schema de saida do endpoint.",
      "acao": "Nao repita o mesmo request em laco — em endpoint de escrita, repetir pode duplicar efeito. Abra chamado com o `trace_id`."
    },
    {
      "codigo": "prefeitura_nao_encontrada",
      "status": 404,
      "origem": "handler",
      "quando": "`?prefeitura_id` (ou `prefeitura_id` no corpo de POST /folhas) aponta para uma organizacao que nao existe, ou que este token nao alcanca.",
      "acao": "Os dois casos respondem IGUAL de proposito: distinguir 'nao existe' de 'existe e nao e' sua' permitiria varrer ids e mapear quantas organizacoes a Atlas atende. Confira a lista em `GET /me` — so' as organizacoes com relacao institucional vigente sao alcancaveis."
    },
    {
      "codigo": "permissao_insuficiente",
      "status": 403,
      "origem": "handler",
      "quando": "A organizacao esta no escopo, mas o PAPEL da relacao nao libera este recurso neste modo — por exemplo, quem apenas administra as consignacoes tentando abrir competencia.",
      "acao": "403, e nao 404, porque a essa altura voce ja provou alcancar a organizacao: `error.details` traz `recurso`, `modo` e os `papeis` que voce exerce nela. Mudar isso e' decisao da averbadora, na relacao institucional."
    },
    {
      "codigo": "unidade_obrigatoria",
      "status": 400,
      "origem": "handler",
      "quando": "`POST /folhas` sem `prefeitura_id` quando o token alcanca mais de uma organizacao.",
      "acao": "Escolha a unidade: `error.details.unidades` traz id, nome e os papeis de cada uma. Nao ha default silencioso — abrir folha na organizacao errada e' desconto que nao chega em quem devia."
    }
  ],
  "servidor": [
    {
      "codigo": "condicoes_ausentes",
      "status": 422,
      "origem": "dominio",
      "quando": "O banco ainda não apresentou as condições contratuais da proposta.",
      "acao": "Aguarde o banco apresentar a proposta antes do aceite do servidor."
    },
    {
      "codigo": "proposta_desatualizada",
      "status": 409,
      "origem": "dominio",
      "quando": "A versão exibida ao servidor difere da versão atual da proposta.",
      "acao": "Recarregue o detalhe, exiba novamente as condições e envie a versão atual no aceite."
    },
    {
      "codigo": "unauthorized",
      "status": 401,
      "origem": "middleware",
      "quando": "DUAS coisas distintas: token ausente/invalido/revogado (middleware), OU senha de transacao do servidor incorreta, inexistente ou de outra pessoa (operacoes de aceite e proposta).",
      "acao": "Leia a mensagem antes de agir. `API token ausente/invalido` e' credencial da aplicacao; `Senha de transacao incorreta` e' credencial da PESSOA — emitir token novo nao resolve o segundo caso."
    },
    {
      "codigo": "forbidden",
      "status": 403,
      "origem": "middleware",
      "quando": "Token de outra camada, escopo insuficiente, ou operacao sobre contrato que nao pertence a nenhuma matricula deste CPF.",
      "acao": "Escopo aparece na mensagem. Para o caso do contrato, confira que o `adf` sai de `GET /contratos` deste mesmo token."
    },
    {
      "codigo": "not_found",
      "status": 404,
      "origem": "handler",
      "quando": "Servidor do token inexistente, matricula que nao pertence ao CPF, averbacao/intencao/beneficio/termo inexistente, ou emprestimo externo nao encontrado no tombamento.",
      "acao": "Um 404 em `matricula` costuma significar que ela e' de outro CPF — confira em `GET /matriculas`, que lista todas as do titular."
    },
    {
      "codigo": "validation_error",
      "status": 400,
      "origem": "handler",
      "quando": "Corpo invalido, ou a operacao nao cabe no estado atual: averbacao que nao esta pendente de confirmacao, contrato que nao esta ativo para portar, beneficio pausado ou sem valor mensal, e ja existir emprestimo ou portabilidade em analise.",
      "acao": "Leia `error.details`. A recusa mais comum nao e' de formato — e' de estado: so ha UMA operacao de emprestimo em analise por matricula de cada vez."
    },
    {
      "codigo": "proposta_em_andamento",
      "status": 422,
      "origem": "dominio",
      "quando": "Ja existe proposta em analise para a matricula, e uma nova consumiria margem que ja esta reservada.",
      "acao": "Acompanhe ou cancele a proposta anterior antes de criar outra. Codigo proprio justamente para voce distinguir isto de um corpo malformado."
    },
    {
      "codigo": "cet_acima_do_teto",
      "status": 422,
      "origem": "dominio",
      "quando": "O CET a.m. enviado em `taxa_am` excede o teto de CET que a prefeitura definiu para o convenio da matricula. O teto e' da PREFEITURA, nao do banco, e limita o CET — juros, IOF, tarifas e seguro juntos — em emprestimo, portabilidade e refinanciamento.",
      "acao": "Use o CET que veio na simulacao ou nas ofertas: ele ja respeita o teto. A mensagem traz o valor recusado e o teto aplicado, nesta ordem."
    },
    {
      "codigo": "condicao_indisponivel",
      "status": 422,
      "origem": "dominio",
      "quando": "A combinacao de prazo e CET enviada nao e' uma condicao publicada pelo banco numa tabela vigente do convenio — ou nenhuma tabela cobre o prazo.",
      "acao": "Refaca a escolha a partir de `GET /ofertas`: envie exatamente o prazo e o `taxa_am` de uma das opcoes. A mensagem traz o CET vigente para o prazo pedido."
    },
    {
      "codigo": "margem_insuficiente",
      "status": 422,
      "origem": "dominio",
      "quando": "A parcela excede a margem disponivel do bucket. Conta proposta em analise e consignado em outro banco informado pela prefeitura.",
      "acao": "Consulte `GET /margem` antes de enviar e respeite `margens[].disponivel`. Simular NAO reserva: entre a simulacao e a proposta, outra operacao pode consumir a folga."
    },
    {
      "codigo": "vinculo_bloqueado",
      "status": 422,
      "origem": "dominio",
      "quando": "O orgao ou a averbadora registrou bloqueio ativo para esta folha e matricula.",
      "acao": "Mostre o motivo ao servidor e aguarde a liberacao administrativa do vinculo."
    },
    {
      "codigo": "servidor_nao_averbavel",
      "status": 422,
      "origem": "dominio",
      "quando": "A folha declarou o servidor cedido ou desligado. Nao ha holerite onde descontar, e a averbacao fica fechada ate a folha declara-lo ativo. Licenca NAO fecha: a margem sai do salario informado. `details.situacao` traz a situacao e `message` o motivo.",
      "acao": "Nao reenvie com outro valor: nenhum valor passa. Mostre o motivo ao servidor e aguarde a proxima carga da folha."
    },
    {
      "codigo": "margem_pendente_classificacao",
      "status": 422,
      "origem": "dominio",
      "quando": "O servidor tem desconto saindo do contracheque cuja rubrica ainda nao foi classificada nesta folha, e a averbadora ativou o bloqueio para ela. Nao e' 'nao cabe': e' 'nao da' pra saber se cabe' — enquanto o produto do desconto e' desconhecido, o teto ocupado do bucket tambem e'.",
      "acao": "Nao adianta reduzir o valor: nenhum valor passa enquanto durar a pendencia. `GET /margem` traz `pendente_classificacao` com o valor envolvido. A liberacao depende da classificacao da rubrica pela averbadora, e vale so' para o vinculo (matricula naquela folha) — as outras matriculas do mesmo servidor seguem operando."
    },
    {
      "codigo": "cet_abaixo_do_minimo",
      "status": 422,
      "origem": "dominio",
      "quando": "CET a.m. abaixo de 0,5% — quase sempre erro de unidade ou de digitacao (`taxa_am` e' FRACAO: 0.0179, nao 1.79 nem 0.00179).",
      "acao": "Confira a conversao do percentual para fracao antes de reenviar. Use o `taxa_am` que veio em `GET /ofertas`."
    },
    {
      "codigo": "rate_limited",
      "status": 429,
      "origem": "middleware",
      "quando": "Excesso de requisicoes na janela.",
      "acao": "Respeite `error.details.reset_in` (segundos) antes de tentar de novo."
    },
    {
      "codigo": "use_portabilidade",
      "status": 422,
      "origem": "dominio",
      "quando": "Proposta com `tipo: portabilidade`. Portabilidade nao nasce como proposta: tem rito proprio.",
      "acao": "Abra com `POST /portabilidade` (contrato + banco de destino). O contrato novo nasce na liquidacao pela credora."
    },
    {
      "codigo": "transicao_invalida",
      "status": 409,
      "origem": "dominio",
      "quando": "A portabilidade ja saiu da etapa em que a acao cabe (ex.: confirmar o que ja foi confirmado, cancelar o que ja encerrou).",
      "acao": "Releia `GET /portabilidade/{id}`: `estado` e `quem_age` dizem o que cabe agora."
    },
    {
      "codigo": "contrato_inelegivel",
      "status": 422,
      "origem": "dominio",
      "quando": "O contrato nao pode ser portado agora (nao esta ativo em folha, nao e' emprestimo, ou esta em outra operacao). A `message` traz o motivo.",
      "acao": "Consulte `GET /portabilidade/elegiveis`, que traz o motivo de cada contrato."
    },
    {
      "codigo": "autoportabilidade",
      "status": 422,
      "origem": "dominio",
      "quando": "O banco de destino escolhido ja e' o credor do contrato.",
      "acao": "Escolha outro banco em `bancos_destino`. Para renegociar com o mesmo banco, use o refinanciamento."
    },
    {
      "codigo": "convenio_invalido",
      "status": 422,
      "origem": "dominio",
      "quando": "O banco de destino nao tem convenio ativo na folha do servidor.",
      "acao": "Escolha um banco listado em `bancos_destino` de `GET /portabilidade/elegiveis`."
    },
    {
      "codigo": "portabilidade_em_andamento",
      "status": 409,
      "origem": "dominio",
      "quando": "Ja existe portabilidade aberta sobre este contrato.",
      "acao": "Acompanhe a existente em `GET /portabilidade`, ou cancele-a antes de abrir outra."
    },
    {
      "codigo": "portabilidade_expirada",
      "status": 422,
      "origem": "dominio",
      "quando": "O prazo da etapa venceu antes da confirmacao. A portabilidade foi encerrada como `expirada`.",
      "acao": "Se ainda fizer sentido, abra uma nova com `POST /portabilidade`."
    },
    {
      "codigo": "nao_autorizado",
      "status": 403,
      "origem": "dominio",
      "quando": "Acao que nao cabe ao servidor nesta portabilidade.",
      "acao": "O servidor confirma e cancela; saldo, pagamento e liquidacao sao dos bancos."
    },
    {
      "codigo": "internal_error",
      "status": 500,
      "origem": "middleware",
      "quando": "Falha nao tratada do nosso lado. Inclui resposta que nao casou com o schema de saida do endpoint.",
      "acao": "Nao repita em laco operacao de escrita — pode duplicar efeito. Abra chamado com o `trace_id`."
    }
  ],
  "averbadora": [
    {
      "codigo": "unauthorized",
      "status": 401,
      "origem": "middleware",
      "quando": "Header `Authorization` ausente, token inexistente/revogado, ou store de tokens indisponivel.",
      "acao": "Confira o header `Authorization: Bearer atl_…`."
    },
    {
      "codigo": "forbidden",
      "status": 403,
      "origem": "middleware",
      "quando": "Token de outra camada, ou sem o escopo que o endpoint exige.",
      "acao": "A mensagem diz qual escopo falta. Esta camada opera o sistema inteiro — conceda escopo de escrita so ao token que precisa escrever."
    },
    {
      "codigo": "not_found",
      "status": 404,
      "origem": "handler",
      "quando": "Banco, prefeitura, convenio, contrato, folha, servidor, comunicado, termo ou beneficio inexistente.",
      "acao": "Diferente das outras camadas, aqui 404 significa mesmo que NAO EXISTE — nao ha isolamento por parceiro escondendo o registro de voce."
    },
    {
      "codigo": "validation_error",
      "status": 400,
      "origem": "handler",
      "quando": "Corpo invalido, ou referencia a registro inexistente: prefeitura sem convenios cadastrados, CPF ja em uso por outra matricula da mesma prefeitura, competencia fora de AAAAMM.",
      "acao": "Leia `error.details`. Na importacao de servidores as recusas mais comuns sao CPF fora de 11 digitos e matricula repetida no mesmo lote."
    },
    {
      "codigo": "rate_limited",
      "status": 429,
      "origem": "middleware",
      "quando": "Excesso de requisicoes na janela.",
      "acao": "Respeite `error.details.reset_in`. Importacao em lote deve ser UMA chamada com ate 500 itens, nao uma chamada por servidor."
    },
    {
      "codigo": "persistencia_falhou",
      "status": 503,
      "origem": "handler",
      "quando": "O banco de dados recusou a gravacao. Na consolidacao de folha a operacao inteira e desfeita: nenhuma parcela avanca e a folha fica como estava.",
      "acao": "Repita o mesmo pedido, de preferencia com a mesma `Idempotency-Key`. Persistindo, abra chamado com o `trace_id` e o `error.details.alvo`."
    },
    {
      "codigo": "internal_error",
      "status": 500,
      "origem": "middleware",
      "quando": "Falha nao tratada do nosso lado. Inclui resposta que nao casou com o schema de saida do endpoint.",
      "acao": "Nao repita em laco operacao de escrita desta camada — pode duplicar cadastro. Abra chamado com o `trace_id`."
    }
  ]
};

/**
 * Criticas especificas de cada endpoint. `unauthorized`, `forbidden` e
 * `rate_limited` vem do middleware e valem pra camada inteira — por isso nao
 * aparecem aqui.
 */
export const CRITICAS_POR_ENDPOINT: Record<string, string[]> = {
  "GET /v1/external/banco/margem": [
    "validation_error",
    "not_found"
  ],
  "GET /v1/external/banco/servidores": [
    "validation_error"
  ],
  "GET /v1/external/banco/contratos/{adf}": [
    "not_found"
  ],
  "POST /v1/external/banco/contratos/averbar": [
    "not_found",
    "validation_error",
    "prazo_fora_da_tabela",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "numero_contrato_em_uso",
    "calculo_invalido",
    "margem_insuficiente"
  ],
  "POST /v1/external/banco/contratos/{adf}/acao": [
    "not_found",
    "validation_error",
    "transicao_invalida",
    "portabilidade_em_andamento",
    "acao_invalida_reserva_cartao"
  ],
  "POST /v1/external/banco/contratos/{adf}/editar": [
    "not_found",
    "validation_error",
    "prazo_fora_da_tabela",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "cet_abaixo_dos_juros",
    "numero_contrato_em_uso",
    "calculo_invalido",
    "margem_insuficiente"
  ],
  "POST /v1/external/banco/contratos/{adf}/tratar-falha": [
    "not_found",
    "validation_error",
    "reenvio_sem_snapshot"
  ],
  "POST /v1/external/banco/tabelas": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/banco/ofertas": [
    "validation_error"
  ],
  "GET /v1/external/banco/portabilidade/elegiveis": [
    "validation_error",
    "not_found"
  ],
  "GET /v1/external/banco/portabilidade/{id}": [
    "not_found"
  ],
  "POST /v1/external/banco/portabilidade": [
    "not_found",
    "validation_error",
    "contrato_inelegivel",
    "autoportabilidade",
    "convenio_invalido",
    "portabilidade_em_andamento"
  ],
  "POST /v1/external/banco/portabilidade/{id}/decisao": [
    "not_found",
    "validation_error",
    "nao_autorizado",
    "transicao_invalida",
    "condicoes_nao_aceitas"
  ],
  "POST /v1/external/banco/portabilidade/{id}/saldo": [
    "not_found",
    "validation_error",
    "nao_autorizado",
    "transicao_invalida",
    "portabilidade_expirada"
  ],
  "POST /v1/external/banco/portabilidade/{id}/proposta": [
    "not_found",
    "validation_error",
    "nao_autorizado",
    "transicao_invalida",
    "portabilidade_expirada",
    "convenio_invalido",
    "prazo_fora_da_tabela",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "cet_abaixo_dos_juros",
    "saldo_nao_coberto",
    "numero_contrato_em_uso",
    "margem_insuficiente_na_projecao",
    "proposta_ja_aceita"
  ],
  "GET /v1/external/banco/portabilidade/{id}/comprovante": [
    "not_found"
  ],
  "POST /v1/external/banco/portabilidade/{id}/pagamento": [
    "not_found",
    "validation_error",
    "nao_autorizado",
    "transicao_invalida",
    "portabilidade_expirada",
    "condicoes_nao_aceitas",
    "saldo_nao_coberto"
  ],
  "POST /v1/external/banco/portabilidade/{id}/liquidar": [
    "not_found",
    "nao_autorizado",
    "transicao_invalida",
    "portabilidade_expirada",
    "condicoes_ausentes",
    "contrato_inelegivel",
    "convenio_invalido",
    "margem_insuficiente"
  ],
  "POST /v1/external/banco/portabilidade/{id}/cancelar": [
    "not_found",
    "nao_autorizado",
    "transicao_invalida",
    "portabilidade_expirada"
  ],
  "GET /v1/external/banco/refinanciamentos/{id}": [
    "not_found"
  ],
  "POST /v1/external/banco/refinanciamentos": [
    "not_found",
    "validation_error",
    "refin_inelegivel",
    "refinanciamento_em_andamento",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "cet_acima_da_tabela",
    "refin_troco_invalido",
    "margem_insuficiente"
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/responder": [
    "not_found",
    "validation_error",
    "transicao_invalida",
    "refin_inelegivel",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "cet_acima_da_tabela",
    "refin_troco_invalido",
    "margem_insuficiente",
    "refin_expirado"
  ],
  "POST /v1/external/banco/refinanciamentos/{id}/cancelar": [
    "not_found",
    "transicao_invalida"
  ],
  "POST /v1/external/banco/bate-carteira": [
    "validation_error"
  ],
  "POST /v1/external/banco/webhooks": [
    "validation_error"
  ],
  "POST /v1/external/banco/cartoes/reservas": [
    "cartao_em_manutencao",
    "not_found",
    "validation_error",
    "valor_acima_disponivel",
    "reserva_ja_ativa",
    "numero_contrato_em_uso",
    "margem_pendente_classificacao",
    "servidor_nao_averbavel"
  ],
  "GET /v1/external/banco/cartoes/reservas": [
    "cartao_em_manutencao"
  ],
  "GET /v1/external/banco/cartoes/reservas/{adf}": [
    "cartao_em_manutencao",
    "not_found"
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos": [
    "cartao_em_manutencao",
    "not_found",
    "validation_error",
    "reserva_inativa",
    "valor_acima_reservado",
    "competencia_anterior_a_reserva",
    "competencia_congelada",
    "parcelas_esgotadas"
  ],
  "POST /v1/external/banco/cartoes/reservas/{adf}/lancamentos/cancelar": [
    "cartao_em_manutencao",
    "not_found",
    "validation_error",
    "competencia_congelada"
  ],
  "POST /v1/external/banco/cartoes/lancamentos": [
    "cartao_em_manutencao",
    "validation_error"
  ],
  "GET /v1/external/prefeitura/servidores": [
    "not_found"
  ],
  "POST /v1/external/prefeitura/folhas": [
    "not_found",
    "validation_error"
  ],
  "PATCH /v1/external/prefeitura/folhas/{id}": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/prefeitura/folhas/{competencia}/movimento": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/prefeitura/folhas/{id}/retorno": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/prefeitura/folhas/{id}/resultados": [
    "not_found"
  ],
  "POST /v1/external/prefeitura/adf/confirmar": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/prefeitura/adf/falha": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/prefeitura/anuencia": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/servidor/margem": [
    "not_found"
  ],
  "POST /v1/external/servidor/propostas/simular": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/servidor/propostas": [
    "not_found",
    "validation_error",
    "cet_abaixo_do_minimo",
    "cet_acima_do_teto",
    "condicao_indisponivel",
    "margem_insuficiente",
    "proposta_em_andamento",
    "use_portabilidade"
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/confirmar": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/servidor/averbacoes-pendentes/{adf}/rejeitar": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/servidor/portabilidade/elegiveis": [
    "not_found"
  ],
  "GET /v1/external/servidor/portabilidade/{id}": [
    "not_found"
  ],
  "POST /v1/external/servidor/portabilidade": [
    "not_found",
    "validation_error",
    "contrato_inelegivel",
    "autoportabilidade",
    "convenio_invalido",
    "portabilidade_em_andamento"
  ],
  "POST /v1/external/servidor/portabilidade/{id}/confirmar": [
    "not_found",
    "validation_error",
    "transicao_invalida",
    "portabilidade_expirada"
  ],
  "POST /v1/external/servidor/portabilidade/{id}/cancelar": [
    "not_found",
    "transicao_invalida",
    "nao_autorizado",
    "portabilidade_expirada"
  ],
  "POST /v1/external/servidor/beneficios/{id}/contratar": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/servidor/termos/{tipo}": [
    "not_found"
  ],
  "POST /v1/external/averbadora/bancos": [
    "validation_error"
  ],
  "POST /v1/external/averbadora/prefeituras": [
    "validation_error"
  ],
  "POST /v1/external/averbadora/convenios": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/convenios/{id}/config": [
    "not_found",
    "validation_error"
  ],
  "PATCH /v1/external/averbadora/servidores/{matricula}": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/pre-reservas/{id}/cancelar": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/folhas": [
    "not_found",
    "validation_error",
    "persistencia_falhou"
  ],
  "POST /v1/external/averbadora/folhas/{id}/consolidar": [
    "not_found",
    "validation_error",
    "persistencia_falhou"
  ],
  "POST /v1/external/averbadora/adf/confirmar": [
    "validation_error"
  ],
  "POST /v1/external/averbadora/adf/falha": [
    "validation_error"
  ],
  "POST /v1/external/averbadora/bate-carteira": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/comunicados": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/vitrine": [
    "not_found",
    "validation_error"
  ],
  "POST /v1/external/averbadora/termos/{tipo}": [
    "not_found",
    "validation_error"
  ],
  "GET /v1/external/averbadora/contratos/{adf}": [
    "not_found"
  ]
};

export interface AnexoDoc {
  titulo: string;
  nota?: string;
  itens: { valor: string; descricao: string }[];
}

/** Tabelas de enum, por camada. Acoes e eventos vem da fonte, nao de copia. */
export const ANEXOS_POR_CAMADA: Record<string, AnexoDoc[]> = {
  "banco": [
    {
      "titulo": "Acoes de contrato",
      "nota": "Valores aceitos em `POST /contratos/{adf}/acao`. Acao fora da situacao atual responde 409 `transicao_invalida`, nao 200.",
      "itens": [
        {
          "valor": "aprovar",
          "descricao": "Aprova a proposta em analise. Onde a prefeitura exige CCB, o contrato assinado precisa estar anexado antes — sem ele responde 400."
        },
        {
          "valor": "confirmar",
          "descricao": "Averba a proposta (aprovada ou direto da analise). E' a partir daqui que o desconto entra na folha."
        },
        {
          "valor": "quitar",
          "descricao": "Encerra o contrato por liquidacao (desaverba). Exige `motivo`. A margem libera agora; o desconto em folha para na proxima competencia — o mes corrente ainda instrui."
        },
        {
          "valor": "suspender",
          "descricao": "Pausa um contrato ativo, mantendo o vinculo. Continua consumindo margem."
        },
        {
          "valor": "alongar",
          "descricao": "Estende o prazo. Use `parcelas_extras` para dizer quantas parcelas acrescentar."
        },
        {
          "valor": "alterar",
          "descricao": "Altera observacoes e codigo de verba. Nao mexe em valor, prazo ou taxa — para isso use `/editar`."
        },
        {
          "valor": "cancelar",
          "descricao": "Cancela a operacao. Libera a margem e e' terminal."
        },
        {
          "valor": "expirar",
          "descricao": "Expira a reserva. Aplicado pela propria averbadora no vencimento da trava; nao e' acao de banco."
        },
        {
          "valor": "confirmar_servidor",
          "descricao": "Aceite do SERVIDOR em operacao iniciada pelo banco. So o proprio servidor executa — o banco nao pode confirmar por ele."
        },
        {
          "valor": "rejeitar_servidor",
          "descricao": "Recusa do servidor em operacao iniciada pelo banco. Tambem so o servidor executa."
        },
        {
          "valor": "editar_valores",
          "descricao": "Contra-proposta: muda valor, prazo e taxa enquanto a proposta esta em analise. Exposta como `POST /contratos/{adf}/editar`."
        }
      ]
    },
    {
      "titulo": "Tipos de operacao",
      "itens": [
        {
          "valor": "EMPRESTIMO",
          "descricao": "Consignado novo. Consome a margem de EMPRESTIMO (percentual ou saldo definido pela regra da unidade pagadora)."
        },
        {
          "valor": "REFIN",
          "descricao": "Refinanciamento de operacao existente. Mesmo bucket de margem do emprestimo."
        },
        {
          "valor": "ECONSIGNADO",
          "descricao": "Cartao consignado. Consome o bucket CARTAO_CONSIGNADO (percentual ou saldo definido pela regra da unidade pagadora)."
        }
      ]
    },
    {
      "titulo": "Buckets de margem",
      "nota": "O bucket diz QUAL margem a operacao consome; quanto ela vale e' da regra da unidade pagadora — percentuais por produto calculados sobre o salario, com ou sem teto somado, ou saldo informado pelo proprio orgao. Por padrao os tres sao independentes; com margem informada de saldo compartilhado, consumir um reduz os outros. `max_comprometimento_pct` do convenio e' informativo e nao altera o calculo.",
      "itens": [
        {
          "valor": "EMPRESTIMO",
          "descricao": "Emprestimo consignado e refinanciamento: percentual ou saldo definido pela regra da unidade pagadora."
        },
        {
          "valor": "CARTAO_CONSIGNADO",
          "descricao": "Cartao consignado: percentual ou saldo definido pela regra da unidade pagadora."
        },
        {
          "valor": "CARTAO_BENEFICIOS",
          "descricao": "Cartao beneficio: percentual ou saldo definido pela regra da unidade pagadora."
        }
      ]
    },
    {
      "titulo": "Estados de portabilidade",
      "nota": "Rito próprio, distinto de reserva composta. Prazo em dias úteis do convênio (7 sem configuração). Etapa bancária atrasada permanece operável e bloqueia novas averbações do responsável até regularizar todas as pendências. Espera pelo servidor pode expirar; análise manual não penaliza bancos. Credora sem operação ativa dispensa resposta e exige comprovante analisado pela averbadora.",
      "itens": [
        {
          "valor": "aguardando_proposta",
          "descricao": "Proponente: apresente as condições para o servidor aceitar em POST /portabilidade/{id}/proposta."
        },
        {
          "valor": "aguardando_servidor",
          "descricao": "O proponente abriu; falta o servidor confirmar (com a senha, no app ou pela API dele). A credora ainda nao age."
        },
        {
          "valor": "solicitada",
          "descricao": "Credora: aceite com saldo_devedor ou recuse com motivo em POST /portabilidade/{id}/decisao. A rota /saldo também registra aceite e saldo juntos."
        },
        {
          "valor": "aguardando_pagamento",
          "descricao": "Proponente: informe o valor pago em POST /portabilidade/{id}/pagamento; comprovante obrigatório se analise_manual=true."
        },
        {
          "valor": "aguardando_liquidacao",
          "descricao": "Credora liquida em POST /portabilidade/{id}/liquidar; se analise_manual=true, quem_age=averbadora e o comprovante aguarda análise manual."
        },
        {
          "valor": "concluida",
          "descricao": "Origem `Quitado` e destino (`adf_destino`) averbado no proponente, na mesma transacao."
        },
        {
          "valor": "cancelada",
          "descricao": "Cancelada pelo proponente ou servidor, recusada pela credora, ou a origem deixou de estar ativa."
        },
        {
          "valor": "expirada",
          "descricao": "O prazo para o servidor confirmar venceu."
        }
      ]
    },
    {
      "titulo": "Estados de refinanciamento",
      "nota": "Negociacao de refinanciamento com o proprio credor. So' `solicitado` e `ofertado` prendem as origens; o contrato novo nasce no aceite.",
      "itens": [
        {
          "valor": "solicitado",
          "descricao": "O servidor pediu. Responda com `aprovar` (condicoes) ou `recusar`."
        },
        {
          "valor": "ofertado",
          "descricao": "Condicoes na mesa, aguardando o aceite do servidor."
        },
        {
          "valor": "aceito",
          "descricao": "Contrato REFIN sucessor criado Aprovado; averbe-o pelo fluxo de contrato."
        },
        {
          "valor": "efetivado",
          "descricao": "A folha aplicou o sucessor e as origens viraram Substituido."
        },
        {
          "valor": "recusado",
          "descricao": "Recusado pelo banco ou pelo servidor."
        },
        {
          "valor": "cancelado",
          "descricao": "Cancelado, ou o sucessor foi cancelado antes da folha."
        },
        {
          "valor": "expirado",
          "descricao": "A validade venceu sem resposta."
        }
      ]
    },
    {
      "titulo": "Situacao da parcela",
      "itens": [
        {
          "valor": "Paga",
          "descricao": "Ja descontada em folha."
        },
        {
          "valor": "Em aberto",
          "descricao": "A proxima a ser descontada."
        },
        {
          "valor": "A vencer",
          "descricao": "Futura."
        }
      ]
    },
    {
      "titulo": "Situacao da ADF na folha",
      "nota": "Campo `folha_status`, e por competencia em `folha_competencias` (chave AAAAMM). O mapa por competencia e' o autoritativo — o contrato aparece em varias folhas, uma por mes de parcela.",
      "itens": [
        {
          "valor": "recebida",
          "descricao": "ADF enviada; a prefeitura ainda nao confirmou."
        },
        {
          "valor": "aplicada",
          "descricao": "Desconto entrou na folha da competencia."
        },
        {
          "valor": "falha",
          "descricao": "A prefeitura reprovou o desconto. O motivo vem em `folha_motivo`."
        },
        {
          "valor": "interrompida_desligamento",
          "descricao": "Desconto interrompido por desligamento do servidor."
        },
        {
          "valor": "parcial",
          "descricao": "A folha descontou menos que o instruido; a parcela do mes se encerra e a diferenca vem em `residuo`."
        },
        {
          "valor": "suspensa_sem_folha",
          "descricao": "O servidor nao teve folha no mes. A parcela nao avanca e nao ha residuo."
        },
        {
          "valor": "suspensa_por_teto",
          "descricao": "A Atlas tirou o desconto do movimento para caber no teto global da regra do orgao. A parcela nao avanca e nao ha residuo; a divida continua."
        }
      ]
    },
    {
      "titulo": "Eventos de webhook",
      "nota": "Valores aceitos em `events` ao cadastrar um webhook.",
      "itens": [
        {
          "valor": "proposta.criada",
          "descricao": "Proposta ou reserva registrada."
        },
        {
          "valor": "proposta.aprovada",
          "descricao": "Proposta aprovada pelo banco."
        },
        {
          "valor": "proposta.rejeitada",
          "descricao": "Proposta recusada."
        },
        {
          "valor": "proposta.expirada",
          "descricao": "Reserva venceu a trava e liberou a margem."
        },
        {
          "valor": "contrato.averbado",
          "descricao": "Averbacao confirmada — o desconto passa a valer na folha."
        },
        {
          "valor": "contrato.suspenso",
          "descricao": "Contrato pausado."
        },
        {
          "valor": "contrato.cancelado",
          "descricao": "Contrato cancelado."
        },
        {
          "valor": "contrato.quitado",
          "descricao": "Contrato quitado."
        },
        {
          "valor": "contrato.alongado",
          "descricao": "Prazo estendido."
        },
        {
          "valor": "contrato.alterado",
          "descricao": "Campos administrativos alterados."
        },
        {
          "valor": "folha.sincronizada",
          "descricao": "A prefeitura processou a folha da competencia."
        },
        {
          "valor": "folha.fechada",
          "descricao": "Folha da competencia fechada — nao aceita mais ADF."
        },
        {
          "valor": "servidor.bloqueado",
          "descricao": "Servidor impedido de novas operacoes."
        },
        {
          "valor": "servidor.desbloqueado",
          "descricao": "Servidor liberado."
        },
        {
          "valor": "comunicado.publicado",
          "descricao": "Comunicado novo da averbadora."
        },
        {
          "valor": "portabilidade.solicitada",
          "descricao": "Portabilidade aberta — pelo banco proponente (aguarda a confirmação do servidor) ou pelo servidor (aguarda a proposta do banco)."
        },
        {
          "valor": "portabilidade.proposta_informada",
          "descricao": "O banco apresentou as condições e aguarda o aceite do servidor."
        },
        {
          "valor": "portabilidade.confirmada",
          "descricao": "O servidor confirmou a portabilidade aberta pelo proponente: a credora tem o prazo da etapa para informar o saldo devedor."
        },
        {
          "valor": "portabilidade.saldo_informado",
          "descricao": "A credora informou o saldo devedor. O proponente tem o prazo da etapa para informar o valor pago à credora."
        },
        {
          "valor": "portabilidade.pagamento_informado",
          "descricao": "O proponente informou o pagamento. A credora tem o prazo da etapa para liquidar."
        },
        {
          "valor": "portabilidade.concluida",
          "descricao": "A credora liquidou: a origem foi quitada e o contrato destino (`adf_destino`) nasceu averbado no proponente."
        },
        {
          "valor": "portabilidade.cancelada",
          "descricao": "Portabilidade cancelada pelo proponente ou pelo servidor, ou porque a origem deixou de estar ativa."
        },
        {
          "valor": "portabilidade.expirada",
          "descricao": "O prazo de uma etapa venceu sem a ação de quem devia agir."
        },
        {
          "valor": "refinanciamento.solicitado",
          "descricao": "Servidor pediu refinanciamento de contrato(s) seu(s). Responda em `POST /refinanciamentos/{id}/responder`."
        },
        {
          "valor": "refinanciamento.ofertado",
          "descricao": "Condições de refinanciamento na mesa, aguardando o aceite do servidor."
        },
        {
          "valor": "refinanciamento.aceito",
          "descricao": "Os dois lados concordaram: o contrato REFIN sucessor (`adf_sucessor`) nasceu Aprovado e aguarda a sua averbação."
        },
        {
          "valor": "refinanciamento.recusado",
          "descricao": "Negociação recusada (pelo banco ou pelo servidor)."
        },
        {
          "valor": "refinanciamento.cancelado",
          "descricao": "Negociação cancelada, ou o contrato sucessor morreu antes de entrar em folha."
        },
        {
          "valor": "refinanciamento.expirado",
          "descricao": "A validade do pedido ou da oferta venceu sem resposta."
        },
        {
          "valor": "refinanciamento.efetivado",
          "descricao": "A folha aplicou o sucessor: as origens foram substituídas."
        },
        {
          "valor": "webhook.test",
          "descricao": "Disparo manual de teste — util para validar assinatura e conectividade sem esperar evento real."
        }
      ]
    }
  ],
  "prefeitura": [
    {
      "titulo": "Situacao da ADF",
      "nota": "Ciclo de vida do desconto de um contrato numa competencia. Voce so muda entre `aplicada` e `falha`; `recebida` e' o estado em que a averbadora entrega.",
      "itens": [
        {
          "valor": "recebida",
          "descricao": "A averbadora enviou o desconto e aguarda a sua folha processar."
        },
        {
          "valor": "aplicada",
          "descricao": "O desconto entrou na folha da competencia. Confirmado via POST /adf/confirmar."
        },
        {
          "valor": "falha",
          "descricao": "O desconto nao entrou. Reportado via POST /adf/falha, com motivo — o banco decide o tratamento a partir dele."
        },
        {
          "valor": "interrompida_desligamento",
          "descricao": "Desconto interrompido porque o servidor foi desligado."
        },
        {
          "valor": "suspensa_por_teto",
          "descricao": "A Atlas tirou o desconto do movimento para caber no teto global da regra do orgao. Nao vem no seu arquivo: e' registrado pela Atlas, sem falha e sem residuo."
        }
      ]
    },
    {
      "titulo": "Situacao da folha",
      "nota": "Fechar a folha ABRE sozinha a competencia seguinte — o ciclo nao para esperando alguem lembrar.",
      "itens": [
        {
          "valor": "aberta",
          "descricao": "Aceita novas averbacoes ate a data de corte."
        },
        {
          "valor": "fechada",
          "descricao": "Nao aceita mais ADFs. Exige ao menos uma movimentacao registrada, salvo a partir da data de repasse."
        },
        {
          "valor": "consolidada",
          "descricao": "Estado final, apos o repasse."
        }
      ]
    },
    {
      "titulo": "Reconciliacao do tombamento",
      "nota": "Resultado do confronto de cada linha da sua remessa com o que a Atlas ja tinha.",
      "itens": [
        {
          "valor": "ok",
          "descricao": "A linha bate com o registro da Atlas."
        },
        {
          "valor": "divergente",
          "descricao": "Valor ou parcelas conflitam. Nao e' erro de arquivo — e' diferenca de dado, e vale revisao antes de virar margem consumida."
        },
        {
          "valor": "novo",
          "descricao": "Operacao que a Atlas nao conhecia. Entra como emprestimo externo e passa a consumir margem do servidor."
        }
      ]
    },
    {
      "titulo": "Formato de importacao",
      "nota": "Combinado por convenio. Os dois endpoints de importacao aceitam o CSV cru (`Content-Type: text/csv`) ou o envelope JSON `{\"csv\": \"...\"}`.",
      "itens": [
        {
          "valor": "CSV",
          "descricao": "Arquivo de texto separado por virgula."
        },
        {
          "valor": "EXCEL",
          "descricao": "Planilha — convertida para CSV antes do envio."
        },
        {
          "valor": "API",
          "descricao": "Integracao direta, sem arquivo."
        }
      ]
    },
    {
      "titulo": "Operacao em lote — o que conferir",
      "nota": "Os endpoints de ADF aceitam ate 500 ids e respondem 200 mesmo quando aplicam MENOS do que voce pediu. Nao ha codigo de erro para isso porque nao e' erro: id inexistente, ja no estado pedido, ou de outra prefeitura simplesmente nao entra na conta.",
      "itens": [
        {
          "valor": "solicitadas",
          "descricao": "Quantos ids voce mandou."
        },
        {
          "valor": "aplicadas / falhas",
          "descricao": "Quantos realmente mudaram de estado."
        },
        {
          "valor": "adfs",
          "descricao": "Quais mudaram. Compare com o que enviou: a diferenca sao os ids que nao valeram, e ela nao vem em forma de erro."
        }
      ]
    }
  ],
  "servidor": [
    {
      "titulo": "Onde a senha do servidor e' exigida",
      "nota": "O token identifica a APLICACAO; a senha identifica a PESSOA. Token de integracao nao substitui a anuencia do servidor — e' a regra que separa 'um sistema agindo por mim' de 'um sistema agindo sem mim'.",
      "itens": [
        {
          "valor": "POST /averbacoes-pendentes/{adf}/confirmar",
          "descricao": "SEMPRE. E' o aceite de uma operacao que o banco iniciou."
        },
        {
          "valor": "POST /averbacoes-pendentes/{adf}/rejeitar",
          "descricao": "SEMPRE, pelo mesmo motivo."
        },
        {
          "valor": "POST /propostas",
          "descricao": "Quando a prefeitura do convenio exige senha de proposta. Sem ela, responde 401."
        },
        {
          "valor": "POST /portabilidade e POST /portabilidade/{id}/confirmar",
          "descricao": "Quando a prefeitura exige senha de transacao. Confirmar e' o consentimento da pessoa com a portabilidade que o banco de destino abriu."
        },
        {
          "valor": "demais rotas",
          "descricao": "Nao exigem senha — sao leitura ou operacao sem efeito financeiro imediato."
        }
      ]
    },
    {
      "titulo": "Buckets de margem",
      "nota": "O bucket diz QUAL margem a operacao consome; quanto ela vale e' da regra da unidade pagadora (percentuais calculados sobre o salario ou saldo informado pelo orgao). Por padrao independentes; com saldo compartilhado, consumir um reduz os outros.",
      "itens": [
        {
          "valor": "EMPRESTIMO",
          "descricao": "Consignado e refinanciamento: percentual ou saldo definido pela regra da unidade pagadora."
        },
        {
          "valor": "CARTAO_CONSIGNADO",
          "descricao": "Cartao consignado: percentual ou saldo definido pela regra da unidade pagadora."
        },
        {
          "valor": "CARTAO_BENEFICIOS",
          "descricao": "Cartao beneficio: percentual ou saldo definido pela regra da unidade pagadora."
        }
      ]
    },
    {
      "titulo": "Tipo de proposta",
      "itens": [
        {
          "valor": "novo",
          "descricao": "Credito novo."
        },
        {
          "valor": "portabilidade",
          "descricao": "Recusado com 422 `use_portabilidade`: portabilidade tem rito proprio em `POST /portabilidade`."
        },
        {
          "valor": "refinanciamento",
          "descricao": "Renegociar contrato existente."
        }
      ]
    },
    {
      "titulo": "Estados de portabilidade",
      "nota": "Rito entre o banco de destino (proponente) e o banco atual (credora). `quem_age` diz de quem e' a vez; cada etapa vence em dias uteis.",
      "itens": [
        {
          "valor": "aguardando_servidor",
          "descricao": "O banco de destino abriu. Confirme em `POST /portabilidade/{id}/confirmar` ou cancele."
        },
        {
          "valor": "solicitada",
          "descricao": "Aguardando o banco atual informar o saldo devedor."
        },
        {
          "valor": "aguardando_pagamento",
          "descricao": "Aguardando o banco de destino informar as condicoes do contrato novo."
        },
        {
          "valor": "aguardando_liquidacao",
          "descricao": "Aguardando o banco atual liquidar."
        },
        {
          "valor": "concluida",
          "descricao": "Contrato antigo quitado; o novo (`adf_destino`) ja esta averbado."
        },
        {
          "valor": "cancelada",
          "descricao": "Cancelada por voce ou pelo banco de destino, ou o contrato deixou de estar ativo."
        },
        {
          "valor": "expirada",
          "descricao": "Uma etapa venceu sem a acao de quem devia agir."
        }
      ]
    },
    {
      "titulo": "Uma operacao por vez",
      "nota": "Regra de estado que causa a recusa mais comum desta camada, e que nao e' obvia lendo so os campos.",
      "itens": [
        {
          "valor": "emprestimo em analise",
          "descricao": "Bloqueia nova proposta na MESMA matricula, ate ser aprovada, recusada ou expirar."
        },
        {
          "valor": "outra matricula",
          "descricao": "Nao e' bloqueada: cada matricula do CPF tem margem e fila proprias."
        },
        {
          "valor": "portabilidade aberta",
          "descricao": "Prende so' o contrato de origem: outra portabilidade ou refinanciamento dele espera o rito acabar. A margem so' muda na liquidacao."
        }
      ]
    }
  ],
  "averbadora": [
    {
      "titulo": "Operacoes sem volta",
      "nota": "Esta camada NAO tem isolamento por parceiro: o token alcanca todos os bancos e todas as prefeituras. Nenhum erro protege de acertar o alvo errado, entao a conferencia e' sua, antes de enviar.",
      "itens": [
        {
          "valor": "POST /adf/confirmar",
          "descricao": "Marca ADFs como descontadas em folha, e o lote pode cruzar PREFEITURAS. Nao ha desfazer — o contrato passa a Ativo e o banco e' notificado."
        },
        {
          "valor": "POST /adf/falha",
          "descricao": "Reprova ADFs e notifica os bancos. Tambem cruza prefeituras."
        },
        {
          "valor": "POST /folhas/{id}/consolidar",
          "descricao": "Estado final da folha. Consolidada nao reabre, e nenhuma outra rota leva a folha para `consolidada`. Com parte do retorno faltando ou arquivo recebido e nao processado, antes da data de repasse, exige `?confirmar_incompleto=true`. Aceita `Idempotency-Key`: repetir com a mesma chave devolve a primeira resposta."
        },
        {
          "valor": "PATCH /servidores/{matricula}",
          "descricao": "Reduzir `salarioLiquido` reduz a margem na hora; contratos ja averbados NAO sao revistos, e o servidor pode ficar com margem negativa."
        },
        {
          "valor": "POST /eventos/disparar",
          "descricao": "Em `production`, os webhooks dos parceiros recebem de verdade. Nao ha modo de teste dentro do ambiente de producao."
        },
        {
          "valor": "POST /bancos com status=pausado",
          "descricao": "Derruba todos os tokens daquele banco na hora — as chamadas dele passam a responder 401."
        }
      ]
    },
    {
      "titulo": "Upsert disfarcado de POST",
      "nota": "Nestes endpoints o campo `id` e' opcional: omitir CRIA, enviar SOBRESCREVE o registro existente. Nao ha confirmacao nem aviso de que houve substituicao.",
      "itens": [
        {
          "valor": "POST /bancos",
          "descricao": "`id` numerico."
        },
        {
          "valor": "POST /prefeituras",
          "descricao": "`id` numerico."
        },
        {
          "valor": "POST /convenios",
          "descricao": "`id` textual."
        },
        {
          "valor": "POST /folhas",
          "descricao": "`id` textual."
        },
        {
          "valor": "POST /comunicados",
          "descricao": "`id` textual."
        },
        {
          "valor": "POST /vitrine",
          "descricao": "`id` textual."
        }
      ]
    },
    {
      "titulo": "Unidades que divergem entre campos",
      "nota": "A causa mais provavel de um numero errado nesta camada. Nao ha validacao que pegue: 1.8 e 0.018 sao ambos aceitos, e o efeito e' 100x de diferenca.",
      "itens": [
        {
          "valor": "taxaMaxAm (config de convenio)",
          "descricao": "PERCENTUAL: 1.8 significa CET de 1,8% a.m. — e' teto de CET, nao de juros."
        },
        {
          "valor": "taxa_am (averbacao, tabelas, ofertas)",
          "descricao": "FRACAO: 0.018 significa CET de 1,8% a.m. (cartao: juros do rotativo)."
        },
        {
          "valor": "percentualComissao (config de convenio)",
          "descricao": "FRACAO: 0.02 significa 2%."
        },
        {
          "valor": "maxComprometimentoPct (config de convenio)",
          "descricao": "FRACAO: 0.35 significa 35%. E' informativo — nao e' validado."
        }
      ]
    },
    {
      "titulo": "O que esta camada NAO faz",
      "nota": "Deliberado: operacoes destrutivas nao existem na API externa. Elas vivem so no painel, atras de senha e de um kill switch.",
      "itens": [
        {
          "valor": "purge / reseed",
          "descricao": "Nao expostos. Limpeza de base e' fluxo exclusivo do painel."
        },
        {
          "valor": "delete de matriculas, lotes e anuencias",
          "descricao": "Nao expostos."
        },
        {
          "valor": "reverter desligamento",
          "descricao": "Nao exposto."
        },
        {
          "valor": "desativacoes",
          "descricao": "O que existe aqui e' PAUSAR e REATIVAR (bancos, beneficios, vitrine) — reversivel por construcao."
        }
      ]
    }
  ]
};
